import { useEffect, useMemo, useRef, useState } from 'react'
import './App.css'
import ActionMenu from './components/ActionMenu'
import Button from './components/Button'
import ClearableInput from './components/ClearableInput'
import ConfirmationDialog from './components/ConfirmationDialog'
import DateRangeField from './components/DateRangeField'
import ExpandableButton from './components/ExpandableButton'
import IconButton from './components/IconButton'
import Modal from './components/Modal'
import PasswordInput from './components/PasswordInput'
import StatusBadge from './components/StatusBadge'
import SymbolIcon from './components/SymbolIcon'
import { installDomTranslator } from './i18nRuntime'
import { DE_UI_LITERALS, DE_UI_TEMPLATES } from './i18n/de'
import { EN_UI_LITERALS, EN_UI_TEMPLATES } from './i18n/en'
import { normalizeApiError, parseApiError } from './errorHandling'
import { getExportPublicUrl } from './exportDownload'
import {
  getImpersonationPermissionDecision,
  getResellerImpersonationStatus,
  getSubResellerImpersonationActions,
  IMPERSONATION_ACTION_REVOKE,
  IMPERSONATION_STATUS_APPROVED,
  IMPERSONATION_STATUS_NONE,
  IMPERSONATION_STATUS_REQUESTED,
  IMPERSONATION_STATUS_REVOKED
} from './impersonationActions'
import WEB_CHANGELOG from './generated-changelog.json'
import { buildPlatformOrganizationRows } from './platformOrganizationTree'
import { formatScopedPosLabel, posForCompany, posForSite, sitesForCompany } from './artifactScope'
import privacyPolicyUrl from './assets/Datenschutzerklaerung.pdf'
import elefantWhiteImageUrl from './assets/ELeFANT-white.png'
import elefantAudioUrl from './assets/ELeFANT.mp3'
import elefantImageUrl from './assets/ELeFANT.png'
import termsUrl from './assets/Geschaeftsbedingungen.pdf'
import {
  CUSTOM_ARTIFACT_TYPE,
  customArtifactLabel,
  DE_ARTIFACT_TYPE_LABELS,
  hasRequiredArtifactDescription
} from './artifactTypes'

const NAV_ITEMS = [
  { label: 'Prüfexport', icon: 'export' },
  { label: 'Web-Upload', icon: 'upload' },
  { label: 'API-Keys', icon: 'key' },
  { label: 'Administration', icon: 'settings' }
]

const TENANT_USER_RIGHTS_DEFAULT = {
  can_manage_companies: false,
  can_manage_sites: false,
  can_manage_pos_and_api_keys: false,
  can_run_exports: false,
  can_manage_users: false,
  can_manage_impersonation: false,
}

const TENANT_USER_RIGHTS_OPTIONS = [
  { key: 'can_manage_companies', label: 'Unternehmen verwalten' },
  { key: 'can_manage_sites', label: 'Betriebsstätten verwalten' },
  { key: 'can_manage_pos_and_api_keys', label: 'Kassen/POS und API-Keys verwalten' },
  { key: 'can_run_exports', label: 'Export durchführen' },
  { key: 'can_manage_users', label: 'Nutzerverwaltung' },
  { key: 'can_manage_impersonation', label: 'Impersonisation verwalten' }
]
const UI_SEARCH_THRESHOLD = 8
const UI_MULTISELECT_VISIBLE_ROWS = 3
const PASSKEY_DEFAULT_NAME = 'iShield Key 2 Pro'
const UPLOAD_FILE_LIMIT_BYTES = 2_000_000_000
const UPLOAD_FILE_LIMIT_LABEL = '2 GB'
const IMPERSONATION_NOTIFICATION_DISMISSED_KEY = 'fiscalarchive_impersonation_notification_dismissed'

const POS_TSE_TYPE_OPTIONS = [
  { value: 'swissbit_cloud_tse', label: 'Swissbit Cloud TSE' },
  { value: 'dn_tse_cloud', label: 'DN TSE Cloud' },
  { value: 'swissbit_hw_tse', label: 'Swissbit HW TSE' },
  { value: 'fiskaly_cloud_tse', label: 'fiskaly Cloud TSE' },
  { value: 'other', label: 'Andere' }
]
const detectBrowserUiLang = () => ((typeof navigator !== 'undefined' && String(navigator.language || '').toLowerCase().startsWith('de')) ? 'de' : 'en')
let CURRENT_UI_LITERALS = detectBrowserUiLang() === 'de' ? DE_UI_LITERALS : EN_UI_LITERALS
let CURRENT_UI_TEMPLATES = detectBrowserUiLang() === 'de' ? DE_UI_TEMPLATES : EN_UI_TEMPLATES
const setCurrentUiLang = (lang) => {
  const safe = String(lang || '').toLowerCase() === 'de' ? 'de' : 'en'
  CURRENT_UI_LITERALS = safe === 'de' ? DE_UI_LITERALS : EN_UI_LITERALS
  CURRENT_UI_TEMPLATES = safe === 'de' ? DE_UI_TEMPLATES : EN_UI_TEMPLATES
}

const uploadBlobWithProgress = ({ url, method = 'PUT', headers = {}, body, onProgress }) => (
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url, true)
    Object.entries(headers).forEach(([key, value]) => {
      if (value != null && value !== '') {
        xhr.setRequestHeader(key, String(value))
      }
    })
    xhr.upload.onprogress = (event) => {
      if (typeof onProgress !== 'function') return
      if (event.lengthComputable && event.total > 0) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percent: Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
        })
        return
      }
      onProgress({
        loaded: event.loaded,
        total: 0,
        percent: null
      })
    }
    xhr.onload = () => {
      let data = null
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch {
        data = null
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status, data })
        return
      }
      reject(new Error(normalizeApiError(xhr.status, data, { uploadFileLimitLabel: UPLOAD_FILE_LIMIT_LABEL })))
    }
    xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload.'))
    xhr.onabort = () => reject(new Error('Upload abgebrochen.'))
    xhr.send(body)
  })
)

const sha256DigestHex = async (file) => {
  if (!globalThis.crypto?.subtle) {
    throw new Error('SHA-256 wird von diesem Browser nicht unterstützt.')
  }
  const buffer = await file.arrayBuffer()
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
}

const normalizePosTseType = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (POS_TSE_TYPE_OPTIONS.some((option) => option.value === normalized)) return normalized
  return 'swissbit_cloud_tse'
}

const getPosTseTypeLabel = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  return POS_TSE_TYPE_OPTIONS.find((option) => option.value === normalized)?.label || String(value || '').trim() || '-'
}

const isSwissbitCloudPosTseType = (value) => ['swissbit_cloud_tse', 'dn_tse_cloud'].includes(normalizePosTseType(value))

const isFiskalyCloudPosTseType = (value) => normalizePosTseType(value) === 'fiskaly_cloud_tse'

const getArtifactTypeLabel = (artifactType, country) => {
  if (!artifactType) return '-'
  const normalizedCountry = String(country || '').trim().toUpperCase()
  if (normalizedCountry === 'DE') {
    const label = DE_ARTIFACT_TYPE_LABELS[artifactType]
    return label ? uiLiteral(label) : artifactType
  }
  return artifactType
}

const uiLiteral = (label) => CURRENT_UI_LITERALS[label] || label

const formatUiTemplate = (key, vars = {}) => {
  const template = CURRENT_UI_TEMPLATES[key] || ''
  if (!template) return ''
  return String(template).replace(/\{\{(\w+)\}\}/g, (_, name) => String(vars?.[name] ?? ''))
}

const formatConfirmationTemplate = (key, vars = {}) => {
  const template = CURRENT_UI_TEMPLATES[key] || ''
  if (!template) return ''
  const parts = String(template).split(/(\{\{\w+\}\})/g)
  return parts.map((part, index) => {
    const match = part.match(/^\{\{(\w+)\}\}$/)
    if (!match) return part
    const name = match[1]
    const value = String(vars?.[name] ?? '')
    return name === 'label' ? <strong key={`${key}-${index}`}>{value}</strong> : value
  })
}

const getDefaultUploadRange = () => {
  const now = new Date()
  const today = toIsoDate(now)
  return {
    from: today,
    to: today
  }
}

const formatDateDisplay = (value) => {
  if (!value) return '-'
  if (/^-?\d+$/.test(String(value).trim())) {
    const d = toDateTimeValue(value)
    if (!d || Number.isNaN(d.getTime())) return String(value)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}.${mm}.${yyyy}`
  }
  const raw = String(value)
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return `${m[3]}.${m[2]}.${m[1]}`
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

const toDateTimeValue = (value) => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value < 1000000000000 ? value * 1000 : value)
  }
  const raw = String(value).trim()
  if (/^-?\d+$/.test(raw)) {
    const numeric = Number(raw)
    return new Date(numeric < 1000000000000 ? numeric * 1000 : numeric)
  }
  return new Date(raw)
}

const formatDateTimeDisplay = (value) => {
  const d = toDateTimeValue(value)
  if (!d) return '-'
  if (Number.isNaN(d.getTime())) return String(value)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`
}

const toIsoDate = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const monthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1)

const buildCalendarCells = (monthDate) => {
  const first = monthStart(monthDate)
  const firstWeekday = (first.getDay() + 6) % 7
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null)
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(toIsoDate(new Date(first.getFullYear(), first.getMonth(), day)))
  }
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

const LegalBottomBar = ({ showTerms = false, appVersion = 'dev' }) => {
  return (
    <footer className="legal-bottom-bar" aria-label="Rechtliche Links">
      <span className="legal-bottom-items">
        <a className="legal-bottom-link sb-link" href="https://www.swissbit.com/de/impressum/" target="_blank" rel="noreferrer">
          Impressum
        </a>
        {showTerms && (
          <a className="legal-bottom-link sb-link" href={termsUrl} target="_blank" rel="noreferrer">
            Geschäftsbedingungen
          </a>
        )}
        <a className="legal-bottom-link sb-link" href={privacyPolicyUrl} target="_blank" rel="noreferrer">
          Datenschutz
        </a>
        <span className="legal-bottom-release">
          <a className="legal-bottom-link sb-link" href="/changelog">
            Changelog
          </a>
          <span className="legal-bottom-version">{appVersion}</span>
        </span>
      </span>
    </footer>
  )
}

const ChangelogPage = ({ appVersion = 'dev' }) => (
  <div className="changelog-page sb-scope">
    <main className="changelog-page-main">
      <header className="changelog-page-header">
        <div>
          <div className="changelog-page-product">ELeFANT</div>
          <h1>Changelog</h1>
        </div>
        <a className="sb-link changelog-page-back" href="/">← ELeFANT</a>
      </header>
      <div className="changelog-page-introduction">
        <p>This changelog applies to product version {WEB_CHANGELOG.appliesToProductVersion}.</p>
        <p>Gaps in version numbers are expected because product releases without changes relevant to Web users are omitted.</p>
      </div>
      <div className="changelog-page-releases" data-i18n-skip="1">
        {WEB_CHANGELOG.releases.map((release) => (
          <section className="changelog-release" id={`v${release.version}`} key={release.version}>
            <h2>
              <a href={`#v${release.version}`}>v{release.version}</a>
              <time dateTime={release.date}>{release.date}</time>
            </h2>
            <ul>
              {release.changes.map((change) => <li key={change}>{change}</li>)}
            </ul>
          </section>
        ))}
      </div>
    </main>
    <LegalBottomBar appVersion={appVersion} />
  </div>
)

const highlightSearchSubstring = (value, query, skipTranslation = false) => {
  const text = value == null || value === '' ? '-' : String(value)
  const needle = String(query || '').trim()
  if (!needle) return skipTranslation ? <span data-i18n-skip="1">{text}</span> : text
  const haystack = text.toLowerCase()
  const lowerNeedle = needle.toLowerCase()
  if (!lowerNeedle) return skipTranslation ? <span data-i18n-skip="1">{text}</span> : text
  let from = 0
  let hit = haystack.indexOf(lowerNeedle, from)
  if (hit === -1) return skipTranslation ? <span data-i18n-skip="1">{text}</span> : text
  const nodes = []
  let idx = 0
  while (hit !== -1) {
    if (hit > from) nodes.push(text.slice(from, hit))
    nodes.push(
      <mark className="search-substring-mark" key={`m-${idx}`}>
        {text.slice(hit, hit + needle.length)}
      </mark>
    )
    idx += 1
    from = hit + needle.length
    hit = haystack.indexOf(lowerNeedle, from)
  }
  if (from < text.length) nodes.push(text.slice(from))
  return skipTranslation ? <span data-i18n-skip="1">{nodes}</span> : nodes
}

const decodeBase64UrlToArrayBuffer = (value) => {
  const input = String(value || '')
  const padded = input + '='.repeat((4 - (input.length % 4)) % 4)
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

const encodeArrayBufferToBase64Url = (value) => {
  if (!value) return ''
  const bytes = value instanceof ArrayBuffer ? new Uint8Array(value) : new Uint8Array(value.buffer || value)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const toCredentialCreateOptions = (payload) => {
  const source = payload && typeof payload === 'object' ? payload : {}
  const options = { ...source }
  options.challenge = decodeBase64UrlToArrayBuffer(source.challenge)
  if (source.user && source.user.id) {
    options.user = { ...source.user, id: decodeBase64UrlToArrayBuffer(source.user.id) }
  }
  options.excludeCredentials = (source.excludeCredentials || []).map((item) => ({
    ...item,
    id: decodeBase64UrlToArrayBuffer(item.id)
  }))
  return options
}

const toCredentialRequestOptions = (payload) => {
  const source = payload && typeof payload === 'object' ? payload : {}
  const options = { ...source }
  options.challenge = decodeBase64UrlToArrayBuffer(source.challenge)
  options.allowCredentials = Array.isArray(source.allowCredentials)
    ? source.allowCredentials.map((item) => ({ ...item, id: decodeBase64UrlToArrayBuffer(item.id) }))
    : []
  return options
}

const serializePasskeyRegistrationCredential = (credential) => {
  const response = credential?.response || {}
  return {
    id: credential?.id || '',
    rawId: encodeArrayBufferToBase64Url(credential?.rawId),
    type: credential?.type || 'public-key',
    response: {
      clientDataJSON: encodeArrayBufferToBase64Url(response.clientDataJSON),
      attestationObject: encodeArrayBufferToBase64Url(response.attestationObject),
      transports: typeof response.getTransports === 'function' ? response.getTransports() : []
    }
  }
}

const serializePasskeyAuthenticationCredential = (credential) => {
  const response = credential?.response || {}
  return {
    id: credential?.id || '',
    rawId: encodeArrayBufferToBase64Url(credential?.rawId),
    type: credential?.type || 'public-key',
    response: {
      clientDataJSON: encodeArrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: encodeArrayBufferToBase64Url(response.authenticatorData),
      signature: encodeArrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle ? encodeArrayBufferToBase64Url(response.userHandle) : ''
    }
  }
}

const CheckboxMultiSelect = ({
  options,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  emptyLabel = 'Keine Daten',
  searchable = false,
  searchThreshold = UI_SEARCH_THRESHOLD,
  visibleRows = null
}) => {
  const selected = Array.isArray(value) ? value : []
  const selectedSet = new Set(selected)
  const [query, setQuery] = useState('')

  const showSearch = searchable && options.length >= searchThreshold
  const normalizedQuery = (showSearch ? query : '').trim().toLowerCase()
  const filteredOptions = useMemo(() => {
    if (!showSearch || !normalizedQuery) return options
    return options.filter((option) => {
      const label = String(option?.label || '').toLowerCase()
      return label.includes(normalizedQuery)
    })
  }, [normalizedQuery, options, showSearch])

  const toggle = (optionValue) => {
    if (disabled) return
    if (selectedSet.has(optionValue)) {
      onChange(selected.filter((item) => item !== optionValue))
      return
    }
    onChange([...selected, optionValue])
  }

  const shouldScroll = Number.isInteger(visibleRows) && visibleRows > 0 && filteredOptions.length > visibleRows
  const listStyle = shouldScroll ? { '--multi-select-max-rows': String(visibleRows) } : undefined

  return (
    <div className="multi-select-wrap">
      {showSearch && (
        <ClearableInput
          type="search"
          inputClassName="multi-select-search"
          placeholder="Filter..."
          value={query}
          onValueChange={setQuery}
          disabled={disabled}
        />
      )}
      <div className={`multi-select${readOnly ? ' is-readonly' : (disabled ? ' is-disabled' : '')}${shouldScroll ? ' is-scrollable' : ''}`} aria-disabled={disabled} style={listStyle}>
        {!filteredOptions.length && <div className="multi-select-empty">{emptyLabel}</div>}
        {filteredOptions.map((option) => (
          <label className="multi-select-option" key={option.value}>
            <input
              type="checkbox"
              checked={selectedSet.has(option.value)}
              onChange={() => toggle(option.value)}
              disabled={disabled}
            />
            <span data-i18n-skip="1">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

const MultiSelectFieldHeader = ({ label, options, value }) => {
  const optionValues = new Set(options.map((option) => option.value))
  const selectedCount = (Array.isArray(value) ? value : []).filter((item) => optionValues.has(item)).length
  const totalCount = options.length
  const accessibleCount = formatUiTemplate('selection.count', {
    selected: selectedCount,
    total: totalCount
  })

  return (
    <div className="multi-select-field-header">
      <label>{label}</label>
      <span className="multi-select-field-count" aria-label={accessibleCount}>
        <span className="multi-select-field-count-desktop" aria-hidden="true">{accessibleCount}</span>
        <span className="multi-select-field-count-mobile" aria-hidden="true">{selectedCount}/{totalCount}</span>
      </span>
    </div>
  )
}

const normalizeTenantUserRights = (raw) => {
  const source = raw && typeof raw === 'object' ? raw : {}
  const rights = {
    can_manage_companies: Boolean(source.can_manage_companies),
    can_manage_sites: Boolean(source.can_manage_sites),
    can_manage_pos_and_api_keys: Boolean(source.can_manage_pos_and_api_keys),
    can_run_exports: Boolean(source.can_run_exports),
    can_manage_users: Boolean(source.can_manage_users),
    can_manage_impersonation: Boolean(source.can_manage_impersonation),
  }
  // Hierarchical implication: higher scope includes lower scope rights.
  if (rights.can_manage_companies) {
    rights.can_manage_sites = true
    rights.can_manage_pos_and_api_keys = true
  } else if (rights.can_manage_sites) {
    rights.can_manage_pos_and_api_keys = true
  }
  return rights
}

const normalizeAuthUser = (raw) => {
  const source = raw && typeof raw === 'object' && raw.user && typeof raw.user === 'object'
    ? raw.user
    : (raw && typeof raw === 'object' ? raw : {})
  return {
    ...source,
    role: typeof source.role === 'string' ? source.role : '',
    preferred_lang: String(source.preferred_lang || '').toLowerCase() === 'de' ? 'de' : (String(source.preferred_lang || '').toLowerCase() === 'en' ? 'en' : ''),
    rights: normalizeTenantUserRights(source.rights),
    impersonation: source.impersonation || { active: false }
  }
}

function Application({ authToken, authUser, authRestoring, onReplaceAuthSession, onClearAuthSession, onUpdateAuthUser }) {
  const TIME_RANGE_REQUIRED_ERROR = 'Bitte Zeitraum vollständig wählen (Von/Bis).'
  const browserDefaultLang = useMemo(() => detectBrowserUiLang(), [])
  const initialUiLang = authUser?.preferred_lang === 'de' || authUser?.preferred_lang === 'en'
    ? authUser.preferred_lang
    : browserDefaultLang
  const [uiLang, setUiLang] = useState(initialUiLang)
  const defaultUploadRange = useMemo(() => getDefaultUploadRange(), [])
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState(import.meta.env.VITE_LOGIN_PASSWORD || '')
  const [loginError, setLoginError] = useState('')
  const [loginFieldErrors, setLoginFieldErrors] = useState({ email: false, password: false })
  const [loginInfo, setLoginInfo] = useState('')
  const [loginBusy, setLoginBusy] = useState(false)
  const [forgotPasswordBusy, setForgotPasswordBusy] = useState(false)
  const [passkeyLoginBusy, setPasskeyLoginBusy] = useState(false)
  const [resetPasswordNew, setResetPasswordNew] = useState('')
  const [resetPasswordRepeat, setResetPasswordRepeat] = useState('')
  const [resetPasswordBusy, setResetPasswordBusy] = useState(false)
  const [resetPasswordError, setResetPasswordError] = useState('')
  const [resetPasswordFieldErrors, setResetPasswordFieldErrors] = useState({ newPassword: false, repeatPassword: false })
  const [activationTermsAccepted, setActivationTermsAccepted] = useState(false)
  const [termsDialogAccepted, setTermsDialogAccepted] = useState(false)
  const [termsDialogBusy, setTermsDialogBusy] = useState(false)
  const [termsDialogError, setTermsDialogError] = useState('')
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState('')
  const [resetTokenValidating, setResetTokenValidating] = useState(false)
  const [resetTokenValid, setResetTokenValid] = useState(null)
  const [active, setActive] = useState('Administration')
  const [profiles, setProfiles] = useState([])
  const [apiKeys, setApiKeys] = useState([])
  const [selectedApiKeyIds, setSelectedApiKeyIds] = useState([])
  const [bulkApiKeyApprovalBusy, setBulkApiKeyApprovalBusy] = useState(false)
  const [bulkApiKeyApprovalStatus, setBulkApiKeyApprovalStatus] = useState('')
  const [exports, setExports] = useState([])
  const [companies, setCompanies] = useState([])
  const [companiesWithDeleted, setCompaniesWithDeleted] = useState([])
  const [sites, setSites] = useState([])
  const [sitesWithDeleted, setSitesWithDeleted] = useState([])
  const [posList, setPosList] = useState([])
  const [posListWithDeleted, setPosListWithDeleted] = useState([])
  const [companyName, setCompanyName] = useState('')
  const [companyCountry, setCompanyCountry] = useState('DE')
  const [companyCreateError, setCompanyCreateError] = useState('')
  const [companyNameInvalid, setCompanyNameInvalid] = useState(false)
  const [siteName, setSiteName] = useState('')
  const [siteCreateError, setSiteCreateError] = useState('')
  const [siteNameInvalid, setSiteNameInvalid] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState('')
  const [createPosDialog, setCreatePosDialog] = useState({
    siteId: '',
    posId: '',
    posLabel: '',
    tseType: 'swissbit_cloud_tse',
    tseSerialNumber: '',
    status: '',
    fieldErrors: {},
    apiKey: '',
    copied: false
  })
  const [showCompanyCreate, setShowCompanyCreate] = useState(false)
  const [expandedCompanies, setExpandedCompanies] = useState({})
  const [expandedSites, setExpandedSites] = useState({})
  const [organizationSearchExpansion, setOrganizationSearchExpansion] = useState({ query: '', companies: {}, sites: {} })
  const [editCompany, setEditCompany] = useState({ id: '', name: '' })
  const [editSite, setEditSite] = useState({ id: '', name: '' })
  const [editPos, setEditPos] = useState({
    id: '',
    posSerial: '',
    posLabel: '',
    tseType: 'swissbit_cloud_tse',
    tseSerialNumber: '',
    status: '',
    fieldErrors: {}
  })
  const [editTenant, setEditTenant] = useState(false)
  const [tenantNameEdit, setTenantNameEdit] = useState('')
  const [uploadCompanyId, setUploadCompanyId] = useState('')
  const [uploadSiteId, setUploadSiteId] = useState('')
  const [uploadCompanySearchTerm, setUploadCompanySearchTerm] = useState('')
  const [tenantAdminOrgName, setTenantAdminOrgName] = useState('')
  const [tenantAdminName, setTenantAdminName] = useState('')
  const [tenantAdminEmail, setTenantAdminEmail] = useState('')
  const [showTenantCreate, setShowTenantCreate] = useState(false)
  const [editTenantAdmin, setEditTenantAdmin] = useState({ userId: '', organizationName: '', name: '', email: '' })
  const [expandedPlatformOrgId, setExpandedPlatformOrgId] = useState('')
  const [expandedPlatformMobileRowId, setExpandedPlatformMobileRowId] = useState('')
  const [platformOrgSearchTerm, setPlatformOrgSearchTerm] = useState('')
  const [tenantAdmins, setTenantAdmins] = useState([])
  const [resellerAdmins, setResellerAdmins] = useState([])
  const [resendInvitationBusyUserId, setResendInvitationBusyUserId] = useState('')
  const [resellerAdminName, setResellerAdminName] = useState('')
  const [resellerAdminContactName, setResellerAdminContactName] = useState('')
  const [resellerAdminEmail, setResellerAdminEmail] = useState('')
  const [showResellerCreate, setShowResellerCreate] = useState(false)
  const [resellerSearchTerm, setResellerSearchTerm] = useState('')
  const [editResellerAdmin, setEditResellerAdmin] = useState({ userId: '', name: '', contactName: '', email: '' })
  const [expandedPlatformResellerId, setExpandedPlatformResellerId] = useState('')
  const [platformOrgExpandedResellerIds, setPlatformOrgExpandedResellerIds] = useState({})
  const [resellerTenantOrgName, setResellerTenantOrgName] = useState('')
  const [resellerTenantName, setResellerTenantName] = useState('')
  const [resellerTenantEmail, setResellerTenantEmail] = useState('')
  const [resellerTenantRequestImpersonation, setResellerTenantRequestImpersonation] = useState(true)
  const [resellerSubRequestImpersonation, setResellerSubRequestImpersonation] = useState(true)
  const [showResellerTenantCreate, setShowResellerTenantCreate] = useState(false)
  const [showResellerSubCreate, setShowResellerSubCreate] = useState(false)
  const [editResellerTenantAdmin, setEditResellerTenantAdmin] = useState({ userId: '', tenantId: '', organizationName: '', name: '', email: '' })
  const [expandedResellerTenantId, setExpandedResellerTenantId] = useState('')
  const [expandedResellerMobileRowId, setExpandedResellerMobileRowId] = useState('')
  const [resellerOrgExpandedResellerIds, setResellerOrgExpandedResellerIds] = useState({})
  const [resellerTenantAdmins, setResellerTenantAdmins] = useState([])
  const [resellerImpersonationPermissions, setResellerImpersonationPermissions] = useState([])
  const [resellerImpersonationError, setResellerImpersonationError] = useState('')
  const [impersonationNotificationDismissed, setImpersonationNotificationDismissed] = useState(() => (
    typeof window !== 'undefined' && window.sessionStorage.getItem(IMPERSONATION_NOTIFICATION_DISMISSED_KEY) === '1'
  ))
  const [resellerImpersonationSearchTerm, setResellerImpersonationSearchTerm] = useState('')
  const [platformError, setPlatformError] = useState('')
  const [platformSuccess, setPlatformSuccess] = useState('')
  const [adminFieldErrors, setAdminFieldErrors] = useState({ tenantAdmin: {}, resellerAdmin: {}, resellerTenantAdmin: {}, tenantUser: {}, tenantImpersonation: {} })
  const [tenantUsers, setTenantUsers] = useState([])
  const [tenantImpersonationPermissions, setTenantImpersonationPermissions] = useState([])
  const [tenantImpersonationSearchTerm, setTenantImpersonationSearchTerm] = useState('')
  const [tenantImpersonationEmail, setTenantImpersonationEmail] = useState('')
  const [tenantImpersonationError, setTenantImpersonationError] = useState('')
  const [showTenantImpersonationCreate, setShowTenantImpersonationCreate] = useState(false)
  const [expandedTenantImpersonationPermissionId, setExpandedTenantImpersonationPermissionId] = useState('')
  const [tenantUserName, setTenantUserName] = useState('')
  const [tenantUserEmail, setTenantUserEmail] = useState('')
  const [tenantUserRights, setTenantUserRights] = useState(TENANT_USER_RIGHTS_DEFAULT)
  const [showTenantUserCreate, setShowTenantUserCreate] = useState(false)
  const [expandedTenantUserId, setExpandedTenantUserId] = useState('')
  const [editTenantUser, setEditTenantUser] = useState({ userId: '', name: '', email: '', rights: TENANT_USER_RIGHTS_DEFAULT })
  const [tenantUserError, setTenantUserError] = useState('')
  const [tenantUserSuccess, setTenantUserSuccess] = useState('')
  const [rightsDialog, setRightsDialog] = useState({ open: false, mode: 'create', userId: '', label: '' })
  const [rightsDraft, setRightsDraft] = useState(TENANT_USER_RIGHTS_DEFAULT)
  const [confirmationDialog, setConfirmationDialog] = useState({ open: false, title: '', message: '', confirmLabel: '', tone: 'default', onConfirm: null, sessionExitKind: '' })
  const [pendingSessionExitKind, setPendingSessionExitKind] = useState('')
  const sessionExitActionsRef = useRef({})
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [showUserSettings, setShowUserSettings] = useState(false)
  const [showTenantOrganizationPanel, setShowTenantOrganizationPanel] = useState(false)
  const [showTenantUsersPanel, setShowTenantUsersPanel] = useState(false)
  const [showTenantFullAccessPanel, setShowTenantFullAccessPanel] = useState(false)
  const [preferredLanguageDraft, setPreferredLanguageDraft] = useState(initialUiLang)
  const [settingsBusy, setSettingsBusy] = useState(false)
  const [settingsError, setSettingsError] = useState('')
  const [settingsSuccess, setSettingsSuccess] = useState('')
  const [passwordNew, setPasswordNew] = useState('')
  const [passwordRepeat, setPasswordRepeat] = useState('')
  const [passwordBusy, setPasswordBusy] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordFieldErrors, setPasswordFieldErrors] = useState({ newPassword: false, repeatPassword: false })
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passkeys, setPasskeys] = useState([])
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  const [passkeyError, setPasskeyError] = useState('')
  const [passkeySuccess, setPasskeySuccess] = useState('')
  const [showPasskeyPanel, setShowPasskeyPanel] = useState(false)
  const [showPasskeyCreate, setShowPasskeyCreate] = useState(false)
  const [passkeyName, setPasskeyName] = useState(PASSKEY_DEFAULT_NAME)
  const [passkeyNameInvalid, setPasskeyNameInvalid] = useState(false)
  const [editPasskey, setEditPasskey] = useState({ passkeyId: '', name: '' })
  const [uploadArtifactType, setUploadArtifactType] = useState('tse_tar')
  const [uploadCustomArtifactLabel, setUploadCustomArtifactLabel] = useState('')
  const [uploadFrom, setUploadFrom] = useState(defaultUploadRange.from)
  const [uploadTo, setUploadTo] = useState(defaultUploadRange.to)
  const [uploadPosRefs, setUploadPosRefs] = useState([])
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadBundledFiles, setUploadBundledFiles] = useState([])
  const [, setUploadLastAddedFile] = useState('')
  const [uploadBusy, setUploadBusyState] = useState(false)
  const uploadBusyRef = useRef(false)
  const setUploadBusy = (busy) => {
    uploadBusyRef.current = busy
    setUploadBusyState(busy)
  }
  const [uploadTransferProgress, setUploadTransferProgress] = useState({ active: false, loaded: 0, total: 0, percent: null })
  const [uploadError, setUploadError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState('')
  const [uploadPickerOpen, setUploadPickerOpen] = useState(false)
  const [uploadPickerMonth, setUploadPickerMonth] = useState(() => monthStart(new Date()))
  const uploadFileInputRef = useRef(null)
  const activeUploadArtifactIdRef = useRef('')
  const elefantAudioRef = useRef(null)
  const [apiKeysSearchTerm, setApiKeysSearchTerm] = useState('')
  const [expandedApiKeyMobileId, setExpandedApiKeyMobileId] = useState('')
  const [apiKeyActionDialog, setApiKeyActionDialog] = useState({
    open: false,
    mode: 'approve',
    apiKey: '',
    posId: '',
    posSerial: '',
    companyId: '',
    siteId: '',
    createCompany: false,
    createSite: false,
    newCompanyName: '',
    newCompanyCountry: 'DE',
    newSiteName: '',
    reportedCompanyName: '',
    reportedCompanyCountry: '',
    reportedSiteName: '',
    reportedPosSerial: '',
    reportedPosLabel: '',
    status: '',
    busy: false
  })
  const [apiKeyOnboardingCode, setApiKeyOnboardingCode] = useState({
    code: '',
    busy: false,
    error: '',
    copied: false
  })
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('')
  const organizationSearchQuery = organizationSearchTerm.trim().toLowerCase()
  const [tenantUsersSearchTerm, setTenantUsersSearchTerm] = useState('')
  const [exportCompanyIds, setExportCompanyIds] = useState([])
  const [exportSiteIds, setExportSiteIds] = useState([])
  const [exportPosIds, setExportPosIds] = useState([])
  const [exportRangeStart, setExportRangeStart] = useState('')
  const [exportRangeEnd, setExportRangeEnd] = useState('')
  const [exportIncludeDeleted, setExportIncludeDeleted] = useState(false)
  const [exportPreview, setExportPreview] = useState(null)
  const [exportSelectedArtifactTypes, setExportSelectedArtifactTypes] = useState([])
  const [exportFilterApplied, setExportFilterApplied] = useState(false)
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState('')
  const [exportSuccess, setExportSuccess] = useState('')
  const [exportAuditorEmail, setExportAuditorEmail] = useState('')
  const [exportAuditorEmailEnabled, setExportAuditorEmailEnabled] = useState(false)
  const [exportAuditorEmailError, setExportAuditorEmailError] = useState('')
  const [exportPickerOpen, setExportPickerOpen] = useState(false)
  const [exportPickerMonth, setExportPickerMonth] = useState(() => monthStart(new Date()))
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const componentMountedRef = useRef(true)
  const i18nLiteralsRef = useRef({})
  const [i18nVersion, setI18nVersion] = useState(0)

  const apiBase = import.meta.env.VITE_API_BASE || '/api/v1'
  const appVersion = import.meta.env.VITE_APP_VERSION || 'dev'
  const isActivationRoute = typeof window !== 'undefined' && window.location.pathname === '/activate-account'
  const isPasswordTokenRoute = (typeof window !== 'undefined' && window.location.pathname === '/reset-password') || isActivationRoute
  const resetPasswordToken = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('token') || ''
  }, [])
  const [resetTokenPurpose, setResetTokenPurpose] = useState(isActivationRoute ? 'onboarding' : 'password_reset')
  const tenantId = authUser?.tenant_id || import.meta.env.VITE_TENANT_ID || 't-1'
  const fallbackRole = authToken ? '' : (import.meta.env.VITE_USER_ROLE || '')
  const userRole = authUser?.role || fallbackRole || ''
  const isTenantAdmin = userRole === 'tenant_admin'
  const isTenantUser = userRole === 'tenant_user'
  const isResellerAdmin = userRole === 'reseller_admin'
  const sessionRights = normalizeTenantUserRights(authUser?.rights)
  const effectiveRights = isTenantAdmin
    ? {
        can_manage_companies: true,
        can_manage_sites: true,
        can_manage_pos_and_api_keys: true,
        can_run_exports: true,
        can_manage_users: true,
        can_manage_impersonation: true,
      }
    : (isTenantUser ? sessionRights : TENANT_USER_RIGHTS_DEFAULT)
  const canRenameOrganization = isTenantAdmin
  const canEditCompanies = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_manage_companies))
  const canEditSites = canEditCompanies || (isTenantUser && Boolean(effectiveRights.can_manage_sites))
  const canEditPos = canEditSites || (isTenantUser && Boolean(effectiveRights.can_manage_pos_and_api_keys))
  const canRunExports = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_run_exports))
  const canManageApiKeys = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_manage_pos_and_api_keys))
  const canManageUsers = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_manage_users))
  const canManageImpersonation = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_manage_impersonation))
  const createdPosDisplayName = String(
    createPosDialog.posLabel || createPosDialog.posId || ''
  ).trim() || '-'
  const createdPosSerial = String(createPosDialog.posId || '').trim() || '-'
  const isStructureNavigation = active === 'Unternehmensstruktur'
  const showTopbarOrg = isTenantAdmin || isTenantUser
  const roleDisplayLabel = userRole === 'platform_admin'
    ? 'Platform Admin'
    : (userRole === 'reseller_admin'
        ? 'Reseller Admin'
        : (userRole === 'tenant_admin'
            ? 'Tenant Admin'
            : (userRole === 'tenant_user' ? 'Tenant User' : 'Nutzer')))
  const topbarUserLabel = roleDisplayLabel
  const userDisplayName = authUser?.name || authUser?.full_name || authUser?.username || ''
  const showTopbarUserName = !isResellerAdmin && userRole !== 'platform_admin'
  const showTopbarRole = false
  const passkeySupported = typeof window !== 'undefined' && Boolean(window.PublicKeyCredential)
  const navItems = useMemo(
    () => {
      if (userRole === 'platform_admin') return [{ label: 'Administration', icon: 'settings' }]
      if (userRole === 'reseller_admin') return [{ label: 'Administration', icon: 'settings' }]
      const items = NAV_ITEMS.filter((item) => (item.label === 'Prüfexport' ? canRunExports : true))
      if (isTenantAdmin || isTenantUser) {
        return items.flatMap((item) => (item.label === 'Administration' ? [{ label: 'Unternehmensstruktur', icon: 'hierarchy' }, item] : [item]))
      }
      return items
    },
    [userRole, canRunExports, isTenantAdmin, isTenantUser]
  )
  const pendingImpersonationPermissions = useMemo(() => {
    const permissions = userRole === 'reseller_admin'
      ? resellerImpersonationPermissions
      : (userRole === 'tenant_admin' ? tenantImpersonationPermissions : [])
    return permissions.filter((item) => String(item.status || '').toLowerCase() === IMPERSONATION_STATUS_REQUESTED)
  }, [resellerImpersonationPermissions, tenantImpersonationPermissions, userRole])
  const pendingImpersonationPermissionCount = pendingImpersonationPermissions.length
  const pendingImpersonationRequesterNames = pendingImpersonationPermissions.map((item) => (
    item.requester_name || item.helper_name || item.requester_email || item.helper_email || '-'
  ))
  const hashTarget = useMemo(() => {
    if (typeof window === 'undefined') return ''
    const raw = String(window.location.hash || '').trim().toLowerCase().replace(/^#/, '')
    if (!raw) return ''
    const map = {
      'web-upload': 'Web-Upload',
      'pruefexport': 'Prüfexport',
      'api-keys': 'API-Keys',
      'administration': 'Administration',
      'unternehmensstruktur': 'Unternehmensstruktur'
    }
    return map[raw] || ''
  }, [])

  const authHeaders = useMemo(
    () => ({
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      ...(userRole ? { 'X-User-Role': userRole } : {})
    }),
    [authToken, tenantId, userRole]
  )
  const isOnboardingPasswordFlow = resetTokenPurpose === 'onboarding' || isActivationRoute
  const resetRouteTitle = isOnboardingPasswordFlow ? 'Konto aktivieren' : 'Passwort zurücksetzen'
  const resetRouteSubmitIdle = isOnboardingPasswordFlow ? 'Passwort setzen' : 'Passwort zurücksetzen'
  const resetRouteSubmitBusy = isOnboardingPasswordFlow ? 'Konto wird aktiviert…' : 'Speichern…'
  const resetRouteValidating = isOnboardingPasswordFlow ? 'Prüfe Aktivierungslink…' : 'Prüfe Link…'
  const resetRouteMissingMessage = isOnboardingPasswordFlow ? 'Aktivierungslink fehlt oder ist ungültig.' : 'Reset-Link fehlt oder ist ungültig.'
  const resetRouteInvalidMessage = isOnboardingPasswordFlow ? 'Aktivierungslink ist ung\u00fcltig oder abgelaufen. Bitte fordern Sie eine neue Einladung bei Ihrem Administrator an.' : 'Reset-Link ist ungültig oder abgelaufen.'
  const resetRouteValidationFailedMessage = isOnboardingPasswordFlow ? 'Aktivierungslink konnte nicht geprüft werden.' : 'Reset-Link konnte nicht geprüft werden.'
  const resetRouteSuccessMessage = isOnboardingPasswordFlow ? 'Konto aktiviert. Sie können sich jetzt einloggen.' : 'Passwort zurückgesetzt. Sie können sich jetzt einloggen.'

  useEffect(() => {
    componentMountedRef.current = true
    return () => {
      componentMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    setOrganizationSearchExpansion({ query: organizationSearchQuery, companies: {}, sites: {} })
  }, [organizationSearchQuery])

  useEffect(() => {
    if (authUser) {
      const preferred = String(authUser?.preferred_lang || '').toLowerCase()
      const effective = preferred === 'de' || preferred === 'en' ? preferred : 'en'
      setUiLang((prev) => (prev === effective ? prev : effective))
      setPreferredLanguageDraft(effective)
      return
    }
    setUiLang((prev) => (prev === browserDefaultLang ? prev : browserDefaultLang))
    setPreferredLanguageDraft(browserDefaultLang)
  }, [authUser, authUser?.preferred_lang, browserDefaultLang])

  useEffect(() => {
    if (!uploadSuccess && !exportSuccess) return undefined
    const timeoutId = window.setTimeout(() => {
      setUploadSuccess('')
      setExportSuccess('')
    }, 7000)
    return () => window.clearTimeout(timeoutId)
  }, [uploadSuccess, exportSuccess])

  useEffect(() => {
    setCurrentUiLang(uiLang)
    i18nLiteralsRef.current = CURRENT_UI_LITERALS
    setI18nVersion((value) => value + 1)
  }, [uiLang])

  useEffect(() => {
    const root = document.body
    const uninstall = installDomTranslator(root, i18nLiteralsRef.current)
    return uninstall
  }, [i18nVersion, exports, apiKeys, companies, sites, posList, active, authUser, loginError, loginInfo, uploadError, exportError, tenantUserError, tenantUserSuccess, tenantImpersonationError, showTenantOrganizationPanel, showTenantUsersPanel, showTenantFullAccessPanel])

  useEffect(() => {
    setCreatePosDialog((prev) => (prev.apiKey ? {
      siteId: '', posId: '', posLabel: '', tseType: 'swissbit_cloud_tse',
      tseSerialNumber: '', status: '', fieldErrors: {}, apiKey: '', copied: false
    } : prev))
  }, [active])

  const clearAuth = () => {
    onClearAuthSession()
  }

  const dismissImpersonationNotification = () => {
    sessionStorage.setItem(IMPERSONATION_NOTIFICATION_DISMISSED_KEY, '1')
    setImpersonationNotificationDismissed(true)
  }

  const applyAuthSession = (payload) => {
    onReplaceAuthSession(payload)
  }

  const parseError = (res) => parseApiError(res, { uploadFileLimitLabel: UPLOAD_FILE_LIMIT_LABEL })

  const setAdminFormFieldErrors = (form, fieldErrors) => {
    setAdminFieldErrors((prev) => ({ ...prev, [form]: fieldErrors }))
  }

  const clearAdminFormFieldError = (form, field) => {
    setAdminFieldErrors((prev) => ({ ...prev, [form]: { ...prev[form], [field]: false } }))
  }

  const togglePasswordChange = () => {
    if (showPasswordChange) {
      setPasswordNew('')
      setPasswordRepeat('')
      setPasswordError('')
      setPasswordFieldErrors({ newPassword: false, repeatPassword: false })
      setPasswordSuccess('')
    }
    setShowPasswordChange((prev) => !prev)
  }

  const toggleAdminCreateForm = (form, isOpen, setOpen) => {
    if (isOpen) {
      setAdminFormFieldErrors(form, {})
      setPlatformError('')
      setPlatformSuccess('')
    }
    setOpen((prev) => !prev)
  }

  const toggleTenantUserCreate = () => {
    if (showTenantUserCreate) {
      setAdminFormFieldErrors('tenantUser', {})
      setTenantUserError('')
      setTenantUserSuccess('')
    }
    setShowTenantUserCreate((prev) => !prev)
  }

  const toggleTenantImpersonationCreate = () => {
    if (showTenantImpersonationCreate) {
      setAdminFormFieldErrors('tenantImpersonation', {})
      setTenantImpersonationError('')
    }
    setShowTenantImpersonationCreate((prev) => !prev)
  }

  const toggleUserSettings = () => {
    if (showUserSettings) {
      setSettingsError('')
      setSettingsSuccess('')
    }
    setShowUserSettings((prev) => !prev)
  }

  const togglePasskeyPanel = () => {
    if (showPasskeyPanel) {
      setPasskeyError('')
      setPasskeySuccess('')
      setShowPasskeyCreate(false)
      setEditPasskey({ passkeyId: '', name: '' })
    }
    setShowPasskeyPanel((prev) => !prev)
  }

  const handleLogin = async (event) => {
    event.preventDefault()
    setLoginError('')
    setLoginInfo('')
    const nextFieldErrors = {
      email: !loginEmail.trim(),
      password: !loginPassword
    }
    if (nextFieldErrors.email || nextFieldErrors.password) {
      setLoginFieldErrors(nextFieldErrors)
      setLoginError(nextFieldErrors.email && nextFieldErrors.password
        ? 'Bitte E-Mail und Passwort eingeben.'
        : nextFieldErrors.email
          ? 'Bitte E-Mail eingeben.'
          : 'Bitte Passwort eingeben.')
      return
    }
    setLoginFieldErrors({ email: false, password: false })
    setLoginBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      })
      if (!res.ok) {
        if (res.status === 401) {
          setLoginFieldErrors({ email: true, password: true })
        }
        throw new Error(await parseError(res))
      }
      const data = await res.json()
      applyAuthSession({
        token: data.token,
        user: data.user
      })
    } catch (err) {
      setLoginError(err?.message || 'Login fehlgeschlagen.')
    } finally {
      setLoginBusy(false)
    }
  }

  const handleForgotPassword = async () => {
    setLoginError('')
    setLoginInfo('')
    const email = loginEmail.trim().toLowerCase()
    if (!email) {
      setLoginError('Bitte E-Mail eingeben.')
      return
    }
    setForgotPasswordBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/password/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      setLoginInfo('Wenn die E-Mail-Adresse bekannt ist, wurde ein Reset-Link versendet.')
    } catch (err) {
      setLoginError(err?.message || 'Passwort vergessen fehlgeschlagen.')
    } finally {
      setForgotPasswordBusy(false)
    }
  }

  const handleResetPassword = async (event) => {
    event.preventDefault()
    setResetPasswordError('')
    setResetPasswordSuccess('')
    setResetPasswordFieldErrors({ newPassword: false, repeatPassword: false })
    if (resetTokenValid === false) {
      setResetPasswordError(resetRouteInvalidMessage)
      return
    }
    if (!resetPasswordToken) {
      setResetPasswordError('Reset-Link fehlt oder ist ungültig.')
      return
    }
    if (isOnboardingPasswordFlow && !activationTermsAccepted) {
      setResetPasswordError('Bitte akzeptieren Sie die Geschäftsbedingungen.')
      return
    }
    if (!resetPasswordNew || !resetPasswordRepeat) {
      const nextFieldErrors = { newPassword: !resetPasswordNew, repeatPassword: !resetPasswordRepeat }
      setResetPasswordFieldErrors(nextFieldErrors)
      setResetPasswordError(nextFieldErrors.newPassword && nextFieldErrors.repeatPassword
        ? 'Bitte alle Passwortfelder ausfüllen.'
        : nextFieldErrors.newPassword
          ? 'Bitte neues Passwort eingeben.'
          : 'Bitte Passwort wiederholen.')
      return
    }
    if (resetPasswordNew.length < 8) {
      setResetPasswordFieldErrors({ newPassword: true, repeatPassword: false })
      setResetPasswordError('Neues Passwort muss mindestens 8 Zeichen haben.')
      return
    }
    if (resetPasswordNew !== resetPasswordRepeat) {
      setResetPasswordFieldErrors({ newPassword: false, repeatPassword: true })
      setResetPasswordError('Neues Passwort und Wiederholung stimmen nicht überein.')
      return
    }
    setResetPasswordBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: resetPasswordToken,
          new_password: resetPasswordNew,
          ...(isOnboardingPasswordFlow ? { terms_accepted: activationTermsAccepted } : {})
        })
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      setResetPasswordSuccess(resetRouteSuccessMessage)
      setResetPasswordNew('')
      setResetPasswordRepeat('')
    } catch (err) {
      setResetPasswordError(err?.message || 'Passwort zurücksetzen fehlgeschlagen.')
    } finally {
      setResetPasswordBusy(false)
    }
  }

  const handleAcceptTerms = async () => {
    if (!termsDialogAccepted || termsDialogBusy) return
    setTermsDialogBusy(true)
    setTermsDialogError('')
    try {
      const res = await fetch(`${apiBase}/auth/terms/accept`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ terms_accepted: true })
      })
      if (!res.ok) throw new Error(await parseError(res))
      const data = await res.json()
      onUpdateAuthUser((current) => ({ ...current, terms_accepted_at: data.terms_accepted_at }))
    } catch (err) {
      setTermsDialogError(err?.message || 'Die Zustimmung konnte nicht gespeichert werden.')
    } finally {
      setTermsDialogBusy(false)
    }
  }

  useEffect(() => {
    if (!isPasswordTokenRoute) return
    if (!resetPasswordToken) {
      setResetTokenValid(false)
      setResetPasswordError(resetRouteMissingMessage)
      return
    }
    let cancelled = false
    const validateToken = async () => {
      setResetTokenValidating(true)
      try {
        const res = await fetch(`${apiBase}/auth/password/reset/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetPasswordToken })
        })
        if (!res.ok) {
          if (!cancelled) {
            setResetTokenValid(false)
            setResetPasswordError(resetRouteInvalidMessage)
          }
          return
        }
        const data = await res.json()
        if (cancelled) return
        if (data?.valid) {
          setResetTokenValid(true)
          setResetTokenPurpose(data?.purpose === 'onboarding' ? 'onboarding' : 'password_reset')
          setResetPasswordError('')
        } else {
          setResetTokenValid(false)
          setResetPasswordError(resetRouteInvalidMessage)
        }
      } catch {
        if (!cancelled) {
          setResetTokenValid(false)
          setResetPasswordError(resetRouteValidationFailedMessage)
        }
      } finally {
        if (!cancelled) setResetTokenValidating(false)
      }
    }
    validateToken()
    return () => {
      cancelled = true
    }
  }, [apiBase, isPasswordTokenRoute, resetPasswordToken, resetRouteInvalidMessage, resetRouteMissingMessage, resetRouteValidationFailedMessage])

  const discardOpenUploadArtifacts = async () => {
    const artifactIds = [...new Set([
      ...uploadBundledFiles.map((item) => item.fileId),
      activeUploadArtifactIdRef.current
    ].filter(Boolean))]
    if (!artifactIds.length) return { sessionExpired: false }
    const results = await Promise.allSettled(artifactIds.map((artifactId) => (
      fetch(`${apiBase}/web/artifacts/${artifactId}`, {
        method: 'DELETE',
        headers: authHeaders
      })
    )))
    return {
      sessionExpired: results.some((result) => (
        result.status === 'fulfilled' && result.value.status === 401
      ))
    }
  }

  const prepareImpersonationTransition = async () => {
    if (uploadBusyRef.current) return false
    const cleanup = await discardOpenUploadArtifacts()
    if (cleanup.sessionExpired) {
      clearAuth()
      return false
    }
    return true
  }

  const performLogout = async () => {
    if (uploadBusyRef.current) return
    try {
      await discardOpenUploadArtifacts()
      if (authToken) {
        await fetch(`${apiBase}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` }
        })
      }
    } catch {
      // ignore
    } finally {
      clearAuth()
    }
  }

  const refreshTenantAdmins = async () => {
    const res = await fetch(`${apiBase}/platform/tenant-admins`, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setTenantAdmins(Array.isArray(data) ? data : [])
    }
  }

  const refreshResellerAdmins = async () => {
    const endpoint = authUser?.role === 'reseller_admin'
      ? `${apiBase}/reseller/reseller-admins`
      : `${apiBase}/platform/reseller-admins`
    const res = await fetch(endpoint, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setResellerAdmins(Array.isArray(data) ? data : [])
    }
  }

  const refreshResellerTenantAdmins = async () => {
    const res = await fetch(`${apiBase}/reseller/tenant-admins`, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setResellerTenantAdmins(Array.isArray(data) ? data : [])
    }
  }

  const refreshResellerImpersonationPermissions = async () => {
    const res = await fetch(`${apiBase}/reseller/impersonation-permissions`, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setResellerImpersonationPermissions(Array.isArray(data) ? data : [])
      return
    }
    setResellerImpersonationPermissions([])
  }

  const refreshTenantImpersonationPermissions = async () => {
    const res = await fetch(`${apiBase}/tenant/impersonation-permissions`, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setTenantImpersonationPermissions(Array.isArray(data) ? data : [])
    }
  }

  const refreshTenantUsers = async () => {
    const res = await fetch(`${apiBase}/tenant/users?ts=${Date.now()}`, {
      headers: authHeaders,
      cache: 'no-store'
    })
    if (res.ok) {
      const data = await res.json()
      const normalized = Array.isArray(data)
        ? data.map((item) => ({ ...item, rights: normalizeTenantUserRights(item?.rights) }))
        : []
      setTenantUsers(normalized)
    }
  }

  const refreshPasskeys = async () => {
    if (!authToken || !authUser) return
    const res = await fetch(`${apiBase}/auth/passkeys`, { headers: authHeaders })
    if (!res.ok) {
      setPasskeys([])
      return
    }
    const data = await res.json()
    setPasskeys(Array.isArray(data) ? data : [])
  }

  const handleCreateTenantAdmin = async () => {
    setPlatformError('')
    const fieldErrors = { organizationName: !tenantAdminOrgName.trim(), name: !tenantAdminName.trim(), email: !tenantAdminEmail.trim() }
    if (fieldErrors.organizationName || fieldErrors.name || fieldErrors.email) {
      setAdminFormFieldErrors('tenantAdmin', fieldErrors)
      setPlatformError('Bitte alle Pflichtfelder ausfüllen.')
      return
    }
    setAdminFormFieldErrors('tenantAdmin', {})
    const res = await fetch(`${apiBase}/platform/tenant-admins`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        organization_name: tenantAdminOrgName.trim(),
        name: tenantAdminName.trim(),
        email: tenantAdminEmail.trim()
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setTenantAdminOrgName('')
    setTenantAdminName('')
    setTenantAdminEmail('')
    await refreshTenantAdmins()
  }

  const handleCreateResellerAdmin = async () => {
    setPlatformError('')
    if (!resellerAdminEmail.trim()) {
      setAdminFormFieldErrors('resellerAdmin', { email: true })
      setPlatformError('E-Mail ist erforderlich.')
      return
    }
    setAdminFormFieldErrors('resellerAdmin', {})
    const payload = { email: resellerAdminEmail.trim() }
    if (resellerAdminName.trim()) payload.name = resellerAdminName.trim()
    if (resellerAdminContactName.trim()) payload.contact_name = resellerAdminContactName.trim()
    if (authUser?.role === 'reseller_admin') {
      payload.request_impersonation = resellerSubRequestImpersonation
    }
    const endpoint = authUser?.role === 'reseller_admin'
      ? `${apiBase}/reseller/reseller-admins`
      : `${apiBase}/platform/reseller-admins`
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        setPlatformError(await parseError(res))
        return
      }
      setResellerAdminName('')
      setResellerAdminContactName('')
      setResellerAdminEmail('')
      setResellerSubRequestImpersonation(true)
      setShowResellerCreate(false)
      setShowResellerSubCreate(false)
      await refreshResellerAdmins()
    } catch (error) {
      setPlatformError(error instanceof Error ? error.message : 'Reseller konnte nicht angelegt werden.')
    }
  }

  const handleSaveResellerAdminEdit = async () => {
    setPlatformError('')
    if (!editResellerAdmin.userId || !editResellerAdmin.name.trim()) return
    const res = await fetch(`${apiBase}/platform/reseller-admins/${editResellerAdmin.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        name: editResellerAdmin.name.trim(),
        contact_name: String(editResellerAdmin.contactName || '').trim()
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })
    await refreshResellerAdmins()
  }

  const closeConfirmationDialog = () => {
    setPendingSessionExitKind('')
    setConfirmationDialog({ open: false, title: '', message: '', confirmLabel: '', tone: 'default', onConfirm: null, sessionExitKind: '' })
  }

  const openConfirmationDialog = ({ title = 'Aktion bestätigen', message, confirmLabel = 'Bestätigen', destructive = false, tone = destructive ? 'destructive' : 'default', onConfirm, sessionExitKind = '' }) => {
    setConfirmationDialog({ open: true, title, message, confirmLabel, tone, onConfirm, sessionExitKind })
  }

  const requestSessionExitWithPendingUpload = ({ title, confirmLabel, sessionExitKind }) => {
    const hasPendingUpload = uploadBundledFiles.length > 0 || Boolean(activeUploadArtifactIdRef.current) || uploadBusyRef.current
    if (!hasPendingUpload) {
      void sessionExitActionsRef.current[sessionExitKind]?.()
      return
    }
    openConfirmationDialog({
      title,
      message: uploadBusyRef.current
        ? 'Der aktuelle Upload-Vorgang wird zuerst abgeschlossen. Danach werden verbleibende Dateien und Upload-Auswahlen verworfen. Dies kann nicht rückgängig gemacht werden.'
        : 'Der offene Web-Upload enthält Dateien, die noch nicht archiviert sind. Beim Verlassen der aktuellen Sitzung werden diese Dateien und alle Upload-Auswahlen verworfen. Dies kann nicht rückgängig gemacht werden.',
      confirmLabel,
      destructive: true,
      sessionExitKind
    })
  }

  const handleLogout = () => {
    requestSessionExitWithPendingUpload({
      title: 'Offenen Upload verwerfen und ausloggen?',
      confirmLabel: 'Upload verwerfen und ausloggen',
      sessionExitKind: 'logout'
    })
  }

  const confirmDialogAction = () => {
    if (confirmationDialog.sessionExitKind) {
      if (uploadBusyRef.current) {
        setPendingSessionExitKind(confirmationDialog.sessionExitKind)
        return
      }
      const action = sessionExitActionsRef.current[confirmationDialog.sessionExitKind]
      closeConfirmationDialog()
      void action?.()
      return
    }
    const onConfirm = confirmationDialog.onConfirm
    closeConfirmationDialog()
    void onConfirm?.()
  }

  const handleDeleteResellerAdmin = async (userId, label, confirmed = false) => {
    setPlatformError('')
    const meta = resellerAdmins.find((item) => item.user_id === userId)
    const currentStatus = String(meta?.status || '').toLowerCase()
    const nextStatus = currentStatus === 'active' ? 'disabled' : 'active'
    const actionLabel = nextStatus === 'disabled' ? 'deaktivieren' : 'aktivieren'
    if (!confirmed) {
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.toggle.generic', { label, action: uiLiteral(actionLabel) }),
        confirmLabel: nextStatus === 'disabled' ? 'Deaktivieren' : 'Aktivieren',
        tone: nextStatus === 'disabled' ? 'warning' : 'default',
        onConfirm: () => handleDeleteResellerAdmin(userId, label, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/platform/reseller-admins/${userId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status: nextStatus })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshResellerAdmins()
  }

  const handleResendInvitation = async (userId) => {
    setPlatformError('')
    setPlatformSuccess('')
    setResendInvitationBusyUserId(userId)
    try {
      const res = await fetch(`${apiBase}/auth/users/${userId}/invitation/resend`, {
        method: 'POST',
        headers: authHeaders
      })
      if (!res.ok) {
        setPlatformError(await parseError(res))
        return
      }
      await refreshResellerAdmins()
      setPlatformSuccess('Einladung wurde erneut gesendet.')
    } catch {
      setPlatformError('Einladung konnte nicht erneut gesendet werden.')
    } finally {
      setResendInvitationBusyUserId('')
    }
  }

  const handleResendTenantUserInvitation = async (userId) => {
    setTenantUserError('')
    setTenantUserSuccess('')
    setResendInvitationBusyUserId(userId)
    try {
      const res = await fetch(`${apiBase}/auth/users/${userId}/invitation/resend`, {
        method: 'POST',
        headers: authHeaders
      })
      if (!res.ok) {
        setTenantUserError(await parseError(res))
        return
      }
      await refreshTenantUsers()
      setTenantUserSuccess('Einladung wurde erneut gesendet.')
    } catch {
      setTenantUserError('Einladung konnte nicht erneut gesendet werden.')
    } finally {
      setResendInvitationBusyUserId('')
    }
  }

  const handleResendTenantAdminInvitation = async (userId, isResellerSurface = false) => {
    setPlatformError('')
    setPlatformSuccess('')
    setResendInvitationBusyUserId(userId)
    try {
      const res = await fetch(`${apiBase}/auth/users/${userId}/invitation/resend`, {
        method: 'POST',
        headers: authHeaders
      })
      if (!res.ok) {
        setPlatformError(await parseError(res))
        return
      }
      if (isResellerSurface) {
        await refreshResellerTenantAdmins()
      } else {
        await refreshTenantAdmins()
      }
      setPlatformSuccess('Einladung wurde erneut gesendet.')
    } catch {
      setPlatformError('Einladung konnte nicht erneut gesendet werden.')
    } finally {
      setResendInvitationBusyUserId('')
    }
  }

  const handleUpdateResellerImpersonationPermissionStatus = async (permissionId, status, label = '', confirmed = false) => {
    setResellerImpersonationError('')
    if (status === IMPERSONATION_STATUS_REVOKED && !confirmed) {
      const displayLabel = label || uiLiteral('Reseller')
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.revoke.impersonation_access', { label: displayLabel }),
        confirmLabel: 'Widerrufen',
        destructive: true,
        onConfirm: () => handleUpdateResellerImpersonationPermissionStatus(permissionId, status, label, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/reseller/impersonation-permissions/${permissionId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status })
    })
    if (!res.ok) {
      setResellerImpersonationError(await parseError(res))
      return
    }
    await refreshResellerImpersonationPermissions()
    await refreshResellerAdmins()
  }

  const handleCreateResellerTenantAdmin = async () => {
    setPlatformError('')
    const fieldErrors = { organizationName: !resellerTenantOrgName.trim(), name: !resellerTenantName.trim(), email: !resellerTenantEmail.trim() }
    if (fieldErrors.organizationName || fieldErrors.name || fieldErrors.email) {
      setAdminFormFieldErrors('resellerTenantAdmin', fieldErrors)
      setPlatformError('Bitte alle Pflichtfelder ausfüllen.')
      return
    }
    setAdminFormFieldErrors('resellerTenantAdmin', {})
    const res = await fetch(`${apiBase}/reseller/tenant-admins`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        organization_name: resellerTenantOrgName.trim(),
        name: resellerTenantName.trim(),
        email: resellerTenantEmail.trim(),
        request_impersonation: resellerTenantRequestImpersonation
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setResellerTenantOrgName('')
    setResellerTenantName('')
    setResellerTenantEmail('')
    setResellerTenantRequestImpersonation(true)
    setShowResellerTenantCreate(false)
    await refreshResellerTenantAdmins()
  }

  const handleSaveResellerTenantAdminEdit = async () => {
    setPlatformError('')
    if (!editResellerTenantAdmin.userId || !editResellerTenantAdmin.organizationName.trim() || !editResellerTenantAdmin.name.trim()) return
    const res = await fetch(`${apiBase}/reseller/tenant-admins/${editResellerTenantAdmin.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        organization_name: editResellerTenantAdmin.organizationName.trim(),
        name: editResellerTenantAdmin.name.trim()
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setEditResellerTenantAdmin({ userId: '', tenantId: '', organizationName: '', name: '', email: '' })
    await refreshResellerTenantAdmins()
  }

  const handleToggleResellerTenantStatus = async (item, confirmed = false) => {
    setPlatformError('')
    const currentStatus = (item?.status || '').toLowerCase()
    const targetStatus = currentStatus === 'active' ? 'disabled' : 'active'
    if (targetStatus === 'disabled' && !confirmed) {
      const label = item?.organization_name || item?.name || item?.email || 'Organisation'
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.toggle.generic', { label, action: 'deaktivieren' }),
        confirmLabel: 'Deaktivieren',
        tone: 'warning',
        onConfirm: () => handleToggleResellerTenantStatus(item, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/reseller/tenants/${item.tenant_id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status: targetStatus })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshResellerTenantAdmins()
  }

  const handleRequestResellerTenantImpersonation = async (tenantId) => {
    setResellerImpersonationError('')
    const res = await fetch(apiBase + '/reseller/tenants/' + tenantId + '/impersonation-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders }
    })
    if (!res.ok) {
      setResellerImpersonationError(await parseError(res))
      return
    }
    await refreshResellerTenantAdmins()
  }

  const handleWithdrawResellerTenantImpersonation = async (tenantId, status, label = '', confirmed = false) => {
    setResellerImpersonationError('')
    if (String(status || '').toLowerCase() === IMPERSONATION_STATUS_APPROVED && !confirmed) {
      const displayLabel = label || uiLiteral('Organisation')
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.withdraw.impersonation_access', { label: displayLabel }),
        confirmLabel: 'Zurückziehen',
        destructive: true,
        onConfirm: () => handleWithdrawResellerTenantImpersonation(tenantId, status, label, true)
      })
      return
    }
    const res = await fetch(apiBase + '/reseller/tenants/' + tenantId + '/impersonation-withdraw', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders }
    })
    if (!res.ok) {
      setResellerImpersonationError(await parseError(res))
      return
    }
    await refreshResellerTenantAdmins()
  }

  const runSubResellerImpersonationMutation = async (userId, action) => {
    setResellerImpersonationError('')
    const res = await fetch(apiBase + '/reseller/reseller-admins/' + userId + '/impersonation-' + action, {
      method: 'POST',
      headers: authHeaders
    })
    if (!res.ok) {
      setResellerImpersonationError(await parseError(res))
      return
    }
    await refreshResellerAdmins()
  }

  const handleRequestSubResellerImpersonation = async (userId) => {
    await runSubResellerImpersonationMutation(userId, 'request')
  }

  const handleWithdrawSubResellerImpersonation = async (userId, status, label = '', confirmed = false) => {
    setResellerImpersonationError('')
    if (String(status || '').toLowerCase() === IMPERSONATION_STATUS_APPROVED && !confirmed) {
      const displayLabel = label || uiLiteral('Reseller')
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.withdraw.impersonation_access', { label: displayLabel }),
        confirmLabel: 'Zurückziehen',
        destructive: true,
        onConfirm: () => handleWithdrawSubResellerImpersonation(userId, status, label, true)
      })
      return
    }
    await runSubResellerImpersonationMutation(userId, 'withdraw')
  }

  const handleImpersonateTenant = async (tenantId) => {
    setPlatformError('')
    if (!(await prepareImpersonationTransition())) return
    const res = await fetch(`${apiBase}/impersonations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tenant_id: tenantId })
    })
    if (!res.ok) {
      if (res.status === 401) {
        clearAuth()
        return
      }
      setPlatformError(await parseError(res))
      return
    }
    const data = await res.json()
    applyAuthSession(data)
  }

  const handleCreateTenantImpersonationPermission = async () => {
    setTenantImpersonationError('')
    if (!tenantImpersonationEmail.trim()) {
      setAdminFormFieldErrors('tenantImpersonation', { email: true })
      setTenantImpersonationError('E-Mail ist erforderlich.')
      return
    }
    setAdminFormFieldErrors('tenantImpersonation', {})
    const res = await fetch(`${apiBase}/tenant/impersonation-permissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ email: tenantImpersonationEmail.trim() })
    })
    if (!res.ok) {
      setTenantImpersonationError(await parseError(res))
      return
    }
    setTenantImpersonationEmail('')
    setShowTenantImpersonationCreate(false)
    await refreshTenantImpersonationPermissions()
  }

  const handleUpdateTenantImpersonationPermissionStatus = async (permissionId, status, label = '', confirmed = false) => {
    setTenantImpersonationError('')
    if (String(status || '').toLowerCase() === IMPERSONATION_STATUS_REVOKED && !confirmed) {
      const displayLabel = label || uiLiteral('Helfer')
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.revoke.impersonation_access', { label: displayLabel }),
        confirmLabel: 'Widerrufen',
        destructive: true,
        onConfirm: () => handleUpdateTenantImpersonationPermissionStatus(permissionId, status, label, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/tenant/impersonation-permissions/${permissionId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status })
    })
    if (!res.ok) {
      setTenantImpersonationError(await parseError(res))
      return
    }
    await refreshTenantImpersonationPermissions()
  }

  const handleImpersonateUser = async (userId) => {
    setPlatformError('')
    if (!(await prepareImpersonationTransition())) return
    const res = await fetch(`${apiBase}/platform/impersonations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ user_id: userId })
    })
    if (!res.ok) {
      if (res.status === 401) {
        clearAuth()
        return
      }
      setPlatformError(await parseError(res))
      return
    }
    const data = await res.json()
    applyAuthSession(data)
  }

  const handleImpersonateResellerUser = async (userId) => {
    setPlatformError('')
    if (!(await prepareImpersonationTransition())) return
    const res = await fetch(`${apiBase}/reseller/impersonations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ user_id: userId })
    })
    if (!res.ok) {
      if (res.status === 401) {
        clearAuth()
        return
      }
      setPlatformError(await parseError(res))
      return
    }
    const data = await res.json()
    applyAuthSession(data)
  }

  const handleTogglePlatformTenantAdminStatus = async (item, confirmed = false) => {
    setPlatformError('')
    const currentStatus = (item?.status || '').toLowerCase()
    const targetStatus = currentStatus === 'active' ? 'disabled' : 'active'
    if (targetStatus === 'disabled' && !confirmed) {
      const label = item?.organization_name || item?.name || item?.email || 'Organisation'
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.toggle.generic', { label, action: 'deaktivieren' }),
        confirmLabel: 'Deaktivieren',
        tone: 'warning',
        onConfirm: () => handleTogglePlatformTenantAdminStatus(item, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/platform/tenant-admins/${item.user_id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status: targetStatus })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshTenantAdmins()
  }

  const handleSaveTenantAdminEdit = async () => {
    setPlatformError('')
    if (!editTenantAdmin.userId || !editTenantAdmin.organizationName.trim() || !editTenantAdmin.name.trim()) return
    const orgName = editTenantAdmin.organizationName.trim()
    const contactName = editTenantAdmin.name.trim()
    const res = await fetch(`${apiBase}/platform/tenant-admins/${editTenantAdmin.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        organization_name: orgName,
        name: contactName
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setEditTenantAdmin({ userId: '', organizationName: '', name: '', email: '' })
    await refreshTenantAdmins()
  }

  const performStopImpersonation = async () => {
    if (!(await prepareImpersonationTransition())) return
    const res = await fetch(`${apiBase}/platform/impersonations/stop`, {
      method: 'POST',
      headers: authHeaders
    })
    if (!res.ok) {
      if (res.status === 401) {
        clearAuth()
        return
      }
      setPlatformError(await parseError(res))
      return
    }
    const data = await res.json()
    applyAuthSession(data)
  }

  const handleStopImpersonation = () => {
    requestSessionExitWithPendingUpload({
      title: 'Offenen Upload verwerfen und Impersonation beenden?',
      confirmLabel: 'Upload verwerfen und Impersonation beenden',
      sessionExitKind: 'stopImpersonation'
    })
  }

  sessionExitActionsRef.current = {
    logout: performLogout,
    stopImpersonation: performStopImpersonation
  }

  useEffect(() => {
    if (!pendingSessionExitKind || uploadBusy) return
    const action = sessionExitActionsRef.current[pendingSessionExitKind]
    setPendingSessionExitKind('')
    setConfirmationDialog({ open: false, title: '', message: '', confirmLabel: '', tone: 'default', onConfirm: null, sessionExitKind: '' })
    void action?.()
  }, [pendingSessionExitKind, uploadBusy])

  const handleCreateTenantUser = async () => {
    setTenantUserError('')
    setTenantUserSuccess('')
    const fieldErrors = { name: !tenantUserName.trim(), email: !tenantUserEmail.trim() }
    if (fieldErrors.name || fieldErrors.email) {
      setAdminFormFieldErrors('tenantUser', fieldErrors)
      setTenantUserError('Bitte alle Pflichtfelder ausfüllen.')
      return
    }
    setAdminFormFieldErrors('tenantUser', {})
    const res = await fetch(`${apiBase}/tenant/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        name: tenantUserName.trim(),
        email: tenantUserEmail.trim(),
        rights: tenantUserRights
      })
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    await res.json()
    setTenantUserName('')
    setTenantUserEmail('')
    setTenantUserRights(TENANT_USER_RIGHTS_DEFAULT)
    setShowTenantUserCreate(false)
    setTenantUserSuccess('Nutzer angelegt.')
    await refreshTenantUsers()
  }

  const handleLoginWithPasskey = async () => {
    setLoginError('')
    setLoginInfo('')
    if (!passkeySupported) {
      setLoginError('Passkeys werden von diesem Browser nicht unterstützt.')
      return
    }
    setPasskeyLoginBusy(true)
    try {
      const beginRes = await fetch(`${apiBase}/auth/passkeys/login/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      if (!beginRes.ok) {
        throw new Error(await parseError(beginRes))
      }
      const beginData = await beginRes.json()
      const publicKey = toCredentialRequestOptions(beginData.public_key)
      const credential = await navigator.credentials.get({
        publicKey
      })
      if (!credential) {
        throw new Error('Passkey-Anmeldung abgebrochen.')
      }
      const verifyRes = await fetch(`${apiBase}/auth/passkeys/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challenge_id: beginData.challenge_id,
          credential: serializePasskeyAuthenticationCredential(credential)
        })
      })
      if (!verifyRes.ok) {
        throw new Error(await parseError(verifyRes))
      }
      const data = await verifyRes.json()
      applyAuthSession({
        token: data.token,
        user: data.user
      })
    } catch (err) {
      const message = String(err?.message || '')
      if (message.toLowerCase().includes('passkey not available')) {
        setLoginError('Passkey-Backend nicht verfügbar (WebAuthn-Modul fehlt oder Server nicht neu gestartet).')
      } else {
        setLoginError(message || 'Passkey-Anmeldung fehlgeschlagen.')
      }
    } finally {
      setPasskeyLoginBusy(false)
    }
  }

  const handleCreatePasskey = async () => {
    setPasskeyError('')
    setPasskeySuccess('')
    setPasskeyNameInvalid(false)
    if (!passkeySupported) {
      setPasskeyError('Passkeys werden von diesem Browser nicht unterstützt.')
      return
    }
    if (!passkeyName.trim()) {
      setPasskeyNameInvalid(true)
      setPasskeyError('Bitte einen Namen für den Passkey angeben.')
      return
    }
    setPasskeyBusy(true)
    try {
      const beginRes = await fetch(`${apiBase}/auth/passkeys/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders }
      })
      if (!beginRes.ok) {
        throw new Error(await parseError(beginRes))
      }
      const beginData = await beginRes.json()
      const publicKey = toCredentialCreateOptions(beginData.public_key)
      const credential = await navigator.credentials.create({ publicKey })
      if (!credential) {
        throw new Error('Passkey-Registrierung abgebrochen.')
      }
      const verifyRes = await fetch(`${apiBase}/auth/passkeys/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          challenge_id: beginData.challenge_id,
          name: passkeyName.trim(),
          credential: serializePasskeyRegistrationCredential(credential)
        })
      })
      if (!verifyRes.ok) {
        throw new Error(await parseError(verifyRes))
      }
      setPasskeySuccess('Passkey hinzugefügt.')
      setPasskeyName(PASSKEY_DEFAULT_NAME)
      setShowPasskeyCreate(false)
      await refreshPasskeys()
    } catch (err) {
      const message = String(err?.message || '')
      if (message.toLowerCase().includes('passkey not available')) {
        setPasskeyError('Passkey-Backend nicht verfügbar (WebAuthn-Modul fehlt oder Server nicht neu gestartet).')
      } else {
        setPasskeyError(message || 'Passkey konnte nicht erstellt werden.')
      }
    } finally {
      setPasskeyBusy(false)
    }
  }

  const handleSavePasskeyName = async () => {
    setPasskeyError('')
    setPasskeySuccess('')
    if (!editPasskey.passkeyId || !editPasskey.name.trim()) return
    const res = await fetch(`${apiBase}/auth/passkeys/${editPasskey.passkeyId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name: editPasskey.name.trim() })
    })
    if (!res.ok) {
      setPasskeyError(await parseError(res))
      return
    }
    setEditPasskey({ passkeyId: '', name: '' })
    setPasskeySuccess('Passkey aktualisiert.')
    await refreshPasskeys()
  }

  const handleDeletePasskey = async (passkeyId, label, confirmed = false) => {
    setPasskeyError('')
    setPasskeySuccess('')
    if (!confirmed) {
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.delete.passkey', { label }),
        confirmLabel: 'Passkey löschen',
        destructive: true,
        onConfirm: () => handleDeletePasskey(passkeyId, label, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/auth/passkeys/${passkeyId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) {
      setPasskeyError(await parseError(res))
      return
    }
    setPasskeySuccess('Passkey gelöscht.')
    await refreshPasskeys()
  }

  const handleSaveTenantUserEdit = async () => {
    setTenantUserError('')
    setTenantUserSuccess('')
    if (!editTenantUser.userId || !editTenantUser.name.trim()) return
    const res = await fetch(`${apiBase}/tenant/users/${editTenantUser.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        name: editTenantUser.name.trim()
      })
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    setEditTenantUser({ userId: '', name: '', email: '', rights: TENANT_USER_RIGHTS_DEFAULT })
    setTenantUserSuccess('Nutzer aktualisiert.')
    await refreshTenantUsers()
  }

  const handleDeleteTenantUser = async (userId, label, confirmed = false) => {
    setTenantUserError('')
    setTenantUserSuccess('')
    if (!confirmed) {
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.delete.user', { label }),
        confirmLabel: 'Nutzer löschen',
        destructive: true,
        onConfirm: () => handleDeleteTenantUser(userId, label, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/tenant/users/${userId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    setTenantUserSuccess('Nutzer gelöscht.')
    await refreshTenantUsers()
  }

  const openTenantUserRightsDialog = (mode, payload = {}) => {
    const initialRights = normalizeTenantUserRights(payload.rights || (mode === 'create' ? tenantUserRights : TENANT_USER_RIGHTS_DEFAULT))
    setRightsDraft(initialRights)
    setRightsDialog({
      open: true,
      mode,
      userId: payload.userId || '',
      label: payload.label || ''
    })
  }

  const closeTenantUserRightsDialog = () => {
    setRightsDialog({ open: false, mode: 'create', userId: '', label: '' })
    setRightsDraft(TENANT_USER_RIGHTS_DEFAULT)
  }

  const toggleTenantUserRight = (key) => {
    setRightsDraft((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const saveTenantUserRightsDialog = async () => {
    if (rightsDialog.mode === 'create') {
      setTenantUserRights(normalizeTenantUserRights(rightsDraft))
      setTenantUserSuccess('Rechte für Neuanlage übernommen.')
      closeTenantUserRightsDialog()
      return
    }

    if (!rightsDialog.userId) return
    setTenantUserError('')
    setTenantUserSuccess('')
    const normalizedDraft = normalizeTenantUserRights(rightsDraft)
    const res = await fetch(`${apiBase}/tenant/users/${rightsDialog.userId}/rights`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ rights: normalizedDraft })
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    let savedRights = normalizedDraft
    try {
      const data = await res.json()
      savedRights = normalizeTenantUserRights(data?.rights || normalizedDraft)
    } catch {
      // ignore parse fallback
    }
    const targetUserId = String(rightsDialog.userId || '')
    setTenantUsers((prev) => prev.map((item) => (
      String(item.user_id || '') === targetUserId ? { ...item, rights: savedRights } : item
    )))
    onUpdateAuthUser((prev) => {
      if (!prev) return prev
      if (String(prev.user_id || '') !== targetUserId) return prev
      return { ...prev, rights: savedRights }
    })
    setTenantUserSuccess('Rechte gespeichert.')
    closeTenantUserRightsDialog()
    await refreshTenantUsers()
  }

  const handleToggleTenantUserLock = async (userId, label, isLocked, confirmed = false) => {
    setTenantUserError('')
    setTenantUserSuccess('')
    const actionLabel = isLocked ? 'entsperren' : 'sperren'
    if (!confirmed) {
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.toggle.user', { label, action: uiLiteral(actionLabel) }),
        confirmLabel: isLocked ? 'Entsperren' : 'Sperren',
        tone: isLocked ? 'default' : 'warning',
        onConfirm: () => handleToggleTenantUserLock(userId, label, isLocked, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/tenant/users/${userId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status: isLocked ? 'active' : 'locked' })
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    setTenantUserSuccess(isLocked ? 'Nutzer wurde entsperrt.' : 'Nutzer wurde gesperrt.')
    await refreshTenantUsers()
  }

  const handleChangePassword = async () => {
    setPasswordError('')
    setPasswordSuccess('')
    setPasswordFieldErrors({ newPassword: false, repeatPassword: false })
    if (!passwordNew || !passwordRepeat) {
      const nextFieldErrors = { newPassword: !passwordNew, repeatPassword: !passwordRepeat }
      setPasswordFieldErrors(nextFieldErrors)
      setPasswordError(nextFieldErrors.newPassword && nextFieldErrors.repeatPassword
        ? 'Bitte alle Passwortfelder ausfüllen.'
        : nextFieldErrors.newPassword
          ? 'Bitte neues Passwort eingeben.'
          : 'Bitte Passwort wiederholen.')
      return
    }
    if (passwordNew !== passwordRepeat) {
      setPasswordFieldErrors({ newPassword: false, repeatPassword: true })
      setPasswordError('Neues Passwort und Wiederholung stimmen nicht überein.')
      return
    }
    if (passwordNew.length < 8) {
      setPasswordFieldErrors({ newPassword: true, repeatPassword: false })
      setPasswordError('Neues Passwort muss mindestens 8 Zeichen haben.')
      return
    }
    setPasswordBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          new_password: passwordNew
        })
      })
      if (!res.ok) {
        setPasswordError(await parseError(res))
        return
      }
      setPasswordNew('')
      setPasswordRepeat('')
      setPasswordSuccess('Passwort wurde geändert.')
    } finally {
      setPasswordBusy(false)
    }
  }

  const handleSaveUserSettings = async () => {
    const nextLang = preferredLanguageDraft === 'de' ? 'de' : 'en'
    setSettingsError('')
    setSettingsSuccess('')
    setSettingsBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ preferred_lang: nextLang })
      })
      if (!res.ok) throw new Error(await parseError(res))
      const data = await res.json()
      const savedLang = String(data?.preferred_lang || nextLang).toLowerCase() === 'de' ? 'de' : 'en'
      onUpdateAuthUser((prev) => (prev ? { ...prev, preferred_lang: savedLang } : prev))
      setUiLang(savedLang)
      setPreferredLanguageDraft(savedLang)
      setSettingsSuccess('Einstellungen gespeichert.')
      if (typeof window !== 'undefined') {
        window.setTimeout(() => {
          window.location.reload()
        }, 120)
      }
    } catch (err) {
      setSettingsError(err?.message || 'Einstellungen konnten nicht gespeichert werden.')
    } finally {
      setSettingsBusy(false)
    }
  }

  useEffect(() => {
    if (!authToken || !authUser) return
    let cancelled = false
    const fetchProfiles = async () => {
      try {
        const res = await fetch(`${apiBase}/profiles`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setProfiles(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchApiKeys = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/api-keys`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setApiKeys(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchExports = async () => {
      try {
        const res = await fetch(`${apiBase}/exports`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setExports(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchCompanies = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/companies?tenant_id=${tenantId}`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setCompanies(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchSites = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/sites?tenant_id=${tenantId}`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setSites(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchPos = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/pos?tenant_id=${tenantId}`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setPosList(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchTenantUsers = async () => {
      if (!canManageUsers) {
        if (!cancelled) setTenantUsers([])
        return
      }
      try {
        const res = await fetch(`${apiBase}/tenant/users?ts=${Date.now()}`, {
          headers: authHeaders,
          cache: 'no-store'
        })
        if (!res.ok) return
        const data = await res.json()
        const normalized = Array.isArray(data)
          ? data.map((item) => ({ ...item, rights: normalizeTenantUserRights(item?.rights) }))
          : []
        if (!cancelled) setTenantUsers(normalized)
      } catch {
        // ignore for demo
      }
    }

    const fetchPasskeys = async () => {
      try {
        const res = await fetch(`${apiBase}/auth/passkeys`, { headers: authHeaders })
        if (!res.ok) {
          if (!cancelled) setPasskeys([])
          return
        }
        const data = await res.json()
        if (!cancelled) setPasskeys(Array.isArray(data) ? data : [])
      } catch {
        if (!cancelled) setPasskeys([])
      }
    }

    fetchProfiles()
    fetchApiKeys()
    fetchExports()
    fetchCompanies()
    fetchSites()
    fetchPos()
    fetchTenantUsers()
    fetchPasskeys()
    if (authUser?.role === 'platform_admin') {
      refreshTenantAdmins()
      refreshResellerAdmins()
    } else if (authUser?.role === 'reseller_admin') {
      refreshResellerAdmins()
      refreshResellerTenantAdmins()
      refreshResellerImpersonationPermissions()
    } else if (authUser?.role === 'tenant_admin') {
      refreshTenantImpersonationPermissions()
    }
    return () => {
      cancelled = true
    }
  }, [apiBase, tenantId, authHeaders, authToken, authUser, canManageUsers])

  useEffect(() => {
    if (!authToken || !authUser) return
    if (!exportIncludeDeleted) {
      setCompaniesWithDeleted([])
      return
    }
    let cancelled = false
    const fetchCompaniesWithDeleted = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/companies?tenant_id=${tenantId}&include_deleted=1`, { headers: authHeaders })
        if (!res.ok || cancelled) return
        const data = await res.json()
        if (!cancelled) {
          setCompaniesWithDeleted(Array.isArray(data) ? data : [])
        }
      } catch {
        if (!cancelled) setCompaniesWithDeleted([])
      }
    }
    fetchCompaniesWithDeleted()
    return () => { cancelled = true }
  }, [apiBase, authHeaders, authToken, authUser, exportIncludeDeleted, tenantId])

  useEffect(() => {
    if (!authToken || !authUser) return
    if (!exportIncludeDeleted) {
      setSitesWithDeleted([])
      return
    }
    let cancelled = false
    const fetchSitesWithDeleted = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/sites?tenant_id=${tenantId}&include_deleted=1`, { headers: authHeaders })
        if (!res.ok || cancelled) return
        const data = await res.json()
        if (!cancelled) setSitesWithDeleted(Array.isArray(data) ? data : [])
      } catch {
        if (!cancelled) setSitesWithDeleted([])
      }
    }
    fetchSitesWithDeleted()
    return () => { cancelled = true }
  }, [apiBase, authHeaders, authToken, authUser, exportIncludeDeleted, tenantId])

  useEffect(() => {
    if (!authToken || !authUser) return
    if (!exportIncludeDeleted) {
      setPosListWithDeleted([])
      return
    }
    let cancelled = false
    const fetchPosWithDeleted = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/pos?tenant_id=${tenantId}&include_deleted=1`, { headers: authHeaders })
        if (!res.ok || cancelled) return
        const data = await res.json()
        if (!cancelled) {
          setPosListWithDeleted(Array.isArray(data) ? data : [])
        }
      } catch {
        if (!cancelled) setPosListWithDeleted([])
      }
    }
    fetchPosWithDeleted()
    return () => { cancelled = true }
  }, [apiBase, authHeaders, authToken, authUser, exportIncludeDeleted, tenantId])

  useEffect(() => {
    if (!navItems.length) return
    if (navItems.some((item) => item.label === active)) return
    setActive(navItems[0].label)
  }, [navItems, active])

  useEffect(() => {
    if (!authToken || !authUser) return
    if (!hashTarget) return
    if (!navItems.some((item) => item.label === hashTarget)) return
    setActive(hashTarget)
  }, [authToken, authUser, hashTarget, navItems])

  const activeProfilesByCountry = useMemo(() => {
    const map = {}
    for (const p of profiles) {
      if (p?.status !== 'active' || !p?.country) continue
      if (!map[p.country]) map[p.country] = p
    }
    return map
  }, [profiles])

  const selectableCountries = useMemo(
    () => Object.keys(activeProfilesByCountry).sort(),
    [activeProfilesByCountry]
  )

  const uploadCompany = useMemo(
    () => companies.find((c) => c.company_id === uploadCompanyId) || null,
    [companies, uploadCompanyId]
  )

  const uploadCompanySelectOptions = useMemo(() => {
    const query = uploadCompanySearchTerm.trim().toLowerCase()
    if (!query) return companies
    return companies.filter((company) =>
      [company.name || '', company.country || '']
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [companies, uploadCompanySearchTerm])

  const uploadCompanySelectionVisible = useMemo(
    () => uploadCompanySelectOptions.some((company) => company.company_id === uploadCompanyId),
    [uploadCompanyId, uploadCompanySelectOptions]
  )

  const uploadResolvedProfile = useMemo(() => {
    if (!uploadCompany?.country) return null
    return activeProfilesByCountry[uploadCompany.country] || null
  }, [activeProfilesByCountry, uploadCompany])

  const uploadSiteOptions = useMemo(
    () => sitesForCompany(sites, uploadCompanyId),
    [sites, uploadCompanyId]
  )

  const uploadPosOptions = useMemo(
    () => uploadSiteId
      ? posForSite(posList, uploadSiteId)
      : posForCompany(posList, sites, uploadCompanyId),
    [posList, sites, uploadCompanyId, uploadSiteId]
  )

  const uploadSite = useMemo(
    () => uploadSiteOptions.find((site) => site.site_id === uploadSiteId) || null,
    [uploadSiteOptions, uploadSiteId]
  )

  const uploadSelectedPos = useMemo(
    () => uploadPosOptions.find((pos) => pos.pos_id === uploadPosRefs[0]) || null,
    [uploadPosOptions, uploadPosRefs]
  )

  const uploadEffectiveSite = useMemo(
    () => uploadSite || sites.find((site) => site.site_id === uploadSelectedPos?.site_id) || null,
    [sites, uploadSelectedPos, uploadSite]
  )

  const uploadContextReady = useMemo(() => {
    return Boolean(uploadCompanyId && uploadResolvedProfile)
  }, [uploadCompanyId, uploadResolvedProfile])
  const uploadDateLocked = uploadBundledFiles.length > 0
  const uploadDialogBlocked = uploadBusy || uploadTransferProgress.active
  const uploadContextFieldState = !uploadContextReady || uploadDialogBlocked
    ? 'unavailable'
    : (uploadDateLocked ? 'readonly' : 'editable')
  const uploadCompanyFieldState = uploadDialogBlocked
    ? 'unavailable'
    : (uploadDateLocked ? 'readonly' : 'editable')
  const uploadTransferPercent = Number.isFinite(uploadTransferProgress.percent) ? uploadTransferProgress.percent : null
  const uploadTransferActive = uploadTransferProgress.active
  const uploadTransferStatusText = uploadTransferPercent == null
    ? 'Upload läuft. Der Dialog ist gesperrt, bis die Datei vollständig übertragen wurde.'
    : formatUiTemplate('upload.transfer.locked.progress', { percent: uploadTransferPercent })
  const uploadCompanyMissingError = (!uploadCompanyId && !uploadContextReady) || uploadError === 'Bitte ein Unternehmen wählen.'
  const uploadRangeMissingError = uploadError === TIME_RANGE_REQUIRED_ERROR
  const exportRangeMissingError = exportError === TIME_RANGE_REQUIRED_ERROR

  const tenantDisplayName = useMemo(() => {
    return authUser?.tenant_name || companies[0]?.name || authUser?.tenant_id || '-'
  }, [authUser, companies])

  useEffect(() => {
    setTenantNameEdit(tenantDisplayName === '-' ? '' : tenantDisplayName)
  }, [tenantDisplayName])

  const siteNameById = useMemo(
    () => Object.fromEntries(sites.map((s) => [s.site_id, s.name])),
    [sites]
  )

  const companyNameById = useMemo(
    () => Object.fromEntries(companies.map((c) => [c.company_id, c.name])),
    [companies]
  )

  const companyCountryById = useMemo(
    () => {
      const pairs = companies.map((c) => [c.company_id, c.country || ''])
      if (exportIncludeDeleted) {
        for (const c of companiesWithDeleted) {
          pairs.push([c.company_id, c.country || ''])
        }
      }
      return Object.fromEntries(pairs)
    },
    [companies, companiesWithDeleted, exportIncludeDeleted]
  )

  const posNameById = useMemo(() => {
    const siteById = new Map(sites.map((site) => [site.site_id, site.name]))
    const formatExportPosName = (pos) => {
      const posLabel = pos.pos_label || pos.pos_serial
      const siteName = pos.site_name || siteById.get(pos.site_id)
      const baseName = siteName ? `${posLabel} (${siteName})` : posLabel
      return pos?.is_deleted
        ? formatUiTemplate('entity.deleted_at', {
          name: baseName,
          date: formatDateDisplay(pos.deleted_at)
        })
        : baseName
    }
    const entries = posList.map((pos) => [pos.pos_id, formatExportPosName(pos)])
    if (exportIncludeDeleted) {
      for (const p of posListWithDeleted) {
        entries.push([p.pos_id, formatExportPosName(p)])
      }
    }
    return Object.fromEntries(entries)
  }, [posList, posListWithDeleted, sites, exportIncludeDeleted])

  const resolveExportCountry = (item) => {
    const ids = Array.isArray(item?.company_ids) ? item.company_ids : []
    if (!ids.length) return ''
    const countries = new Set(ids.map((id) => companyCountryById[id]).filter(Boolean))
    if (countries.size !== 1) return ''
    return Array.from(countries)[0]
  }

  const filteredApiKeys = useMemo(() => {
    const query = apiKeysSearchTerm.trim().toLowerCase()
    if (!query) return apiKeys
    return apiKeys.filter((item) => {
      const haystack = [
        item.pos_serial || '',
        item.company_name || '',
        item.site_name || '',
        item.reported_company_name || '',
        item.reported_site_name || '',
        item.status || '',
        item.config_status || ''
      ].join(' ').toLowerCase()
      return haystack.includes(query)
    })
  }, [apiKeys, apiKeysSearchTerm])

  const pendingApiKeys = useMemo(
    () => filteredApiKeys.filter((item) => String(item.status || '').toLowerCase() === 'requested'),
    [filteredApiKeys]
  )

  const selectedPendingApiKeyIds = useMemo(
    () => new Set(selectedApiKeyIds),
    [selectedApiKeyIds]
  )

  const filteredOrganizationCompanies = useMemo(() => {
    const query = organizationSearchQuery
    if (!query) {
      return companies.map((company) => ({
        ...company,
        sites: sites
          .filter((site) => site.company_id === company.company_id)
          .map((site) => ({
            ...site,
            posItems: posList.filter((pos) => pos.site_id === site.site_id)
          }))
      }))
    }

    return companies.reduce((acc, company) => {
      const companySites = sites
        .filter((site) => site.company_id === company.company_id)
        .map((site) => ({
          ...site,
          posItems: posList.filter((pos) => pos.site_id === site.site_id)
        }))

      const companyMatches = [company.name || '', company.country || '']
        .join(' ')
        .toLowerCase()
        .includes(query)

      const siteSearchResults = companySites.map((site) => {
        const siteMatches = [site.name || '']
          .join(' ')
          .toLowerCase()
          .includes(query)
        const filteredPos = site.posItems.filter((pos) =>
          [pos.pos_id || '', pos.pos_serial || '', pos.pos_label || '']
            .join(' ')
            .toLowerCase()
            .includes(query)
        )
        return {
          site: {
            ...site,
            hasSearchMatchInDescendants: filteredPos.length > 0
          },
          siteMatches,
          filteredPos
        }
      })
      const filteredSites = siteSearchResults.reduce((siteAcc, { site, siteMatches, filteredPos }) => {
        if (siteMatches) siteAcc.push(site)
        else if (filteredPos.length) siteAcc.push({ ...site, posItems: filteredPos })
        return siteAcc
      }, [])

      if (companyMatches) {
        acc.push({
          ...company,
          sites: siteSearchResults.map(({ site }) => site),
          hasSearchMatchInDescendants: filteredSites.length > 0
        })
      } else if (filteredSites.length) {
        acc.push({
          ...company,
          sites: filteredSites,
          hasSearchMatchInDescendants: true
        })
      }
      return acc
    }, [])
  }, [companies, sites, posList, organizationSearchQuery])

  const isOrganizationSearchActive = organizationSearchQuery.length > 0

  const filteredTenantUsers = useMemo(() => {
    const query = tenantUsersSearchTerm.trim().toLowerCase()
    if (!query) return tenantUsers
    return tenantUsers.filter((item) =>
      [
        item.name || '',
        item.email || '',
        item.status === 'locked' ? 'gesperrt locked' : (item.status || '')
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [tenantUsers, tenantUsersSearchTerm])

  useEffect(() => {
    if (!expandedTenantUserId) return
    const stillVisible = filteredTenantUsers.some((item) => item.user_id === expandedTenantUserId)
    if (!stillVisible) setExpandedTenantUserId('')
  }, [filteredTenantUsers, expandedTenantUserId])

  const filteredTenantImpersonationPermissions = useMemo(() => {
    const query = tenantImpersonationSearchTerm.trim().toLowerCase()
    if (!query) return tenantImpersonationPermissions
    return tenantImpersonationPermissions.filter((item) =>
      [
        item.helper_name || '',
        item.helper_email || ''
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [tenantImpersonationPermissions, tenantImpersonationSearchTerm])

  useEffect(() => {
    if (!expandedTenantImpersonationPermissionId) return
    const stillVisible = filteredTenantImpersonationPermissions.some(
      (item) => item.permission_id === expandedTenantImpersonationPermissionId
    )
    if (!stillVisible) setExpandedTenantImpersonationPermissionId('')
  }, [filteredTenantImpersonationPermissions, expandedTenantImpersonationPermissionId])

  const filteredResellerAdmins = useMemo(() => {
    const query = resellerSearchTerm.trim().toLowerCase()
    if (!query) return resellerAdmins
    return resellerAdmins.filter((item) =>
      [
        item.name || '',
        item.email || '',
        item.status || ''
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [resellerAdmins, resellerSearchTerm])

  const filteredResellerImpersonationPermissions = useMemo(() => {
    const query = resellerImpersonationSearchTerm.trim().toLowerCase()
    if (!query) return resellerImpersonationPermissions
    const statusText = (statusRaw) => {
      const status = String(statusRaw || '').toLowerCase()
      if (status === IMPERSONATION_STATUS_APPROVED) return 'Freigegeben'
      if (status === IMPERSONATION_STATUS_REQUESTED) return 'Anfrage offen'
      return 'Nicht freigegeben'
    }
    return resellerImpersonationPermissions.filter((item) =>
      [
        item.requester_name || '',
        item.requester_email || '',
        statusText(item.status)
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [resellerImpersonationPermissions, resellerImpersonationSearchTerm])

  useEffect(() => {
    if (!expandedPlatformResellerId) return
    const stillVisible = filteredResellerAdmins.some((item) => item.user_id === expandedPlatformResellerId)
    if (!stillVisible) setExpandedPlatformResellerId('')
  }, [filteredResellerAdmins, expandedPlatformResellerId])

  const resellerNameById = useMemo(
    () => Object.fromEntries(resellerAdmins.map((item) => [item.user_id, item.name || item.email || item.user_id])),
    [resellerAdmins]
  )

  const filteredPlatformTenantAdmins = useMemo(() => {
    const query = platformOrgSearchTerm.trim().toLowerCase()
    if (!query) return tenantAdmins
    return tenantAdmins.filter((item) =>
      [
        item.organization_name || '',
        item.name || '',
        item.email || '',
        item.reseller_admin_id ? (resellerNameById[item.reseller_admin_id] || item.reseller_admin_id) : 'Direkt'
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [tenantAdmins, platformOrgSearchTerm, resellerNameById])

  const resellerById = useMemo(
    () => Object.fromEntries(resellerAdmins.map((item) => [item.user_id, item])),
    [resellerAdmins]
  )

  const resellerChildrenByParent = useMemo(
    () => resellerAdmins.reduce((acc, reseller) => {
      const key = reseller.created_by_reseller_admin_id || '__root__'
      const list = acc[key] || []
      list.push(reseller)
      acc[key] = list
      return acc
    }, {}),
    [resellerAdmins]
  )

  const platformOrganizationRows = useMemo(() => {
    if (authUser?.role !== 'platform_admin') return []
    return buildPlatformOrganizationRows({
      platformUserId: authUser.user_id,
      resellerAdmins,
      tenantAdmins
    })
  }, [authUser?.role, authUser?.user_id, resellerAdmins, tenantAdmins])

  const resellerOrganizationRows = useMemo(() => {
    if (authUser?.role !== 'reseller_admin') return []
    const tenantsByReseller = resellerTenantAdmins.reduce((acc, item) => {
      const key = item.reseller_admin_id || ''
      const list = acc[key] || []
      list.push(item)
      acc[key] = list
      return acc
    }, {})
    const rows = []
    const walk = (resellerId, level, parentResellerId = '') => {
      const reseller = resellerById[resellerId]
      if (!reseller) return
      const tenants = tenantsByReseller[resellerId] || []
      const children = resellerChildrenByParent[resellerId] || []
      rows.push({ type: 'reseller', level, parentResellerId, item: reseller, hasChildren: Boolean(children.length || tenants.length) })
      for (const tenant of tenants) rows.push({ type: 'tenant', level: level + 1, parentResellerId: resellerId, item: tenant })
      for (const child of children) walk(child.user_id, level + 1, resellerId)
    }
    if (authUser?.user_id && resellerById[authUser.user_id]) {
      const selfId = authUser.user_id
      const ownTenants = tenantsByReseller[selfId] || []
      const childResellers = resellerChildrenByParent[selfId] || []
      for (const tenant of ownTenants) rows.push({ type: 'tenant', level: 0, parentResellerId: '', item: tenant })
      for (const child of childResellers) walk(child.user_id, 0, '')
    } else {
      const roots = resellerChildrenByParent.__root__ || []
      for (const root of roots) walk(root.user_id, 0, '')
    }
    return rows
  }, [authUser?.role, authUser?.user_id, resellerById, resellerChildrenByParent, resellerTenantAdmins])

  const filteredPlatformOrganizationRows = useMemo(() => {
    const query = platformOrgSearchTerm.trim().toLowerCase()
    if (!query) return platformOrganizationRows
    const toAdminStatusLabel = (statusRaw) => {
      const status = String(statusRaw || '').toLowerCase()
      if (status === 'active') return 'Aktiv'
      if (status === 'locked' || status === 'inactive' || status === 'disabled') return 'Deaktiviert'
      return '-'
    }
    const rowKey = (row) => `${row.type}:${row.item.user_id}`
    const parentByResellerId = {}
    for (const row of platformOrganizationRows) {
      if (row.type !== 'reseller') continue
      const resellerId = row.item.user_id
      const parentId = row.parentResellerId || ''
      parentByResellerId[resellerId] = parentId
    }
    const matches = platformOrganizationRows.filter((row) => {
      if (row.type === 'reseller') {
        const item = row.item
        return [item.name || '', item.contact_name || '', item.email || '', toAdminStatusLabel(item.status)]
          .join(' ')
          .toLowerCase()
          .includes(query)
      }
      const item = row.item
      return [item.organization_name || '', item.name || '', item.email || '', 'Organisation']
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
    if (!matches.length) return []
    const include = new Set(matches.map((row) => rowKey(row)))
    for (const row of matches) {
      let parentId = row.parentResellerId || ''
      while (parentId) {
        include.add(`reseller:${parentId}`)
        parentId = parentByResellerId[parentId] || ''
      }
    }
    return platformOrganizationRows.filter((row) => include.has(rowKey(row)))
  }, [platformOrgSearchTerm, platformOrganizationRows])

  const filteredResellerOrganizationRows = useMemo(() => {
    const query = resellerSearchTerm.trim().toLowerCase()
    if (!query) return resellerOrganizationRows
    const rowKey = (row) => `${row.type}:${row.item.user_id}`
    const parentByResellerId = {}
    for (const row of resellerOrganizationRows) {
      if (row.type !== 'reseller') continue
      const resellerId = row.item.user_id
      const parentId = row.parentResellerId || ''
      parentByResellerId[resellerId] = parentId
    }
    const matches = resellerOrganizationRows.filter((row) => {
      if (row.type === 'reseller') {
        const item = row.item
        return [item.name || '', item.contact_name || '', item.email || '', 'Reseller']
          .join(' ')
          .toLowerCase()
          .includes(query)
      }
      const item = row.item
      return [item.organization_name || '', item.name || '', item.email || '']
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
    if (!matches.length) return []
    const include = new Set(matches.map((row) => rowKey(row)))
    for (const row of matches) {
      let parentId = row.parentResellerId || ''
      while (parentId) {
        include.add(`reseller:${parentId}`)
        parentId = parentByResellerId[parentId] || ''
      }
    }
    return resellerOrganizationRows.filter((row) => include.has(rowKey(row)))
  }, [resellerOrganizationRows, resellerSearchTerm])

  const handleToggleCompanyCreate = () => {
    setCompanyCreateError('')
    setCompanyNameInvalid(false)
    setShowCompanyCreate((prev) => !prev)
  }

  const handleNavigateToCompanyStructure = () => {
    setActive('Unternehmensstruktur')
    setOrganizationSearchTerm('')
    refreshOrgTree()
  }

  useEffect(() => {
    const keys = platformOrganizationRows
      .filter((row) => row.type === 'reseller' && row.hasChildren)
      .map((row) => row.item.user_id)
    if (!keys.length) {
      setPlatformOrgExpandedResellerIds({})
      return
    }
    setPlatformOrgExpandedResellerIds((prev) => {
      const next = {}
      let changed = false
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(prev, key)) next[key] = prev[key]
        else {
          next[key] = false
          changed = true
        }
      }
      for (const key of Object.keys(prev)) {
        if (!keys.includes(key)) changed = true
      }
      return changed ? next : prev
    })
  }, [platformOrganizationRows])

  useEffect(() => {
    const keys = resellerOrganizationRows
      .filter((row) => row.type === 'reseller' && row.hasChildren)
      .map((row) => row.item.user_id)
    if (!keys.length) {
      setResellerOrgExpandedResellerIds({})
      return
    }
    setResellerOrgExpandedResellerIds((prev) => {
      const next = {}
      let changed = false
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(prev, key)) next[key] = prev[key]
        else {
          next[key] = false
          changed = true
        }
      }
      for (const key of Object.keys(prev)) {
        if (!keys.includes(key)) changed = true
      }
      return changed ? next : prev
    })
  }, [resellerOrganizationRows])

  const visiblePlatformOrganizationRows = useMemo(() => {
    if (platformOrgSearchTerm.trim()) return filteredPlatformOrganizationRows
    const hiddenParents = new Set()
    const visible = []
    for (const row of filteredPlatformOrganizationRows) {
      const parentId = row.parentResellerId || ''
      if (parentId && hiddenParents.has(parentId)) {
        if (row.type === 'reseller') hiddenParents.add(row.item.user_id)
        continue
      }
      if (row.type === 'reseller') {
        visible.push(row)
        if (!platformOrgExpandedResellerIds[row.item.user_id]) hiddenParents.add(row.item.user_id)
        continue
      }
      visible.push(row)
    }
    return visible
  }, [filteredPlatformOrganizationRows, platformOrgExpandedResellerIds, platformOrgSearchTerm])

  const visibleResellerOrganizationRows = useMemo(() => {
    if (resellerSearchTerm.trim()) return filteredResellerOrganizationRows
    const hiddenParents = new Set()
    const visible = []
    for (const row of filteredResellerOrganizationRows) {
      const parentId = row.parentResellerId || ''
      if (parentId && hiddenParents.has(parentId)) {
        if (row.type === 'reseller') hiddenParents.add(row.item.user_id)
        continue
      }
      if (row.type === 'reseller') {
        visible.push(row)
        if (!resellerOrgExpandedResellerIds[row.item.user_id]) hiddenParents.add(row.item.user_id)
        continue
      }
      visible.push(row)
    }
    return visible
  }, [filteredResellerOrganizationRows, resellerOrgExpandedResellerIds, resellerSearchTerm])

  useEffect(() => {
    if (!expandedPlatformOrgId) return
    const stillVisible = filteredPlatformTenantAdmins.some((item) => item.user_id === expandedPlatformOrgId)
    if (!stillVisible) setExpandedPlatformOrgId('')
  }, [filteredPlatformTenantAdmins, expandedPlatformOrgId])

  useEffect(() => {
    if (!expandedPlatformMobileRowId) return
    const stillVisible = visiblePlatformOrganizationRows.some((row) => `${row.type}:${row.item.user_id}` === expandedPlatformMobileRowId)
    if (!stillVisible) setExpandedPlatformMobileRowId('')
  }, [expandedPlatformMobileRowId, visiblePlatformOrganizationRows])

  useEffect(() => {
    if (!expandedResellerTenantId) return
    const stillVisible = resellerTenantAdmins.some((item) => item.user_id === expandedResellerTenantId)
    if (!stillVisible) setExpandedResellerTenantId('')
  }, [resellerTenantAdmins, expandedResellerTenantId])

  useEffect(() => {
    if (!expandedResellerMobileRowId) return
    const stillVisible = visibleResellerOrganizationRows.some((row) => `${row.type}:${row.item.user_id}` === expandedResellerMobileRowId)
    if (!stillVisible) setExpandedResellerMobileRowId('')
  }, [expandedResellerMobileRowId, visibleResellerOrganizationRows])

  const getImpersonationStatusMeta = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === IMPERSONATION_STATUS_APPROVED) return { key: IMPERSONATION_STATUS_APPROVED, tone: 'success', label: 'OK', title: 'Freigegeben' }
    if (status === IMPERSONATION_STATUS_REQUESTED) return { key: IMPERSONATION_STATUS_REQUESTED, tone: 'pending', label: 'Pending', title: 'Anfrage offen' }
    return { key: IMPERSONATION_STATUS_REVOKED, tone: 'neutral', label: 'Stop', title: 'Nicht freigegeben' }
  }

  const getTenantUserStatusMeta = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === 'initial') return { key: 'initial', tone: 'pending', label: 'Ausstehend' }
    if (status === 'locked') return { key: 'locked', tone: 'neutral', label: 'Gesperrt' }
    return { key: 'active', tone: 'success', label: 'Aktiv' }
  }

  const getAdminStatusMeta = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === 'active') return { key: 'active', tone: 'success', label: 'Aktiv' }
    if (status === 'initial') return { key: 'initial', tone: 'pending', label: 'Ausstehend' }
    if (status === 'locked' || status === 'inactive' || status === 'disabled') return { key: 'disabled', tone: 'neutral', label: 'Deaktiviert' }
    return { key: 'unknown', tone: 'neutral', label: '-' }
  }

  const isImpersonationRequestable = (status) => (
    status === IMPERSONATION_STATUS_NONE || status === IMPERSONATION_STATUS_REVOKED
  )

  const isImpersonationWithdrawable = (status) => (
    status === IMPERSONATION_STATUS_REQUESTED || status === IMPERSONATION_STATUS_APPROVED
  )

  const activeExport = useMemo(() => {
    const inProgress = exports.find((item) => ['queued', 'running', 'requested', 'pending'].includes((item.status || '').toLowerCase()))
    return inProgress || null
  }, [exports])
  const exportStatusLabel = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === 'expired') return 'Abgelaufen'
    if (status === 'completed') return 'OK'
    return String(statusRaw || '-')
  }

  const exportStatusTone = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === 'completed') return 'success'
    if (status === 'expired') return 'neutral'
    return 'info'
  }

  const apiKeyStatusTone = (item) => {
    const status = String(item?.status || '').toLowerCase()
    const configStatus = String(item?.config_status || '').toLowerCase()
    if (status === 'approved' && configStatus !== 'updated') return 'success'
    if (status === 'requested' || configStatus === 'updated') return 'pending'
    if (status === 'deactivated') return 'neutral'
    return 'info'
  }

  const latestExports = useMemo(() => exports.slice(0, 10), [exports])

  const exportTooltipText = (item) => {
    const companyLabels = (item.company_ids || []).map((id) => companyNameById[id] || id)
    const siteLabels = (item.site_ids || []).map((id) => siteNameById[id] || id)
    const posLabels = (item.pos_ids || []).map((id) => posNameById[id] || id)
    const exportCountry = resolveExportCountry(item)
    const period = item.business_time_from && item.business_time_to
      ? formatUiTemplate('range.from_to', {
        from: formatDateDisplay(item.business_time_from),
        to: formatDateDisplay(item.business_time_to)
      })
      : '-'
    const artifactLabel = Array.isArray(item.artifact_types) && item.artifact_types.length
      ? item.artifact_types.map((type) => getArtifactTypeLabel(type, exportCountry)).join(', ')
      : getArtifactTypeLabel(item.artifact_type, exportCountry)
    return [
      formatUiTemplate('export.tooltip.artifact_group', { value: artifactLabel }),
      formatUiTemplate('export.tooltip.company', { value: companyLabels.length ? companyLabels.join(', ') : '-' }),
      formatUiTemplate('export.tooltip.site', { value: siteLabels.length ? siteLabels.join(', ') : '-' }),
      formatUiTemplate('export.tooltip.pos', { value: posLabels.length ? posLabels.join(', ') : '-' }),
      formatUiTemplate('export.tooltip.period', { value: period })
    ].join('\n')
  }

  const exportCompanyOptions = useMemo(() => {
    const active = companies.map((c) => ({
      company_id: c.company_id,
      name: c.name,
      country: c.country,
      is_deleted: false
    }))
    if (!exportIncludeDeleted) return active
    const activeIds = new Set(active.map((c) => c.company_id))
    const deletedMap = new Map()
    companiesWithDeleted.forEach((item) => {
      if (!item?.company_id || activeIds.has(item.company_id) || !item?.is_deleted) return
      const name = item.name || item.company_id
      deletedMap.set(item.company_id, {
        company_id: item.company_id,
        name: formatUiTemplate('entity.deleted', { name }),
        country: item.country || null,
        is_deleted: true
      })
    })
    return [...active, ...Array.from(deletedMap.values())]
  }, [companies, companiesWithDeleted, exportIncludeDeleted])

  const exportSelectedCompanies = useMemo(
    () => exportCompanyOptions.filter((c) => exportCompanyIds.includes(c.company_id)),
    [exportCompanyOptions, exportCompanyIds]
  )

  const exportSiteOptions = useMemo(() => {
    if (exportCompanyIds.length !== 1) return []
    const siteSource = exportIncludeDeleted && sitesWithDeleted.length
      ? sitesWithDeleted
      : sites
    return sitesForCompany(siteSource, exportCompanyIds[0]).map((site) => ({
      ...site,
      name: site.is_deleted
        ? formatUiTemplate('entity.deleted', { name: site.name || site.site_id })
        : site.name
    }))
  }, [sites, sitesWithDeleted, exportCompanyIds, exportIncludeDeleted])

  const exportPosOptions = useMemo(() => {
    if (exportCompanyIds.length !== 1) return []
    const posSites = [...sites, ...sitesWithDeleted]
    const posSource = exportIncludeDeleted ? posListWithDeleted : posList
    const scopedPos = exportSiteIds.length
      ? posSource.filter((pos) => exportSiteIds.includes(pos.site_id))
      : posForCompany(posSource, [...sites, ...sitesWithDeleted], exportCompanyIds[0])
    const posOptions = scopedPos.map((p) => {
      const isDeleted = Boolean(p.is_deleted)
      const baseName = formatScopedPosLabel(p, posSites, exportSiteIds.length !== 1)
      return {
        ...p,
        name: isDeleted
          ? formatUiTemplate('entity.deleted_at', {
            name: baseName,
            date: formatDateDisplay(p.deleted_at)
          })
          : baseName
      }
    })

    return posOptions
  }, [posList, posListWithDeleted, sites, sitesWithDeleted, exportCompanyIds, exportSiteIds, exportIncludeDeleted])

  const exportProfile = useMemo(() => {
    if (!exportSelectedCompanies.length) return null
    const profileRefs = exportSelectedCompanies.map((company) => {
      const profile = activeProfilesByCountry[company.country]
      return profile?.profile_ref || null
    })
    if (profileRefs.some((ref) => !ref)) return null
    const uniqueRefs = new Set(profileRefs)
    if (uniqueRefs.size !== 1) return null
    const firstCompany = exportSelectedCompanies[0]
    return activeProfilesByCountry[firstCompany.country] || null
  }, [activeProfilesByCountry, exportSelectedCompanies])

  const exportCalendarCells = useMemo(() => buildCalendarCells(exportPickerMonth), [exportPickerMonth])
  const uploadCalendarCells = useMemo(() => buildCalendarCells(uploadPickerMonth), [uploadPickerMonth])
  const exportCategoryOptions = useMemo(
    () => {
      const exportCountry = exportProfile?.country || ''
      return Object.entries(exportPreview?.counts || {}).map(([type, count]) => ({
        value: type,
        label: formatUiTemplate('export.category.count', {
          label: getArtifactTypeLabel(type, exportCountry),
          count
        })
      }))
    },
    [exportPreview, exportProfile]
  )
  const exportSelectedCountTotal = exportSelectedArtifactTypes.reduce(
    (total, type) => total + Number(exportPreview?.counts?.[type] || 0),
    0
  )

  const toggleCompanyExpand = (companyId, currentExpanded = false) => {
    if (isOrganizationSearchActive) {
      setOrganizationSearchExpansion((prev) => {
        const current = prev.query === organizationSearchQuery
          ? prev
          : { query: organizationSearchQuery, companies: {}, sites: {} }
        return { ...current, companies: { ...current.companies, [companyId]: !currentExpanded } }
      })
      return
    }
    setExpandedCompanies((prev) => ({ ...prev, [companyId]: !prev[companyId] }))
  }

  const toggleSiteExpand = (siteId, currentExpanded = false) => {
    if (isOrganizationSearchActive) {
      setOrganizationSearchExpansion((prev) => {
        const current = prev.query === organizationSearchQuery
          ? prev
          : { query: organizationSearchQuery, companies: {}, sites: {} }
        return { ...current, sites: { ...current.sites, [siteId]: !currentExpanded } }
      })
      return
    }
    setExpandedSites((prev) => ({ ...prev, [siteId]: !prev[siteId] }))
  }

  const togglePlatformOrgResellerExpand = (userId) => {
    setPlatformOrgExpandedResellerIds((prev) => ({ ...prev, [userId]: !prev[userId] }))
  }

  const toggleResellerOrgResellerExpand = (userId) => {
    setResellerOrgExpandedResellerIds((prev) => ({ ...prev, [userId]: !prev[userId] }))
  }

  useEffect(() => {
    const validCompanyIds = new Set(companies.map((company) => company.company_id))
    setExpandedCompanies((prev) => {
      const next = {}
      let changed = false
      for (const company of companies) {
        if (Object.prototype.hasOwnProperty.call(prev, company.company_id)) {
          next[company.company_id] = prev[company.company_id]
        } else {
          next[company.company_id] = false
          changed = true
        }
      }
      for (const existingId of Object.keys(prev)) {
        if (!validCompanyIds.has(existingId)) changed = true
      }
      return changed ? next : prev
    })
  }, [companies])

  useEffect(() => {
    const validSiteIds = new Set(sites.map((site) => site.site_id))
    setExpandedSites((prev) => {
      const next = {}
      let changed = false
      for (const site of sites) {
        if (Object.prototype.hasOwnProperty.call(prev, site.site_id)) {
          next[site.site_id] = prev[site.site_id]
        } else {
          next[site.site_id] = false
          changed = true
        }
      }
      for (const existingId of Object.keys(prev)) {
        if (!validSiteIds.has(existingId)) changed = true
      }
      return changed ? next : prev
    })
  }, [sites])

  useEffect(() => {
    if (uploadDateLocked) return
    if (uploadCompanySearchTerm.trim()) return
    if (!uploadCompanyId && companies.length === 1) {
      setUploadCompanyId(companies[0].company_id)
      return
    }
    if (!uploadCompanyId || companies.length <= 1) return
    if (!companies.some((company) => company.company_id === uploadCompanyId)) {
      setUploadCompanyId('')
    }
  }, [companies, uploadCompanyId, uploadCompanySearchTerm, uploadDateLocked])

  useEffect(() => {
    if (companies.length < UI_SEARCH_THRESHOLD && uploadCompanySearchTerm) {
      setUploadCompanySearchTerm('')
    }
  }, [companies.length, uploadCompanySearchTerm])

  useEffect(() => {
    if (uploadDateLocked) return
    if (!uploadCompanySearchTerm.trim()) return
    if (uploadCompanySelectOptions.length > 1) {
      if (uploadCompanyId) setUploadCompanyId('')
      return
    }
    if (uploadCompanySelectOptions.length !== 1) return
    const onlyMatchId = uploadCompanySelectOptions[0]?.company_id || ''
    if (!onlyMatchId || onlyMatchId === uploadCompanyId) return
    setUploadCompanyId(onlyMatchId)
  }, [uploadDateLocked, uploadCompanyId, uploadCompanySearchTerm, uploadCompanySelectOptions])

  useEffect(() => {
    if (!exportCompanyIds.length && exportCompanyOptions.length) {
      setExportCompanyIds([exportCompanyOptions[0].company_id])
    }
  }, [exportCompanyOptions, exportCompanyIds])

  useEffect(() => {
    if (!exportCompanyIds.length) return
    const validCompanyIds = new Set(exportCompanyOptions.map((c) => c.company_id))
    const cleaned = exportCompanyIds.filter((id) => validCompanyIds.has(id))
    if (cleaned.length !== exportCompanyIds.length) {
      setExportCompanyIds(cleaned)
      setExportSiteIds([])
      setExportPosIds([])
    }
  }, [exportCompanyOptions, exportCompanyIds])

  useEffect(() => {
    if (!selectableCountries.length) return
    if (companyCountry && selectableCountries.includes(companyCountry)) return
    if (selectableCountries.includes('DE')) {
      setCompanyCountry('DE')
      return
    }
    setCompanyCountry(selectableCountries[0])
  }, [companyCountry, selectableCountries])

  useEffect(() => {
    if (!uploadPosRefs.length) return
    const validPosIds = new Set(uploadPosOptions.map((p) => p.pos_id))
    const cleaned = uploadPosRefs.filter((id) => validPosIds.has(id)).slice(-1)
    if (cleaned.length !== uploadPosRefs.length || cleaned[0] !== uploadPosRefs[0]) {
      setUploadPosRefs(cleaned)
    }
  }, [uploadPosOptions, uploadPosRefs])

  useEffect(() => {
    if (!uploadSiteId) return
    if (uploadSiteOptions.some((site) => site.site_id === uploadSiteId)) return
    setUploadSiteId('')
    setUploadPosRefs([])
  }, [uploadSiteId, uploadSiteOptions])

  useEffect(() => {
    if (uploadArtifactType !== CUSTOM_ARTIFACT_TYPE) {
      setUploadCustomArtifactLabel('')
    }
  }, [uploadArtifactType])

  useEffect(() => {
    if (uploadDateLocked) {
      setUploadPickerOpen(false)
    }
  }, [uploadDateLocked])

  useEffect(() => {
    if (exportPosIds.length && exportPosIds.some((id) => !exportPosOptions.some((p) => p.pos_id === id))) {
      setExportPosIds([])
    }
  }, [exportPosIds, exportPosOptions])

  useEffect(() => {
    const validSiteIds = new Set(exportSiteOptions.map((site) => site.site_id))
    const selectedSiteIds = exportSiteIds.filter((siteId) => validSiteIds.has(siteId))
    if (selectedSiteIds.length === exportSiteIds.length) return
    setExportSiteIds(selectedSiteIds)
    setExportPosIds([])
    setExportFilterApplied(false)
    setExportPreview(null)
  }, [exportSiteIds, exportSiteOptions])

  const handleExportDatePick = (isoDate) => {
    if (!isoDate) return
    setExportError('')
    setExportSuccess('')
    setExportFilterApplied(false)
    setExportPreview(null)
    if (!exportRangeStart || (exportRangeStart && exportRangeEnd)) {
      setExportRangeStart(isoDate)
      setExportRangeEnd('')
      return
    }
    if (isoDate < exportRangeStart) {
      setExportRangeEnd(exportRangeStart)
      setExportRangeStart(isoDate)
    } else {
      setExportRangeEnd(isoDate)
    }
    setExportPickerOpen(false)
  }

  const handleExportPickerMonth = (delta) => {
    setExportPickerMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  const resetExportFilters = () => {
    setExportCompanyIds(exportCompanyOptions.length ? [exportCompanyOptions[0].company_id] : [])
    setExportSiteIds([])
    setExportPosIds([])
    setExportRangeStart('')
    setExportRangeEnd('')
    setExportIncludeDeleted(false)
    setExportSelectedArtifactTypes([])
    setExportFilterApplied(false)
    setExportPreview(null)
    setExportPickerOpen(false)
    setExportPickerMonth(monthStart(new Date()))
    setExportError('')
    setExportSuccess('')
  }

  const handleExportPickerYear = (deltaYears) => {
    setExportPickerMonth((prev) => new Date(prev.getFullYear() + deltaYears, prev.getMonth(), 1))
  }

  const handleUploadDatePick = (isoDate) => {
    if (!isoDate || uploadDateLocked) return
    setUploadError('')
    setUploadSuccess('')
    if (!uploadFrom || (uploadFrom && uploadTo)) {
      setUploadFrom(isoDate)
      setUploadTo('')
      return
    }
    if (isoDate < uploadFrom) {
      setUploadTo(uploadFrom)
      setUploadFrom(isoDate)
    } else {
      setUploadTo(isoDate)
    }
    setUploadPickerOpen(false)
  }

  const handleUploadPickerMonth = (delta) => {
    if (uploadDateLocked) return
    setUploadPickerMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  const handleUploadPickerYear = (deltaYears) => {
    if (uploadDateLocked) return
    setUploadPickerMonth((prev) => new Date(prev.getFullYear() + deltaYears, prev.getMonth(), 1))
  }

  const runExportFilter = async () => {
    setExportError('')
    setExportSuccess('')
    if (!exportCompanyIds.length) {
      setExportError('Bitte mindestens ein Unternehmen wählen.')
      return
    }
    if (!exportRangeStart || !exportRangeEnd) {
      setExportError(TIME_RANGE_REQUIRED_ERROR)
      return
    }
    if (exportRangeEnd < exportRangeStart) {
      setExportError('Zeitraum ungültig: "Bis" liegt vor "Von".')
      return
    }

    try {
      const res = await fetch(`${apiBase}/exports/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          business_time_from: exportRangeStart,
          business_time_to: exportRangeEnd,
          company_ids: exportCompanyIds,
          site_ids: exportSiteIds,
          pos_ids: exportPosIds,
          include_deleted: exportIncludeDeleted
        })
      })
      if (!res.ok) {
        setExportError(await parseError(res))
        return
      }
      const data = await res.json()
      const counts = data && typeof data.counts === 'object' && data.counts !== null ? data.counts : {}
      const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : 0
      setExportSelectedArtifactTypes(Object.keys(counts))
      setExportPreview({ counts, total })
      setExportFilterApplied(true)
    } catch {
      setExportError(formatUiTemplate('export.preview.load_failed'))
      return
    }
  }

  const handleRunExport = async () => {
    setExportError('')
    setExportSuccess('')
    setExportAuditorEmailError('')
    if (!exportFilterApplied || !exportPreview || exportPreview.total <= 0) {
      setExportError('Bitte zuerst Filter anwenden und Treffer erzeugen.')
      return
    }
    if (!exportProfile?.profile_ref) {
      setExportError('Kein gemeinsames wirksames Profil für die gewählte Unternehmensauswahl verfügbar.')
      return
    }
    if (!exportSelectedArtifactTypes.length) {
      setExportError('Bitte mindestens eine Kategorie auswählen.')
      return
    }
    if (exportAuditorEmailEnabled && !String(exportAuditorEmail || '').trim()) {
      setExportAuditorEmailError('Bitte E-Mail-Adresse des Empfängers eingeben.')
      return
    }
    setExportBusy(true)
    try {
      const res = await fetch(`${apiBase}/exports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          profile_ref: exportProfile.profile_ref,
          business_time_from: exportRangeStart,
          business_time_to: exportRangeEnd,
          ...(exportCompanyIds.length === 1 ? { company_id: exportCompanyIds[0] } : {}),
          ...(exportCompanyIds.length > 1 ? { company_ids: exportCompanyIds } : {}),
          ...(exportSiteIds.length === 1 ? { site_id: exportSiteIds[0] } : {}),
          ...(exportSiteIds.length > 1 ? { site_ids: exportSiteIds } : {}),
          ...(exportPosIds.length === 1 ? { pos_id: exportPosIds[0] } : {}),
          ...(exportPosIds.length > 1 ? { pos_ids: exportPosIds } : {}),
          artifact_types: exportSelectedArtifactTypes,
          include_deleted: exportIncludeDeleted,
          ...(exportAuditorEmailEnabled && String(exportAuditorEmail || '').trim() ? { auditor_email: String(exportAuditorEmail).trim() } : {})
        })
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      setExportSuccess(formatUiTemplate('export.created'))
      const listRes = await fetch(`${apiBase}/exports`, { headers: authHeaders })
      if (listRes.ok) {
        const list = await listRes.json()
        setExports(Array.isArray(list) ? list : [])
      }
    } catch (err) {
      setExportError(err?.message || 'Export fehlgeschlagen.')
    } finally {
      setExportBusy(false)
    }
  }

  const handleExportDownload = async (exportId) => {
    setExportError('')
    try {
      const publicUrl = await getExportPublicUrl({
        apiBase,
        exportId,
        authHeaders,
        parseError
      })
      window.location.assign(publicUrl)
    } catch (err) {
      setExportError(err?.message || 'Export konnte nicht heruntergeladen werden.')
    }
  }

  const refreshApiKeys = async () => {
    const res = await fetch(`${apiBase}/tenant/api-keys?ts=${Date.now()}`, {
      headers: authHeaders,
      cache: 'no-store'
    })
    if (res.ok) {
      const data = await res.json()
      setApiKeys(data)
    }
  }

  const handleCreateOnboardingCode = async () => {
    setApiKeyOnboardingCode((prev) => ({
      ...prev,
      busy: true,
      error: '',
      copied: false
    }))
    try {
      const res = await fetch(`${apiBase}/tenant/api-keys/onboarding-code`, {
        method: 'POST',
        headers: authHeaders
      })
      if (!res.ok) {
        const message = await parseError(res)
        const errorMessage = res.status === 401
          ? 'Bitte erneut anmelden.'
          : (res.status === 403
              ? (message || 'Keine Berechtigung, einen Onboarding-Code zu erzeugen.')
              : (res.status === 410
                  ? 'Mandant wurde gelöscht.'
                  : (message || 'Onboarding-Code konnte nicht erzeugt werden.')))
        throw new Error(errorMessage)
      }
      const data = await res.json()
      setApiKeyOnboardingCode({
        code: String(data?.onboarding_code || ''),
        busy: false,
        error: '',
        copied: false
      })
    } catch (err) {
      const message = String(err?.message || '')
      setApiKeyOnboardingCode((prev) => ({
        ...prev,
        busy: false,
        error: message || 'Onboarding-Code konnte nicht erzeugt werden.',
        copied: false
      }))
    }
  }

  const handleOnboardingCodeGenerationRequest = () => {
    if (apiKeyOnboardingCode.code) {
      openConfirmationDialog({
        message: formatUiTemplate('confirm.onboarding_code.generate_another'),
        confirmLabel: 'Neuen Code erzeugen',
        onConfirm: handleCreateOnboardingCode
      })
      return
    }
    handleCreateOnboardingCode()
  }

  const handleCopyOnboardingCode = async () => {
    const code = String(apiKeyOnboardingCode.code || '').trim()
    if (!code) return
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code)
        setApiKeyOnboardingCode((prev) => ({ ...prev, copied: true, error: '' }))
        return
      }
      throw new Error('Clipboard nicht verfügbar.')
    } catch {
      setApiKeyOnboardingCode((prev) => ({
        ...prev,
        copied: false,
        error: 'Onboarding-Code konnte nicht kopiert werden.'
      }))
    }
  }

  const buildBulkApiKeyApprovalPayload = (item) => {
    const companyName = String(item.reported_company_name || item.company_name || '').trim()
    const companyCountry = String(
      item.reported_company_country ||
      companies.find((company) => String(company.name || '').trim().toLowerCase() === companyName.toLowerCase())?.country ||
      ''
    ).trim().toUpperCase()
    const matchingCompany = companies.find((company) => {
      if (company.is_deleted || String(company.name || '').trim().toLowerCase() !== companyName.toLowerCase()) return false
      return !companyCountry || String(company.country || '').trim().toUpperCase() === companyCountry
    })
    const siteName = String(item.reported_site_name || item.site_name || '').trim()
    if (!companyName || !companyCountry || !siteName) return null
    return {
      ...(matchingCompany
        ? { company_id: matchingCompany.company_id }
        : { company_name: companyName, company_country: companyCountry }),
      site_name: siteName
    }
  }

  const toggleApiKeySelection = (apiKeyId) => {
    const id = String(apiKeyId || '').trim()
    if (!id) return
    setSelectedApiKeyIds((prev) => (
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    ))
  }

  const toggleAllPendingApiKeys = () => {
    const pendingIds = pendingApiKeys.map((item) => String(item.api_key_id || '').trim()).filter(Boolean)
    const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedPendingApiKeyIds.has(id))
    setSelectedApiKeyIds(allSelected ? [] : pendingIds)
  }

  const handleApproveSelectedApiKeys = async () => {
    const selectedItems = apiKeys.filter((item) => (
      selectedPendingApiKeyIds.has(String(item.api_key_id || '')) &&
      String(item.status || '').toLowerCase() === 'requested'
    ))
    if (!selectedItems.length || bulkApiKeyApprovalBusy) return
    setBulkApiKeyApprovalBusy(true)
    setBulkApiKeyApprovalStatus('')
    const failed = []
    let approvedCount = 0
    for (const item of selectedItems) {
      const payload = buildBulkApiKeyApprovalPayload(item)
      if (!payload) {
        failed.push(item.api_key_id)
        continue
      }
      try {
        const res = await fetch(`${apiBase}/tenant/api-keys/${item.api_key_id}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify(payload)
        })
        if (!res.ok) {
          failed.push(item.api_key_id)
          continue
        }
        approvedCount += 1
        setApiKeys((prev) => prev.map((current) => (
          current.api_key_id === item.api_key_id
            ? { ...current, status: 'approved', config_status: 'ok' }
            : current
        )))
      } catch {
        failed.push(item.api_key_id)
      }
    }
    setSelectedApiKeyIds(failed)
    await refreshOrgTree()
    setBulkApiKeyApprovalStatus(
      formatUiTemplate(
        failed.length ? 'api_keys.bulk.result_failed' : 'api_keys.bulk.result',
        { approved: approvedCount, failed: failed.length }
      )
    )
    setBulkApiKeyApprovalBusy(false)
  }

  const mergeApiKeyStatus = (apiKeyId, nextStatus) => {
    const id = String(apiKeyId || '').trim()
    const status = String(nextStatus || '').trim().toLowerCase()
    if (!id || !status) return
    setApiKeys((prev) => prev.map((item) => (String(item.api_key_id || '') === id ? { ...item, status } : item)))
  }

  const openApiKeyActionDialog = (item, mode) => {
    const mappedPos = posList.find((pos) => String(pos.pos_id || '') === String(item.pos_id || ''))
    const reportedLabel = item.reported_pos_label || ''
    const reportedTseType = item.reported_tse_type || ''
    const reportedTseSerialNumber = item.reported_tse_serial_number || ''
    setApiKeyActionDialog({
      open: true,
      mode,
      apiKey: item.api_key_id || '',
      posId: item.pos_id || '',
      posSerial: item.pos_serial || '',
      companyId: '',
      siteId: '',
      createCompany: false,
      createSite: false,
      newCompanyName: '',
      newCompanyCountry: item.reported_company_country || 'DE',
      newSiteName: '',
      reportedCompanyName: item.reported_company_name || '',
      reportedCompanyCountry: item.reported_company_country || '',
      reportedSiteName: item.reported_site_name || '',
      reportedPosSerial: item.reported_pos_serial || item.pos_serial || '',
      reportedPosLabel: reportedLabel,
      reportedTseType,
      reportedTseSerialNumber,
      labelChanged: !mappedPos || String(mappedPos.pos_label || '') !== String(reportedLabel),
      tseTypeChanged: !mappedPos || normalizePosTseType(mappedPos.tse_type) !== normalizePosTseType(reportedTseType),
      tseSerialNumberChanged: !mappedPos || String(mappedPos.tse_serial_number || '') !== String(reportedTseSerialNumber),
      status: '',
      busy: false
    })
  }

  const closeApiKeyActionDialog = () => {
    setApiKeyActionDialog({
      open: false,
      mode: 'approve',
      apiKey: '',
      posId: '',
      posSerial: '',
      companyId: '',
      siteId: '',
      createCompany: false,
      createSite: false,
      newCompanyName: '',
      newCompanyCountry: 'DE',
      newSiteName: '',
      reportedCompanyName: '',
      reportedCompanyCountry: '',
      reportedSiteName: '',
      reportedPosSerial: '',
      reportedPosLabel: '',
      reportedTseType: '',
      reportedTseSerialNumber: '',
      labelChanged: false,
      tseTypeChanged: false,
      tseSerialNumberChanged: false,
      status: '',
      busy: false
    })
  }

  const handleApproveOrRefreshApiKey = async () => {
    if (apiKeyActionDialog.mode === 'refresh') {
      setApiKeyActionDialog((prev) => ({ ...prev, busy: true, status: '' }))
      try {
        const res = await fetch(`${apiBase}/tenant/api-keys/${apiKeyActionDialog.apiKey}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({})
        })
        if (!res.ok) {
          const msg = await parseError(res)
          setApiKeyActionDialog((prev) => ({ ...prev, status: msg || 'Aktualisierung fehlgeschlagen.' }))
          return
        }
        await refreshApiKeys()
        closeApiKeyActionDialog()
      } finally {
        setApiKeyActionDialog((prev) => ({ ...prev, busy: false }))
      }
      return
    }
    const companyId = String(apiKeyActionDialog.companyId || '').trim()
    const siteId = String(apiKeyActionDialog.siteId || '').trim()
    if (!apiKeyActionDialog.createCompany && !companyId) {
      setApiKeyActionDialog((prev) => ({
        ...prev,
        status: canEditCompanies
          ? 'Bitte Unternehmen wählen oder neu anlegen.'
          : 'Bitte ein bestehendes Unternehmen wählen. Die Neuanlage erfordert das Recht Unternehmen verwalten.'
      }))
      return
    }
    if (!apiKeyActionDialog.createSite && !siteId) {
      setApiKeyActionDialog((prev) => ({
        ...prev,
        status: canEditSites
          ? 'Bitte Betriebsstätte wählen oder neu anlegen.'
          : 'Bitte eine bestehende Betriebsstätte wählen. Die Neuanlage erfordert das Recht Betriebsstätten verwalten.'
      }))
      return
    }
    const payload = {}
    if (apiKeyActionDialog.createCompany) {
      const companyName = String(apiKeyActionDialog.newCompanyName || '').trim()
      const companyCountry = String(apiKeyActionDialog.newCompanyCountry || '').trim().toUpperCase()
      if (!companyName || !companyCountry) {
        setApiKeyActionDialog((prev) => ({ ...prev, status: 'Bitte neuen Unternehmensnamen und Land angeben.' }))
        return
      }
      payload.company_name = companyName
      payload.company_country = companyCountry
    } else if (companyId) {
      payload.company_id = companyId
    }
    if (apiKeyActionDialog.createSite) {
      const siteName = String(apiKeyActionDialog.newSiteName || '').trim()
      if (!siteName) {
        setApiKeyActionDialog((prev) => ({ ...prev, status: 'Bitte neuen Betriebsstättennamen angeben.' }))
        return
      }
      payload.site_name = siteName
    } else if (siteId) {
      payload.site_id = siteId
    }
    if ((!payload.company_id && !payload.company_name) || (!payload.site_id && !payload.site_name)) {
      setApiKeyActionDialog((prev) => ({ ...prev, status: 'Bitte Unternehmen/Betriebsstätte wählen oder neu anlegen.' }))
      return
    }
    setApiKeyActionDialog((prev) => ({ ...prev, busy: true, status: '' }))
    try {
      const res = await fetch(`${apiBase}/tenant/api-keys/${apiKeyActionDialog.apiKey}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        const msg = await parseError(res)
        setApiKeyActionDialog((prev) => ({ ...prev, busy: false, status: msg || 'Aktion fehlgeschlagen.' }))
        return
      }
      await refreshApiKeys()
      setSelectedApiKeyIds((prev) => prev.filter((id) => id !== apiKeyActionDialog.apiKey))
      closeApiKeyActionDialog()
    } finally {
      setApiKeyActionDialog((prev) => ({ ...prev, busy: false }))
    }
  }

  const handleDeactivateApiKey = async (key) => {
    const apiKey = String(key || '').trim()
    if (!apiKey) return
    // Immediate UI response.
    mergeApiKeyStatus(apiKey, 'deactivated')
    const res = await fetch(`${apiBase}/tenant/api-keys/${apiKey}/deactivate`, {
      method: 'POST',
      headers: authHeaders
    })
    if (!res.ok) {
      await refreshApiKeys()
      return
    }
    try {
      const data = await res.json()
      mergeApiKeyStatus(data?.api_key_id || apiKey, data?.status || 'deactivated')
    } catch {
      // ignore parse fallback
    }
    window.setTimeout(() => {
      refreshApiKeys()
    }, 300)
  }

  const handleDeleteApiKey = async (apiKey, confirmed = false) => {
    const key = typeof apiKey === 'string' ? apiKey : apiKey?.api_key_id
    if (!key) return
    const isRequested = String(apiKey?.status || '').toLowerCase() === 'requested'
    if (!confirmed) {
      const label = apiKey?.pos_serial || apiKey?.reported_pos_serial || key
      openConfirmationDialog({
        message: formatConfirmationTemplate(isRequested ? 'confirm.reject.api_key' : 'confirm.delete.api_key', { label }),
        confirmLabel: isRequested ? 'Zurückweisen' : 'API-Key löschen',
        destructive: true,
        onConfirm: () => handleDeleteApiKey(apiKey, true)
      })
      return
    }
    await fetch(`${apiBase}/tenant/api-keys/${key}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    await refreshApiKeys()
  }

  const handleReactivateApiKey = async (key) => {
    const apiKey = String(key || '').trim()
    if (!apiKey) return
    // Immediate UI response.
    mergeApiKeyStatus(apiKey, 'approved')
    const res = await fetch(`${apiBase}/tenant/api-keys/${apiKey}/reactivate`, {
      method: 'POST',
      headers: authHeaders
    })
    if (!res.ok) {
      await refreshApiKeys()
      return
    }
    try {
      const data = await res.json()
      mergeApiKeyStatus(data?.api_key_id || apiKey, data?.status || 'approved')
    } catch {
      // ignore parse fallback
    }
    window.setTimeout(() => {
      refreshApiKeys()
    }, 300)
  }

  const renderApiActionIcon = (icon, title, onClick, options = {}) => (
    <IconButton
      icon={icon}
      label={title}
      onClick={onClick}
      disabled={Boolean(options.disabled)}
      tone={options.tone || (options.danger ? 'danger' : 'default')}
    />
  )

  const renderEntityActionIcon = (kind, title, onClick, options = {}) => {
    return (
      <IconButton
        icon={kind}
        label={title}
        onClick={onClick}
        disabled={Boolean(options.disabled)}
        tone={kind === 'delete' || options.danger ? 'danger' : 'default'}
        variant="quiet"
      />
    )
  }

  const getTenantUserActions = (item) => {
    const status = String(item.status || '').toLowerCase()
    return [
      {
        key: 'edit',
        label: 'Editieren',
        onClick: () => setEditTenantUser({ userId: item.user_id, name: item.name || '', email: item.email || '', rights: normalizeTenantUserRights(item.rights) })
      },
      status === 'initial' && {
        key: 'resend',
        label: 'Einladung erneut senden',
        onClick: () => handleResendTenantUserInvitation(item.user_id),
        disabled: resendInvitationBusyUserId === item.user_id
      },
      {
        key: 'permissions',
        label: 'Rechte',
        onClick: () => openTenantUserRightsDialog('edit', { userId: item.user_id, label: item.email || item.name || 'Nutzer', rights: item.rights })
      },
      {
        key: 'lock',
        label: status === 'locked' ? 'Entsperren' : 'Sperren',
        tone: status === 'locked' ? 'positive' : 'warning',
        onClick: () => handleToggleTenantUserLock(item.user_id, item.email || item.name || 'Nutzer', status === 'locked')
      },
      {
        key: 'delete',
        label: 'Löschen',
        tone: 'danger',
        onClick: () => handleDeleteTenantUser(item.user_id, item.email || item.name || 'Nutzer')
      }
    ].filter(Boolean)
  }

  const renderApiKeyActions = (item) => {
    if (!canManageApiKeys) return <span>-</span>
    const isDialogOpen = apiKeyActionDialog.open && apiKeyActionDialog.apiKey === (item.api_key_id || '')
    if (isDialogOpen) return <span className="hint">Dialog geöffnet</span>
    const status = String(item.status || '').toLowerCase()
    const configStatus = String(item.config_status || '').toLowerCase()
    if (status === 'requested') {
      return (
        <>
          {renderApiActionIcon('approve', 'Freigeben', () => openApiKeyActionDialog(item, 'approve'), { tone: 'positive' })}
          {renderApiActionIcon('close', 'Zurückweisen', () => handleDeleteApiKey(item), { danger: true })}
        </>
      )
    }
    if (status === 'approved' && configStatus === 'updated') {
      return (
        <>
          {renderApiActionIcon('refresh', 'Aktualisieren', () => openApiKeyActionDialog(item, 'refresh'))}
          {renderApiActionIcon('delete', 'Löschen', () => handleDeleteApiKey(item), { danger: true })}
        </>
      )
    }
    if (status === 'approved') {
      return (
        <>
          {renderApiActionIcon('power', 'Deaktivieren', () => handleDeactivateApiKey(item.api_key_id), { tone: 'warning' })}
          {renderApiActionIcon('delete', 'Löschen', () => handleDeleteApiKey(item), { danger: true })}
        </>
      )
    }
    if (status === 'deactivated') {
      return (
        <>
          {renderApiActionIcon('power', 'Reaktivieren', () => handleReactivateApiKey(item.api_key_id), { tone: 'positive' })}
          {renderApiActionIcon('delete', 'Löschen', () => handleDeleteApiKey(item), { danger: true })}
        </>
      )
    }
    return <span>-</span>
  }

  const mapApiKeyStateLabel = (item) => {
    const status = String(item?.status || '').toLowerCase()
    const configStatus = String(item?.config_status || '').toLowerCase()
    if (status === 'requested') return 'Neu'
    if (status === 'approved' && configStatus === 'updated') return 'Updated'
    if (status === 'approved') return 'OK'
    if (status === 'deactivated') return 'Deaktiviert'
    return status || '-'
  }

  const handleCreateCompany = async (name, country) => {
    const normalizedName = String(name || '').trim()
    if (!normalizedName) {
      setCompanyNameInvalid(true)
      setCompanyCreateError('Bitte Unternehmensname eingeben.')
      return false
    }
    if (!country) {
      setCompanyCreateError('Bitte ein Land auswählen.')
      return false
    }
    const createRes = await fetch(`${apiBase}/tenant/companies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tenant_id: tenantId, name: normalizedName, country })
    })
    if (!createRes.ok) {
      setCompanyCreateError(await parseError(createRes))
      return false
    }
    const res = await fetch(`${apiBase}/tenant/companies?tenant_id=${tenantId}`, { headers: authHeaders })
    if (res.ok) setCompanies(await res.json())
    return true
  }

  const handleCreateSite = async (companyId, name) => {
    const normalizedName = String(name || '').trim()
    if (!normalizedName) {
      setSiteNameInvalid(true)
      setSiteCreateError('Bitte Betriebsstätte eingeben.')
      return false
    }
    const createRes = await fetch(`${apiBase}/tenant/sites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tenant_id: tenantId, company_id: companyId, name: normalizedName })
    })
    if (!createRes.ok) {
      setSiteCreateError(await parseError(createRes))
      return false
    }
    const res = await fetch(`${apiBase}/tenant/sites?tenant_id=${tenantId}`, { headers: authHeaders })
    if (res.ok) setSites(await res.json())
    return true
  }

  const openCreatePosDialog = (siteId) => {
    setCreatePosDialog({
      siteId,
      posId: '',
      posLabel: '',
      tseType: 'swissbit_cloud_tse',
      tseSerialNumber: '',
      status: '',
      fieldErrors: {},
      apiKey: '',
      copied: false
    })
  }

  const resetCreatePosDialog = () => {
    setCreatePosDialog({
      siteId: '',
      posId: '',
      posLabel: '',
      tseType: 'swissbit_cloud_tse',
      tseSerialNumber: '',
      status: '',
      fieldErrors: {},
      apiKey: '',
      copied: false
    })
  }

  const handleCreatePos = async () => {
    const normalizedPosId = String(createPosDialog.posId || '').trim()
    if (!createPosDialog.siteId || !normalizedPosId) {
      setCreatePosDialog((prev) => ({ ...prev, status: 'Bitte POS-Seriennummer angeben.', fieldErrors: { posId: true } }))
      return
    }
    const swissbitCloudMode = isSwissbitCloudPosTseType(createPosDialog.tseType)
    const fiskalyCloudMode = isFiskalyCloudPosTseType(createPosDialog.tseType)
    if (fiskalyCloudMode) {
      setCreatePosDialog((prev) => ({ ...prev, status: 'fiskaly Cloud TSE ist aktuell nicht unterstützt.' }))
      return
    }
    const serialNumber = String(createPosDialog.tseSerialNumber || '').trim()
    if (swissbitCloudMode && !serialNumber) {
      setCreatePosDialog((prev) => ({ ...prev, status: 'TSE Seriennummer ist erforderlich.', fieldErrors: { tseSerialNumber: true } }))
      return
    }
    const resCreate = await fetch(`${apiBase}/tenant/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        tenant_id: tenantId,
        site_id: createPosDialog.siteId,
        pos_serial: normalizedPosId,
        pos_label: createPosDialog.posLabel || '',
        tse_type: normalizePosTseType(createPosDialog.tseType),
        tse_serial_number: serialNumber
      })
    })
    if (!resCreate.ok) {
      const msg = await parseError(resCreate)
      setCreatePosDialog((prev) => ({ ...prev, status: msg || 'POS konnte nicht angelegt werden.' }))
      return
    }
    const created = await resCreate.json()
    const res = await fetch(`${apiBase}/tenant/pos?tenant_id=${tenantId}`, { headers: authHeaders })
    if (res.ok) setPosList(await res.json())
    setCreatePosDialog((prev) => ({
      ...prev,
      apiKey: String(created.api_key || ''),
      posId: String(created.pos_serial || prev.posId || ''),
      posLabel: String(created.pos_label || prev.posLabel || ''),
      copied: false,
      status: '',
      fieldErrors: {}
    }))
  }

  const handleCopyCreatedPosApiKey = async () => {
    const apiKey = String(createPosDialog.apiKey || '')
    if (!apiKey || !navigator.clipboard?.writeText) {
      setCreatePosDialog((prev) => ({ ...prev, status: 'API-Key konnte nicht kopiert werden.', copied: false }))
      return
    }
    try {
      await navigator.clipboard.writeText(apiKey)
      setCreatePosDialog((prev) => ({ ...prev, status: '', copied: true }))
    } catch {
      setCreatePosDialog((prev) => ({ ...prev, status: 'API-Key konnte nicht kopiert werden.', copied: false }))
    }
  }

  const handleRenameTenant = async () => {
    const name = tenantNameEdit.trim()
    if (!name) return
    const res = await fetch(`${apiBase}/tenant/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name })
    })
    if (!res.ok) return
    const data = await res.json()
    onUpdateAuthUser((prev) => (prev ? { ...prev, tenant_name: data.name } : prev))
  }

  const refreshOrgTree = async () => {
    const [companiesRes, sitesRes, posRes, keysRes] = await Promise.all([
      fetch(`${apiBase}/tenant/companies?tenant_id=${tenantId}`, { headers: authHeaders }),
      fetch(`${apiBase}/tenant/sites?tenant_id=${tenantId}`, { headers: authHeaders }),
      fetch(`${apiBase}/tenant/pos?tenant_id=${tenantId}`, { headers: authHeaders }),
      fetch(`${apiBase}/tenant/api-keys`, { headers: authHeaders })
    ])
    const [nextCompanies, nextSites, nextPos, nextKeys] = await Promise.all([
      companiesRes.ok ? companiesRes.json() : null,
      sitesRes.ok ? sitesRes.json() : null,
      posRes.ok ? posRes.json() : null,
      keysRes.ok ? keysRes.json() : null
    ])
    if (nextCompanies) setCompanies(nextCompanies)
    if (nextSites) setSites(nextSites)
    if (nextPos) setPosList(nextPos)
    if (nextKeys) setApiKeys(nextKeys)
  }

  const handleRenameCompany = async (companyId, name) => {
    if (!companyId || !name.trim()) return
    const res = await fetch(`${apiBase}/tenant/companies/${companyId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name: name.trim() })
    })
    if (!res.ok) return
    await refreshOrgTree()
    setEditCompany({ id: '', name: '' })
  }

  const handleRenameSite = async (siteId, name) => {
    if (!siteId || !name.trim()) return
    const res = await fetch(`${apiBase}/tenant/sites/${siteId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name: name.trim() })
    })
    if (!res.ok) return
    await refreshOrgTree()
    setEditSite({ id: '', name: '' })
  }

  const refreshAdministration = async () => {
    if (!authToken || !authUser) return
    if (authUser?.role === 'platform_admin') {
      await Promise.all([refreshTenantAdmins(), refreshResellerAdmins()])
      return
    }
    if (authUser?.role === 'reseller_admin') {
      await Promise.all([refreshResellerAdmins(), refreshResellerTenantAdmins(), refreshResellerImpersonationPermissions()])
      return
    }
    const tasks = [refreshOrgTree(), refreshPasskeys()]
    if (canManageUsers) tasks.push(refreshTenantUsers())
    if (authUser?.role === 'tenant_admin') tasks.push(refreshTenantImpersonationPermissions())
    await Promise.all(tasks)
  }

  const handleEditPosOpen = (pos) => {
    const tseType = normalizePosTseType(pos.tse_type)
    const tseSerialNumber = String(pos.tse_serial_number || '')
    setEditPos({
      id: pos.pos_id,
      posSerial: pos.pos_serial || '',
      posLabel: pos.pos_label || '',
      tseType,
      tseSerialNumber,
      status: '',
      fieldErrors: {}
    })
  }

  const resetEditPos = () => {
    setEditPos({
      id: '',
      posSerial: '',
      posLabel: '',
      tseType: 'swissbit_cloud_tse',
      tseSerialNumber: '',
      status: '',
      fieldErrors: {}
    })
  }

  const handleSavePosSettings = async () => {
    if (!editPos.id) return
    const swissbitCloudMode = isSwissbitCloudPosTseType(editPos.tseType)
    const fiskalyCloudMode = isFiskalyCloudPosTseType(editPos.tseType)
    if (fiskalyCloudMode) {
      setEditPos((prev) => ({ ...prev, status: 'fiskaly Cloud TSE ist aktuell nicht unterstützt.' }))
      return
    }
    const serialNumber = String(editPos.tseSerialNumber || '').trim()
    if (swissbitCloudMode && !serialNumber) {
      setEditPos((prev) => ({ ...prev, status: 'TSE Seriennummer ist erforderlich.', fieldErrors: { tseSerialNumber: true } }))
      return
    }
    const payload = {
      pos_label: editPos.posLabel || '',
      tse_type: normalizePosTseType(editPos.tseType),
      tse_serial_number: serialNumber
    }
    const res = await fetch(`${apiBase}/tenant/pos/${editPos.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      const msg = await parseError(res)
      setEditPos((prev) => ({ ...prev, status: msg || 'POS-Einstellungen konnten nicht gespeichert werden.' }))
      return
    }
    await refreshOrgTree()
    resetEditPos()
  }
  const handleDeleteCompany = async (companyId, companyName, confirmed = false) => {
    if (!companyId) return
    if (!confirmed) {
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.delete.company', { label: companyName }),
        confirmLabel: 'Unternehmen löschen',
        destructive: true,
        onConfirm: () => handleDeleteCompany(companyId, companyName, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/tenant/companies/${companyId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) return
    await refreshOrgTree()
  }

  const handleDeleteSite = async (siteId, siteName, confirmed = false) => {
    if (!siteId) return
    if (!confirmed) {
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.delete.site', { label: siteName }),
        confirmLabel: 'Betriebsstätte löschen',
        destructive: true,
        onConfirm: () => handleDeleteSite(siteId, siteName, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/tenant/sites/${siteId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) return
    await refreshOrgTree()
  }

  const handleDeletePos = async (posId, posName, confirmed = false) => {
    if (!posId) return
    if (!confirmed) {
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.delete.pos', { label: posName }),
        confirmLabel: 'POS löschen',
        destructive: true,
        onConfirm: () => handleDeletePos(posId, posName, true)
      })
      return
    }
    const res = await fetch(`${apiBase}/tenant/pos/${posId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) return
    await refreshOrgTree()
  }

  const handleUploadFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null
    setUploadError('')
    if (nextFile && Number(nextFile.size) > UPLOAD_FILE_LIMIT_BYTES) {
      setUploadFile(null)
      if (uploadFileInputRef.current) {
        uploadFileInputRef.current.value = ''
      }
      setUploadError(`Datei zu groß. Maximal ${UPLOAD_FILE_LIMIT_LABEL} pro Datei.`)
      return
    }
    setUploadFile(nextFile)
  }

  const handleUploadAddFile = async () => {
    const componentIsMounted = () => componentMountedRef.current
    const discardPreparedArtifact = async (artifactId) => {
      if (!artifactId) return
      try {
        const res = await fetch(`${apiBase}/web/artifacts/${artifactId}`, {
          method: 'DELETE',
          headers: authHeaders
        })
        if ((res.ok || res.status === 404) && activeUploadArtifactIdRef.current === artifactId) {
          activeUploadArtifactIdRef.current = ''
        }
      } catch {
        // Cleanup is best-effort when the upload itself has failed.
      }
    }
    setUploadError('')
    setUploadSuccess('')
    if (!uploadCompanyId) {
      setUploadError('Bitte ein Unternehmen wählen.')
      return
    }
    if (!uploadResolvedProfile?.profile_ref) {
      setUploadError('Für das Unternehmensland gibt es kein aktives Profil.')
      return
    }
    if (!uploadFile) {
      setUploadError('Bitte eine Datei auswählen.')
      return
    }
    if (Number(uploadFile.size) > UPLOAD_FILE_LIMIT_BYTES) {
      setUploadError(`Datei zu groß. Maximal ${UPLOAD_FILE_LIMIT_LABEL} pro Datei.`)
      return
    }
    if (!uploadFrom || !uploadTo) {
      setUploadError(TIME_RANGE_REQUIRED_ERROR)
      return
    }
    if (uploadTo < uploadFrom) {
      setUploadError('Zeitraum ungültig: "Bis" liegt vor "Von".')
      return
    }
    if (!hasRequiredArtifactDescription(uploadArtifactType, uploadCustomArtifactLabel)) {
      setUploadError('Bitte eine Bezeichnung für die benutzerdefinierte Artefaktgruppe angeben.')
      return
    }

    let preparedArtifactId = ''
    setUploadBusy(true)
    setUploadTransferProgress({ active: true, loaded: 0, total: Number(uploadFile.size) || 0, percent: 0 })
    try {
      const posNameByIdLocal = Object.fromEntries(
        uploadPosOptions.map((pos) => [pos.pos_id, pos.pos_label || pos.pos_serial])
      )
      const targetPosRef = uploadPosRefs.length ? uploadPosRefs[0] : ''
      const sha256Digest = await sha256DigestHex(uploadFile)
      if (!componentIsMounted()) return
      const fileMeta = {
        artifact_type: uploadArtifactType,
        business_time_from: uploadFrom,
        business_time_to: uploadTo,
        filename: uploadFile.name,
        sha256_digest: sha256Digest,
        company_id: uploadCompanyId
      }
      if (uploadSiteId) fileMeta.site_id = uploadSiteId
      if (uploadArtifactType === CUSTOM_ARTIFACT_TYPE) {
        fileMeta.description = customArtifactLabel(uploadArtifactType, uploadCustomArtifactLabel)
      }
      if (targetPosRef) fileMeta.pos_id = targetPosRef
      const prepareRes = await fetch(`${apiBase}/web/artifacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(fileMeta)
      })
      if (!prepareRes.ok) {
        throw new Error(await parseError(prepareRes))
      }
      const fileData = await prepareRes.json().catch(() => ({}))
      preparedArtifactId = String(fileData.artifact_id || '')
      activeUploadArtifactIdRef.current = preparedArtifactId
      if (!preparedArtifactId || !fileData.upload_url || !fileData.upload_headers) {
        throw new Error('Upload-Vorbereitung lieferte keine Upload-URL.')
      }
      if (!componentIsMounted()) {
        await discardPreparedArtifact(preparedArtifactId)
        return
      }
      await uploadBlobWithProgress({
        url: fileData.upload_url,
        method: 'PUT',
        headers: fileData.upload_headers || {},
        body: uploadFile,
        onProgress: ({ loaded, total, percent }) => {
          if (componentIsMounted()) {
            setUploadTransferProgress({ active: true, loaded, total, percent })
          }
        }
      })
      if (!componentIsMounted()) {
        await discardPreparedArtifact(preparedArtifactId)
        return
      }
      setUploadTransferProgress((prev) => ({
        ...prev,
        active: true,
        loaded: prev.total || prev.loaded,
        percent: 100
      }))
      const completedArtifactId = preparedArtifactId
      setUploadBundledFiles((prev) => [...prev, {
        fileId: completedArtifactId,
        filename: uploadFile.name,
        artifactType: uploadArtifactType,
        artifactLabel: customArtifactLabel(uploadArtifactType, uploadCustomArtifactLabel),
        siteLabel: uploadEffectiveSite?.name || '',
        posLabel: targetPosRef ? (posNameByIdLocal[targetPosRef] || targetPosRef) : '',
        from: uploadFrom,
        to: uploadTo
      }])
      activeUploadArtifactIdRef.current = ''
      preparedArtifactId = ''
      setUploadLastAddedFile(uploadFile.name)
      setUploadFile(null)
      if (uploadFileInputRef.current) {
        uploadFileInputRef.current.value = ''
      }
    } catch (err) {
      await discardPreparedArtifact(preparedArtifactId)
      if (componentIsMounted()) setUploadError(err?.message || 'Upload fehlgeschlagen.')
    } finally {
      if (preparedArtifactId && activeUploadArtifactIdRef.current === preparedArtifactId) {
        activeUploadArtifactIdRef.current = ''
      }
      if (componentIsMounted()) {
        setUploadTransferProgress({ active: false, loaded: 0, total: 0, percent: null })
        setUploadBusy(false)
      }
    }
  }

  const handleUploadRemoveFile = async (fileId, filename = 'Datei', confirmed = false) => {
    setUploadError('')
    setUploadSuccess('')
    if (!fileId) {
      setUploadError('Kein offener Upload.')
      return
    }
    if (!confirmed) {
      openConfirmationDialog({
        message: formatConfirmationTemplate('confirm.delete.upload_file', { label: filename }),
        confirmLabel: 'Datei löschen',
        destructive: true,
        onConfirm: () => handleUploadRemoveFile(fileId, filename, true)
      })
      return
    }
    setUploadBusy(true)
    try {
      const res = await fetch(`${apiBase}/web/artifacts/${fileId}`, {
        method: 'DELETE',
        headers: authHeaders
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      setUploadBundledFiles((prev) => {
        const next = prev.filter((f) => f.fileId !== fileId)
        setUploadLastAddedFile(next.length ? next[next.length - 1].filename : '')
        return next
      })
    } catch (err) {
      setUploadError(err?.message || 'Datei konnte nicht entfernt werden.')
    } finally {
      setUploadBusy(false)
    }
  }

  const handleUploadCommit = async () => {
    setUploadError('')
    setUploadSuccess('')
    if (!uploadBundledFiles.length) {
      setUploadError('Keine Dateien im offenen Upload.')
      return
    }
    const artifactIds = uploadBundledFiles.map((item) => item.fileId).filter(Boolean)
    if (!artifactIds.length) {
      setUploadError('Kein offener Upload. Erst Datei hinzufügen.')
      return
    }

    setUploadBusy(true)
    try {
      const commitRes = await fetch(`${apiBase}/web/artifacts/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          artifact_ids: artifactIds
        })
      })
      if (!commitRes.ok) {
        throw new Error(await parseError(commitRes))
      }
      const commitData = await commitRes.json()

      setUploadSuccess(formatUiTemplate('upload.committed', { id: commitData?.ingest_id || '' }))
      setUploadBundledFiles([])
      setUploadLastAddedFile('')
    } catch (err) {
      setUploadError(err?.message || 'Commit fehlgeschlagen.')
    } finally {
      setUploadBusy(false)
    }
  }

  const handleUploadResetBundle = async () => {
    setUploadError('')
    setUploadSuccess('')
    const artifactIds = uploadBundledFiles.map((item) => item.fileId).filter(Boolean)
    if (artifactIds.length) {
      setUploadBusy(true)
      try {
        for (const artifactId of artifactIds) {
          const res = await fetch(`${apiBase}/web/artifacts/${artifactId}`, {
            method: 'DELETE',
            headers: authHeaders
          })
          if (!res.ok) {
            throw new Error(await parseError(res))
          }
        }
      } catch (err) {
        setUploadError(err?.message || 'Upload konnte nicht verworfen werden.')
        setUploadBusy(false)
        return
      }
      setUploadBusy(false)
    }
    setUploadBundledFiles([])
    setUploadLastAddedFile('')
    setUploadFrom(defaultUploadRange.from)
    setUploadTo(defaultUploadRange.to)
    setUploadPickerOpen(false)
    setUploadPickerMonth(monthStart(new Date()))
    setUploadFile(null)
    setUploadTransferProgress({ active: false, loaded: 0, total: 0, percent: null })
    setUploadSiteId('')
    setUploadPosRefs([])
    if (uploadFileInputRef.current) {
      uploadFileInputRef.current.value = ''
    }
  }

  const handleUploadDiscardRequest = () => {
    if (!uploadBundledFiles.length) {
      void handleUploadResetBundle()
      return
    }
    openConfirmationDialog({
      title: 'Upload verwerfen?',
      message: 'Alle hochgeladenen Dateien und Auswahlen werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.',
      confirmLabel: 'Upload verwerfen',
      destructive: true,
      onConfirm: handleUploadResetBundle
    })
  }

  const handlePlayElefantAudio = () => {
    try {
      if (!elefantAudioRef.current) {
        elefantAudioRef.current = new Audio(elefantAudioUrl)
      }
      elefantAudioRef.current.currentTime = 0
      const playPromise = elefantAudioRef.current.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    } catch {
      // Optional audio feedback; ignore playback errors.
    }
  }

  if (authRestoring) {
    return (
      <div className="login-shell sb-scope">
        <div className="login-card">
          <h1 className="login-product-title">ELeFANT</h1>
          <div className="hint login-info">Sitzung wird geladen…</div>
          <img className="login-elefant-image" src={elefantImageUrl} alt="ELeFANT" />
        </div>
        <LegalBottomBar appVersion={appVersion} />
      </div>
    )
  }

  if (!authToken || !authUser) {
    if (isPasswordTokenRoute) {
      const showResetInvalidState = resetTokenValid === false && !resetTokenValidating
      return (
        <div className="login-shell sb-scope">
          <form className="login-card password-reset-card" noValidate onSubmit={handleResetPassword}>
            <h1 className="login-product-title">ELeFANT</h1>
            <h2 className="login-signin-caption">{resetRouteTitle}</h2>
            {!showResetInvalidState && (
              <div className="form-grid">
                <div>
                  <label>Neues Passwort</label>
                  <PasswordInput
                    value={resetPasswordNew}
                    onChange={(e) => {
                      setResetPasswordNew(e.target.value)
                      setResetPasswordFieldErrors((prev) => ({ ...prev, newPassword: false }))
                      setResetPasswordError('')
                    }}
                    disabled={resetTokenValidating || resetTokenValid === false}
                    invalid={resetPasswordFieldErrors.newPassword}
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label>Passwort wiederholen</label>
                  <PasswordInput
                    value={resetPasswordRepeat}
                    onChange={(e) => {
                      setResetPasswordRepeat(e.target.value)
                      setResetPasswordFieldErrors((prev) => ({ ...prev, repeatPassword: false }))
                      setResetPasswordError('')
                    }}
                    disabled={resetTokenValidating || resetTokenValid === false}
                    invalid={resetPasswordFieldErrors.repeatPassword}
                    autoComplete="new-password"
                  />
                </div>
                {isOnboardingPasswordFlow && (
                  <label className="terms-acceptance-check">
                    <input
                      type="checkbox"
                      required
                      checked={activationTermsAccepted}
                      onChange={(event) => {
                        setActivationTermsAccepted(event.target.checked)
                        setResetPasswordError('')
                      }}
                      disabled={resetTokenValidating || resetTokenValid === false || resetPasswordBusy}
                    />
                    <span>Ich akzeptiere die <a className="sb-link" href={termsUrl} target="_blank" rel="noreferrer">Geschäftsbedingungen</a> für den EleFANT-Dienst.</span>
                  </label>
                )}
              </div>
            )}
            {!showResetInvalidState && (
              <div className="form-validation-slot" aria-live="polite">
                {resetPasswordError && <div className="form-validation-message" role="alert">{resetPasswordError}</div>}
              </div>
            )}
            {showResetInvalidState && (
              <div className="hint reset-link-invalid-message" role="alert">
                {resetRouteInvalidMessage}
              </div>
            )}
            <div className="login-actions">
              {!showResetInvalidState && (
                <Button type="submit" disabled={resetPasswordBusy || resetTokenValidating || resetTokenValid === false || (isOnboardingPasswordFlow && !activationTermsAccepted)} variant="primary">
                  {resetTokenValidating ? resetRouteValidating : (resetPasswordBusy ? resetRouteSubmitBusy : resetRouteSubmitIdle)}
                </Button>
              )}
              <Button className="login-back-btn" onClick={() => { window.location.href = '/' }} variant="secondary">
                <span className="login-back-icon" aria-hidden="true">←</span>
                <span>Zurück zum Login</span>
              </Button>
            </div>
            {resetPasswordSuccess && <div className="hint login-info">{resetPasswordSuccess}</div>}
            <img className="login-elefant-image" src="/ELeFANT.png" alt="" />
          </form>
          <LegalBottomBar appVersion={appVersion} />
        </div>
      )
    }
    return (
      <div className="login-shell sb-scope">
        <form className="login-card" noValidate onSubmit={handleLogin}>
          <h1 className="login-product-title">ELeFANT</h1>
          <p className="login-product-expansion">
            Verwalten Sie hier alle Ihre Daten rund um die Kasse im Swissbit <br />
              <strong>
                Electronic Ledger for Auditability, Integrity and Traceability
              </strong>
          </p>
          <h2 className="login-signin-caption">Hier einloggen</h2>
          <div className="form-grid">
            <div>
              <label>E-Mail</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => {
                  setLoginEmail(e.target.value)
                  setLoginFieldErrors((prev) => ({ ...prev, email: false }))
                  setLoginError('')
                }}
                className={loginFieldErrors.email ? 'field-input-error' : ''}
                aria-invalid={loginFieldErrors.email || undefined}
              />
            </div>
            <div>
              <label>Passwort</label>
              <PasswordInput
                value={loginPassword}
                onChange={(e) => {
                  setLoginPassword(e.target.value)
                  setLoginFieldErrors((prev) => ({ ...prev, password: false }))
                  setLoginError('')
                }}
                invalid={loginFieldErrors.password}
                autoComplete="current-password"
              />
            </div>
          </div>
          <div className="form-validation-slot" aria-live="polite">
            {loginError && <div className="form-validation-message" role="alert">{loginError}</div>}
          </div>
          <div className="login-actions">
            <Button type="submit" disabled={loginBusy || passkeyLoginBusy || forgotPasswordBusy} variant="primary">
              {loginBusy ? 'Anmeldung…' : 'Einloggen'}
            </Button>
            <button
              className="login-forgot-link"
              type="button"
              onClick={handleForgotPassword}
              disabled={loginBusy || passkeyLoginBusy || forgotPasswordBusy}
            >
              {forgotPasswordBusy ? 'Sende Reset-Link…' : 'Passwort vergessen?'}
            </button>
            <Button
              className="passkey-login-btn"
              disabled={loginBusy || passkeyLoginBusy || forgotPasswordBusy || !passkeySupported}
              onClick={handleLoginWithPasskey}
              title={passkeySupported ? 'Mit Passkey anmelden' : 'Passkey nicht unterstützt'} variant="secondary">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 2a3 3 0 0 1 3 3v4h1.5A2.5 2.5 0 0 1 22 11.5v7a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 8 18.5v-7A2.5 2.5 0 0 1 10.5 9H12V5a3 3 0 0 1 3-3zm0 2a1 1 0 0 0-1 1v4h2V5a1 1 0 0 0-1-1zM5 12a3 3 0 1 0 0 6v-2a1 1 0 0 1 0-2v-2z" />
              </svg>
              <span>{passkeyLoginBusy ? 'Warte auf Passkey…' : 'Mit Passkey anmelden'}</span>
            </Button>
          </div>
          {loginInfo && <div className="hint login-info">{loginInfo}</div>}
          <button className="login-elefant-sound-btn" type="button" onClick={handlePlayElefantAudio} aria-label="ELeFANT Sound abspielen">
            <img className="login-elefant-image" src={elefantImageUrl} alt="ELeFANT" />
          </button>
        </form>
        <LegalBottomBar appVersion={appVersion} />
      </div>
    )
  }

  return (
    <div className="app-shell sb-scope">
      {mobileNavOpen && <button className="mobile-nav-backdrop" type="button" onClick={() => setMobileNavOpen(false)} aria-label={'Navigation schließen'} />}

      <aside className={`sidebar sb-inverse${mobileNavOpen ? ' is-open' : ''}`}>
        <div className="brand">
          <div className="brand-logo-square">
            <img className="brand-logo-square-img" src={elefantWhiteImageUrl} alt="ELeFANT" />
          </div>
          <div className="brand-text">
            <div className="brand-title">ELeFANT</div>
            <div className="brand-subtitle">Electronic Ledger For Auditability, iNtegrity and Traceability</div>
          </div>
          <Button
            className="mobile-nav-close"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Navigation schließen" variant="primary">
            <span aria-hidden="true">×</span>
          </Button>
        </div>
        <nav className="nav">
          {navItems.map((item) => (
            <button
              key={item.label}
              className={`nav-item${active === item.label ? ' is-active' : ''}`}
              type="button"
              onClick={() => {
                if (item.label === 'API-Keys') refreshApiKeys()
                if (item.label === 'Administration') refreshAdministration()
                if (item.label === 'Unternehmensstruktur') refreshOrgTree()
                setActive(item.label)
                setMobileNavOpen(false)
              }}
            >
              <span className="nav-item-label">
                <SymbolIcon icon={item.icon} className="nav-item-icon" />
                <span>{item.label}</span>
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="main">
        <header className={`topbar${showTopbarOrg ? '' : ' is-org-hidden'}`}>
          <Button
            className={`mobile-nav-toggle${mobileNavOpen ? ' is-open' : ''}`}
            onClick={() => setMobileNavOpen((prev) => !prev)}
            aria-label={mobileNavOpen ? 'Navigation schließen' : 'Navigation öffnen'}
            aria-expanded={mobileNavOpen}
            variant="primary"
          >
            <SymbolIcon icon="menu" className="mobile-nav-toggle-icon" />
          </Button>
          {pendingImpersonationPermissionCount > 0 && !impersonationNotificationDismissed && (
            <div className="topbar-notification-chip">
              <span className="topbar-notification-chip-info" aria-hidden="true">ⓘ</span>
              <div className="topbar-notification-chip-title">
                <span>Anfrage ausstehend</span>
              </div>
              <div className="topbar-notification-chip-detail" data-i18n-skip="1">
                <span>
                  {pendingImpersonationPermissionCount === 1
                    ? formatUiTemplate('impersonation.request.pending_from_prefix')
                    : formatUiTemplate('impersonation.request.pending_count_from_prefix', { count: pendingImpersonationPermissionCount })}
                </span>
                <strong>{pendingImpersonationRequesterNames.join(', ')}</strong>
                <span>.</span>
              </div>
              <button
                className="topbar-notification-chip-close"
                type="button"
                onClick={dismissImpersonationNotification}
                aria-label="Benachrichtigung schließen"
                title="Benachrichtigung schließen"
              >
                <SymbolIcon icon="close" />
              </button>
            </div>
          )}
          {showTopbarOrg && (
            <div className="context">
              <div className="context-label">{'Organisation'}</div>
              <div className="context-value" data-i18n-skip="1">{tenantDisplayName}</div>
            </div>
          )}
          <div className="context user">
            <div className="context-label">{topbarUserLabel}</div>
            <div className="context-value user-email">
              {showTopbarUserName && userDisplayName && (
                <span className="user-name-inline" data-i18n-skip="1">{userDisplayName} · </span>
              )}
              <span data-i18n-skip="1">{authUser.email}</span>
              {showTopbarRole && <span>{` · ${roleDisplayLabel || '-'}`}</span>}
            </div>
          </div>
          <div className="user-actions">
            {authUser?.impersonation?.active && (
              <Button className="user-impersonation" onClick={handleStopImpersonation} variant="primary">
                <SymbolIcon icon="impersonation" className="topbar-action-icon" />
                <span>{'Impersonation beenden'}</span>
              </Button>
            )}
            <Button className="user-logout" onClick={handleLogout} variant="secondary">
              <SymbolIcon icon="logout" className="topbar-action-icon logout-icon" />
              <span className="logout-label">{'Logout'}</span>
            </Button>
          </div>
        </header>

        <main className="content">
          {active === 'Web-Upload' && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">Web-Upload</div>
                  <h1 className="sb-h1 page-title"><SymbolIcon icon="upload" />Daten archivieren</h1>
                </div>
              </section>

              <section className="grid">
                <div className="card upload-summary-card" style={{ order: 2 }}>
                  <div className="card-title">Zusammenfassung</div>
                  <div className="upload-summary-panel">
                    <div className="list">
                      <div className="list-row"><span>Länderkürzel</span><span>{uploadDateLocked ? (uploadCompany?.country || '-') : '...'}</span></div>
                      <div className="list-row"><span>Unternehmen</span><span>{uploadDateLocked ? (uploadCompany?.name || '-') : '...'}</span></div>
                      <div className="list-row"><span>Betriebsstätte</span><span>{uploadDateLocked ? (uploadEffectiveSite?.name || 'Unternehmensweit') : '...'}</span></div>
                      <div className="list-row">
                        <span>POS Scope</span>
                        <span>
                          {uploadDateLocked
                            ? (uploadPosRefs.length
                              ? formatUiTemplate('upload.pos_selected_count', { count: uploadPosRefs.length })
                              : (uploadSiteId ? 'Betriebsstättenweit' : 'Unternehmensweit'))
                            : '...'}
                        </span>
                      </div>
                      <div className="list-row">
                        <span>Zeitraum</span>
                        <span>{uploadDateLocked ? `${formatDateDisplay(uploadFrom)} - ${formatDateDisplay(uploadTo)}` : '...'}</span>
                      </div>
                      <div className="list-row"><span>Anzahl Elemente</span><span>{uploadBundledFiles.length}</span></div>
                    </div>
                  </div>
                  <div className="upload-summary-actions">
                    <Button
                      className="action-btn"
                      onClick={handleUploadCommit}
                      disabled={uploadBusy || !uploadContextReady} variant="primary">
                      <SymbolIcon icon="inventory_2" className="sb-btn-icon" />
                      {uploadBusy ? 'Lädt…' : 'Verbindlich archivieren'}
                    </Button>
                    <Button className="action-btn" onClick={handleUploadDiscardRequest} disabled={uploadBusy || !uploadContextReady} variant="secondary" tone="destructive">
                      <SymbolIcon icon="delete" className="sb-btn-icon" />
                      Upload verwerfen
                    </Button>
                  </div>
                  {!uploadContextReady && (
                    <div className="hint">Upload deaktiviert: Unternehmen mit gültigem Länderprofil auswählen.</div>
                  )}
                  {uploadBundledFiles.length > 0 && (
                    <div className="upload-file-list-section">
                      <div className="hint upload-file-list-title">Dateien zur Archivierung</div>
                      <div className="table upload-file-table">
                        <div className="table-header">
                          <span>Datei</span><span>Typ</span><span>Aktion</span>
                        </div>
                        {uploadBundledFiles.map((f) => (
                          <div className="table-row" key={f.fileId || `${f.filename}-${f.from}`}>
                            <span data-i18n-skip="1">{f.filename}</span>
                            <span className="upload-file-type">
                              <span className="upload-file-type-primary">
                                <span data-i18n-live="1">{getArtifactTypeLabel(f.artifactType, uploadCompany?.country || '')}</span>
                                {f.artifactLabel && (
                                  <>
                                    {' ('}<span data-i18n-skip="1">{f.artifactLabel}</span>{')'}
                                  </>
                                )}
                              </span>
                              <span className="upload-file-type-classifier">
                                {f.siteLabel
                                  ? <span data-i18n-skip="1">{f.siteLabel}</span>
                                  : <span data-i18n-live="1">Unternehmensweit</span>}
                                {f.posLabel && <><span>, </span><span data-i18n-skip="1">{f.posLabel}</span></>}
                              </span>
                            </span>
                            <span>
                              <IconButton
                                icon="delete"
                                label="Entfernen"
                                ariaLabel="Datei entfernen"
                                tone="danger"
                                disabled={uploadBusy}
                                onClick={() => handleUploadRemoveFile(f.fileId, f.filename)}
                              />
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {uploadError && <div className="hint form-error-hint" role="alert">{uploadError}</div>}
                </div>

                <div className={`card upload-details-card${uploadTransferActive ? ' upload-dialog-blocked' : ''}`} style={{ order: 1 }} aria-busy={uploadTransferActive}>
                  <div className="card-title">Upload-Details</div>
                  <div className="form-grid">
                    <div className="upload-context-zone">
                      <div className={`upload-context-item${uploadCompanyMissingError ? ' has-error' : ''}`}>
                        <label>Unternehmen</label>
                        {companies.length >= UI_SEARCH_THRESHOLD && (
                          <ClearableInput
                            type="search"
                            className="upload-company-search"
                            inputClassName="multi-select-search"
                            placeholder="Filter..."
                            value={uploadCompanySearchTerm}
                            onValueChange={setUploadCompanySearchTerm}
                            disabled={uploadDateLocked || uploadDialogBlocked}
                          />
                        )}
                        <select
                          className={`upload-context-select is-${uploadCompanyFieldState}`}
                          value={uploadCompanySelectionVisible ? uploadCompanyId : ''}
                          onChange={(e) => {
                            setUploadCompanyId(e.target.value)
                            setUploadSiteId('')
                            setUploadPosRefs([])
                          }}
                          disabled={uploadDateLocked || uploadDialogBlocked}
                        >
                          <option value="">Unternehmen wählen</option>
                          {uploadCompanySelectOptions.map((c) => (
                            <option key={c.company_id} value={c.company_id} data-i18n-skip="1">
                              {c.name} ({c.country || '-'})
                            </option>
                          ))}
                        </select>
                        {!uploadCompanyId && <div className="hint form-error-hint upload-company-required-hint">Aktion erforderlich: Unternehmen wählen.</div>}
                      </div>
                      <div className="upload-context-item">
                        <label>Betriebsstätte (optional)</label>
                        <select
                          className={`upload-context-select is-${uploadContextFieldState}`}
                          value={uploadSiteId}
                          onChange={(e) => {
                            setUploadSiteId(e.target.value)
                            setUploadPosRefs([])
                          }}
                          disabled={uploadDateLocked || !uploadContextReady || uploadDialogBlocked}
                        >
                          <option value="">Unternehmensweit</option>
                          {uploadSiteOptions.map((site) => (
                            <option key={site.site_id} value={site.site_id} data-i18n-skip="1">{site.name}</option>
                          ))}
                        </select>
                        <div className="hint">Ohne Betriebsstätten-Auswahl stehen alle POS des Unternehmens zur Auswahl.</div>
                      </div>
                      <div className="upload-context-item">
                        <label>POS (optional)</label>
                        <CheckboxMultiSelect
                          options={uploadPosOptions.map((pos) => ({
                            value: pos.pos_id,
                            label: formatScopedPosLabel(pos, sites, !uploadSiteId)
                          }))}
                          value={uploadPosRefs}
                          onChange={(selected) => setUploadPosRefs(selected.slice(-1))}
                          disabled={uploadDateLocked || !uploadContextReady || uploadDialogBlocked}
                          readOnly={uploadDateLocked && uploadContextReady && !uploadDialogBlocked}
                          emptyLabel={uploadContextReady ? 'Keine POS verfügbar' : 'Keine Daten'}
                          searchable
                          searchThreshold={UI_SEARCH_THRESHOLD}
                          visibleRows={UI_MULTISELECT_VISIBLE_ROWS}
                        />
                        <div className="hint">Ohne POS-Auswahl gilt der Upload für das gesamte Unternehmen oder, falls eine Betriebsstätte ausgewählt ist, für diese Betriebsstätte. Es kann maximal eine POS ausgewählt werden.</div>
                      </div>
                      <div className={`upload-context-item${uploadRangeMissingError ? ' has-error' : ''}`}>
                        <label>Zeitraum</label>
                        <div className={`date-range-wrap${uploadRangeMissingError ? ' has-error' : ''}`}>
                          <DateRangeField
                            start={uploadFrom}
                            end={uploadTo}
                            formatDate={formatDateDisplay}
                            state={uploadContextFieldState}
                            onClick={() => setUploadPickerOpen((open) => !open)}
                          />
                          {uploadPickerOpen && uploadContextReady && !uploadDateLocked && !uploadDialogBlocked && (
                            <div className="date-range-popover">
                              <div className="date-range-head">
                                <div className="date-range-nav-group">
                                  <button className="date-range-nav-btn" type="button" onClick={() => handleUploadPickerYear(-1)} aria-label="Ein Jahr zurück">«</button>
                                  <button className="date-range-nav-btn" type="button" onClick={() => handleUploadPickerMonth(-1)} aria-label="Ein Monat zurück">‹</button>
                                </div>
                                <div className="date-range-head-label">{uploadPickerMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</div>
                                <div className="date-range-nav-group">
                                  <button className="date-range-nav-btn" type="button" onClick={() => handleUploadPickerMonth(1)} aria-label="Ein Monat vor">›</button>
                                  <button className="date-range-nav-btn" type="button" onClick={() => handleUploadPickerYear(1)} aria-label="Ein Jahr vor">»</button>
                                </div>
                              </div>
                              <div className="date-range-weekdays">
                                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
                                  <span key={d}>{d}</span>
                                ))}
                              </div>
                              <div className="date-range-grid">
                                {uploadCalendarCells.map((cell, idx) => {
                                  const inRange = cell && uploadFrom && uploadTo && cell >= uploadFrom && cell <= uploadTo
                                  const isStart = cell && uploadFrom === cell
                                  const isEnd = cell && uploadTo === cell
                                  return (
                                    <button
                                      key={`${cell || 'empty'}-${idx}`}
                                      className={`date-cell${cell ? '' : ' is-empty'}${inRange ? ' is-in-range' : ''}${isStart ? ' is-start' : ''}${isEnd ? ' is-end' : ''}`}
                                      type="button"
                                      disabled={!cell}
                                      onClick={() => handleUploadDatePick(cell)}
                                    >
                                      {cell ? Number(cell.slice(8, 10)) : ''}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="hint">Archivierte Elemente werden in diesem Zeitraum beim Export exportiert.</div>
                      </div>
                      {uploadDateLocked && (
                        <div className="notice-hint upload-locked-details-hint" role="note">
                          <SymbolIcon icon="lock" className="notice-hint-icon" />
                          <span>Diese Details sind für diesen Upload eingefroren. Verwerfen Sie den Upload, um sie wieder zu ändern.</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label>Artefaktgruppe</label>
                      <select
                        value={uploadArtifactType}
                        onChange={(e) => setUploadArtifactType(e.target.value)}
                        disabled={!uploadContextReady || uploadDialogBlocked}
                      >
                        {!uploadContextReady ? (
                          <option value="">Kein aktives Länderprofil</option>
                        ) : (
                          <>
                            <option value="tse_tar">{getArtifactTypeLabel('tse_tar', uploadCompany?.country || '')}</option>
                            <option value="dsfinvk">{getArtifactTypeLabel('dsfinvk', uploadCompany?.country || '')}</option>
                            <option value="procedure_doc">{getArtifactTypeLabel('procedure_doc', uploadCompany?.country || '')}</option>
                            <option value="cash_register_data">{getArtifactTypeLabel('cash_register_data', uploadCompany?.country || '')}</option>
                            <option value={CUSTOM_ARTIFACT_TYPE}>{getArtifactTypeLabel(CUSTOM_ARTIFACT_TYPE, uploadCompany?.country || '')}</option>
                          </>
                        )}
                      </select>
                      {uploadArtifactType === CUSTOM_ARTIFACT_TYPE && (
                        <div className="upload-custom-artifact-hint-wrap">
                          <div className="notice-hint upload-custom-artifact-hint" role="note">
                            <SymbolIcon icon="info" className="notice-hint-icon" />
                            <span>„Benutzerdefiniert“ ist nur für Ausnahmefälle vorgesehen, in denen das Artefakt keiner vorhandenen Gruppe zugeordnet werden kann. Wenden Sie sich bei wiederkehrendem Bedarf an Ihren zuständigen Supportkontakt.</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {uploadArtifactType === CUSTOM_ARTIFACT_TYPE && (
                      <div>
                        <label>Bezeichnung des benutzerdefinierten Typs</label>
                        <input
                          type="text"
                          placeholder="z. B. TSE Zertifikat"
                          value={uploadCustomArtifactLabel}
                          onChange={(e) => setUploadCustomArtifactLabel(e.target.value)}
                          disabled={!uploadContextReady || uploadDialogBlocked}
                        />
                      </div>
                    )}
                    <div>
                      <label>Datei</label>
                      <input
                        ref={uploadFileInputRef}
                        type="file"
                        onChange={handleUploadFileChange}
                        disabled={!uploadContextReady || uploadDialogBlocked}
                      />
                      <div className="hint">Mehrere Dateien können in einen offenen Upload gebündelt werden.</div>
                      <Button
                        className="upload-add-btn action-btn"
                        onClick={handleUploadAddFile}
                        disabled={uploadBusy || !uploadFile || !uploadContextReady} variant="primary">
                        <SymbolIcon icon="upload_file" className="sb-btn-icon" />
                        {uploadTransferActive
                          ? (uploadTransferPercent == null ? 'Upload läuft…' : formatUiTemplate('upload.transfer.progress', { percent: uploadTransferPercent }))
                          : (uploadBusy ? 'Lädt…' : 'Datei hinzufügen')}
                      </Button>
                      {uploadTransferActive && (
                        <div className="upload-progress-panel" role="status" aria-live="polite">
                          <div className="upload-progress-copy">{uploadTransferStatusText}</div>
                          <div className={`upload-progress-bar${uploadTransferPercent == null ? ' is-indeterminate' : ''}`}>
                            <span style={uploadTransferPercent == null ? undefined : { width: `${uploadTransferPercent}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}

          {active === 'Prüfexport' && canRunExports && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">Prüfexport</div>
                  <h1 className="sb-h1 page-title"><SymbolIcon icon="export" />Prüfexport</h1>
                </div>
                {activeExport && (
                  <div className="hero-card">
                    <div className="hero-title">Aktueller Export</div>
                    <div className="hero-metric" data-i18n-skip="1">{activeExport.export_id}</div>
                    <div className="hero-sub">{formatUiTemplate('status.value', { value: exportStatusLabel(activeExport.status) })}</div>
                  </div>
                )}
              </section>

              <section className="grid">
                <div className="card export-filter-card">
                  <div className="card-title">Filter</div>
                  <div className="hint">In diesem Dialog können Sie Kriterien auswählen, was im Export enthalten sein soll.</div>
                  <div className="notice-hint export-global-scope-hint" role="note">
                    <SymbolIcon icon="info" className="notice-hint-icon" />
                    <span>Unternehmensweite Artefakte werden unabhängig von Betriebsstätten- oder POS-Auswahl immer berücksichtigt.</span>
                  </div>
                  <div className="form-grid">
                    <div className="export-filter-settings">
                      <label className="check-item">
                        <span>Gelöschte Einträge zum Filtern anbieten</span>
                        <input
                          type="checkbox"
                          checked={exportIncludeDeleted}
                          onChange={(e) => {
                            const next = e.target.checked
                            setExportIncludeDeleted(next)
                            setExportFilterApplied(false)
                            setExportPreview(null)
                            setExportSelectedArtifactTypes([])
                          }}
                        />
                      </label>
                    </div>
                    <div className="export-filter-section">
                      <MultiSelectFieldHeader
                        label="Unternehmen"
                        options={exportCompanyOptions.map((company) => ({ value: company.company_id }))}
                        value={exportCompanyIds}
                      />
                      <CheckboxMultiSelect
                        options={exportCompanyOptions.map((c) => ({ value: c.company_id, label: c.name }))}
                        value={exportCompanyIds}
                        onChange={(selected) => {
                          setExportCompanyIds(selected)
                          setExportSiteIds([])
                          setExportPosIds([])
                          setExportFilterApplied(false)
                          setExportPreview(null)
                        }}
                        emptyLabel="Keine Unternehmen verfügbar"
                        searchable
                        searchThreshold={UI_SEARCH_THRESHOLD}
                        visibleRows={UI_MULTISELECT_VISIBLE_ROWS}
                      />
                      {!exportCompanyOptions.length && (
                        <button
                          className="link sb-link export-empty-action"
                          type="button"
                          onClick={handleNavigateToCompanyStructure}
                        >
                          (+ Unternehmen in der Unternehmensstruktur hinzufügen)
                        </button>
                      )}
                      {exportCompanyIds.length > 1 && (
                        <div className="hint">Weitere Filterung nach Betriebsstätte oder POS ist bei Auswahl mehrerer Unternehmen nicht möglich.</div>
                      )}
                    </div>
                    <div className="export-filter-section">
                      <MultiSelectFieldHeader
                        label="Betriebsstätten (optional)"
                        options={exportSiteOptions.map((site) => ({ value: site.site_id }))}
                        value={exportSiteIds}
                      />
                      <CheckboxMultiSelect
                        options={exportSiteOptions.map((site) => ({ value: site.site_id, label: site.name }))}
                        value={exportSiteIds}
                        onChange={(selected) => {
                          setExportSiteIds(selected)
                          setExportPosIds([])
                          setExportFilterApplied(false)
                          setExportPreview(null)
                        }}
                        disabled={exportCompanyIds.length !== 1}
                        emptyLabel={exportCompanyIds.length === 1 ? 'Keine Betriebsstätten verfügbar' : 'Keine Daten'}
                        searchable
                        searchThreshold={UI_SEARCH_THRESHOLD}
                        visibleRows={UI_MULTISELECT_VISIBLE_ROWS}
                      />
                      <div className="hint export-scope-hint">Bei Auswahl von Betriebsstätten werden nur deren POS angeboten. Ohne Auswahl werden alle POS des Unternehmens angeboten.</div>
                    </div>
                    <div className="export-filter-section">
                      <MultiSelectFieldHeader
                        label="POS (optional)"
                        options={exportPosOptions.map((pos) => ({ value: pos.pos_id }))}
                        value={exportPosIds}
                      />
                      <CheckboxMultiSelect
                        options={exportPosOptions.map((p) => ({ value: p.pos_id, label: p.name }))}
                        value={exportPosIds}
                        onChange={(selected) => {
                          setExportPosIds(selected)
                          setExportFilterApplied(false)
                          setExportPreview(null)
                        }}
                        disabled={exportCompanyIds.length !== 1}
                        emptyLabel={exportCompanyIds.length === 1 ? 'Keine POS verfügbar' : 'Keine Daten'}
                        searchable
                        searchThreshold={UI_SEARCH_THRESHOLD}
                        visibleRows={UI_MULTISELECT_VISIBLE_ROWS}
                      />
                      <div className="hint export-scope-hint">Bei POS-Auswahl werden betriebsstättenweite Artefakte der zugehörigen Betriebsstätten sowie unternehmensweite Artefakte berücksichtigt.</div>
                    </div>
                    <div className="export-filter-section">
                      <label>Zeitraum</label>
                      <div className={`date-range-wrap${exportRangeMissingError ? ' has-error' : ''}`}>
                        <DateRangeField
                          start={exportRangeStart}
                          end={exportRangeEnd}
                          formatDate={formatDateDisplay}
                          onClick={() => setExportPickerOpen((open) => !open)}
                        />
                        {exportPickerOpen && (
                          <div className="date-range-popover">
                            <div className="date-range-head">
                              <div className="date-range-nav-group">
                                <button className="date-range-nav-btn" type="button" onClick={() => handleExportPickerYear(-1)} aria-label="Ein Jahr zurück">«</button>
                                <button className="date-range-nav-btn" type="button" onClick={() => handleExportPickerMonth(-1)} aria-label="Ein Monat zurück">‹</button>
                              </div>
                              <div className="date-range-head-label">{exportPickerMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</div>
                              <div className="date-range-nav-group">
                                <button className="date-range-nav-btn" type="button" onClick={() => handleExportPickerMonth(1)} aria-label="Ein Monat vor">›</button>
                                <button className="date-range-nav-btn" type="button" onClick={() => handleExportPickerYear(1)} aria-label="Ein Jahr vor">»</button>
                              </div>
                            </div>
                            <div className="date-range-weekdays">
                              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
                                <span key={d}>{d}</span>
                              ))}
                            </div>
                            <div className="date-range-grid">
                              {exportCalendarCells.map((cell, idx) => {
                                const inRange = cell && exportRangeStart && exportRangeEnd && cell >= exportRangeStart && cell <= exportRangeEnd
                                const isStart = cell && exportRangeStart === cell
                                const isEnd = cell && exportRangeEnd === cell
                                return (
                                  <button
                                    key={`${cell || 'empty'}-${idx}`}
                                    className={`date-cell${cell ? '' : ' is-empty'}${inRange ? ' is-in-range' : ''}${isStart ? ' is-start' : ''}${isEnd ? ' is-end' : ''}`}
                                    type="button"
                                    disabled={!cell}
                                    onClick={() => handleExportDatePick(cell)}
                                  >
                                    {cell ? Number(cell.slice(8, 10)) : ''}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {exportError && <div className="hint form-error-hint export-filter-error">{exportError}</div>}
                  <div className="export-filter-actions">
                    <Button className="export-filter-btn action-btn" onClick={runExportFilter} variant="primary"><SymbolIcon icon="filter" className="sb-btn-icon" />Filter anwenden</Button>
                    <Button className="export-filter-btn action-btn" onClick={resetExportFilters} variant="secondary"><SymbolIcon icon="refresh" className="sb-btn-icon" />Filter zurücksetzen</Button>
                  </div>
                </div>

                <div className="card export-preview-card">
                  <div className="card-title">Preview</div>
                  {!exportFilterApplied && <div className="hint">Preview wird nach Klick auf „Filter anwenden“ angezeigt.</div>}
                  {exportFilterApplied && (
                    <>
                      <div>
                        <label>Kategorien</label>
                        <CheckboxMultiSelect
                          options={exportCategoryOptions}
                          value={exportSelectedArtifactTypes}
                          onChange={(selected) => setExportSelectedArtifactTypes(selected)}
                          emptyLabel="Keine Kategorien verfügbar"
                        />
                      </div>
                      <div className="hint">
                        {formatUiTemplate('export.preview.selected_of_total', {
                          selected: exportSelectedCountTotal,
                          total: exportPreview?.total || 0
                        })}
                      </div>
                      {(!exportPreview || exportPreview.total === 0) && <div className="hint">Keine Treffer für die gewählten Filter</div>}
                    </>
                  )}
                  <div className="export-actions-footer">
                    <div className="export-mail-placeholder">
                      <label className="check-item export-mail-toggle">
                        <input
                          type="checkbox"
                          checked={exportAuditorEmailEnabled}
                          onChange={(event) => {
                            setExportAuditorEmailEnabled(event.target.checked)
                            setExportAuditorEmailError('')
                          }}
                        />
                        <span>Export per E-Mail senden (optional, Prüfer/Revision)</span>
                      </label>
                      {exportAuditorEmailEnabled && (
                        <ClearableInput
                          type="email"
                          clearLabel="E-Mail zurücksetzen"
                          placeholder="pruefer@beispiel.de"
                          value={exportAuditorEmail}
                          onValueChange={(value) => {
                            setExportAuditorEmail(value)
                            setExportAuditorEmailError('')
                          }}
                          inputClassName={exportAuditorEmailError ? 'field-input-error' : ''}
                        />
                      )}
                      {exportAuditorEmailEnabled && exportAuditorEmailError && (
                        <div className="hint form-error-hint" role="alert">{exportAuditorEmailError}</div>
                      )}
                      <div className="notice-hint export-retention-hint" role="note">
                        <SymbolIcon icon="info" className="notice-hint-icon" />
                        <span>Der Export steht zum Download für 7 Tage bereit.</span>
                      </div>
                    </div>
                    <Button
                      className="export-run-btn"
                      onClick={handleRunExport}
                      disabled={exportBusy || !exportFilterApplied || !exportPreview || exportSelectedCountTotal === 0} variant="primary">
                      <SymbolIcon icon="export" className="sb-btn-icon" />
                      {exportBusy ? 'Läuft…' : 'Export durchführen'}
                    </Button>
                  </div>
                </div>
              </section>

              <section className="card">
                <div className="card-title">Letzte Exporte</div>
                <div className="table export-table">
                  <div className="table-header">
                    <span>Erstellt am</span><span>Status</span><span>Zeitraum</span><span>Aktionen</span>
                  </div>
                  {latestExports.map((item) => {
                    const status = String(item.status || '').toLowerCase()
                    const isCompleted = status === 'completed'
                    const isExpired = status === 'expired'
                    return (
                    <div className="table-row" key={item.export_id} title={exportTooltipText(item)}>
                      <span>{formatDateTimeDisplay(item.created_at)}</span>
                      <span><StatusBadge variant="compact" tone={exportStatusTone(status)}>{exportStatusLabel(item.status)}</StatusBadge></span>
                      <span>
                        {item.business_time_from && item.business_time_to
                          ? `${formatDateDisplay(item.business_time_from)} - ${formatDateDisplay(item.business_time_to)}`
                          : '-'}
                      </span>
                      <span className="export-download-action">
                        {isCompleted ? (
                          <IconButton
                            icon="download"
                            label="Export herunterladen"
                            onClick={() => handleExportDownload(item.export_id)}
                          />
                        ) : isExpired ? (
                          <IconButton icon="download" label="Link abgelaufen" disabled />
                        ) : null}
                      </span>
                    </div>
                    )
                  })}
                </div>
                {!latestExports.length && <div className="hint empty-list-hint">Keine Exporte verfügbar</div>}
              </section>
            </>
          )}

          {active === 'API-Keys' && authUser?.role !== 'platform_admin' && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">API-Keys</div>
                  <h1 className="sb-h1 page-title"><SymbolIcon icon="key" />API-Key Verwaltung</h1>
                </div>
              </section>

              <section className="card">
                <div className="card-title">{'API-Key Verwaltung'}</div>
                {canManageApiKeys && (
                  <div className="onboarding-code-panel">
                    <div className="onboarding-code-header">
                      <div>
                        <div className="onboarding-code-title">{'Onboarding-Code erzeugen'}</div>
                        <div className="hint onboarding-code-description">
                          <p>{'Ein Onboarding-Code erlaubt es einer oder mehreren Kassen, sich automatisch zu registrieren und jeweils einen eigenen API-Key anzufordern. Ein API-Key ist genau einer Kasse zugeordnet. Er ist das Geheimnis, mit dem sich die Kasse gegenüber dem Service identifiziert, und darf nicht zwischen mehreren Kassen geteilt werden. Der Onboarding-Code darf dagegen von mehreren Kassen verwendet werden. Jeder angeforderte API-Key muss anschließend in der API-Key-Verwaltung freigegeben werden.'}</p>
                          <p>{'Onboarding-Codes sind zeitlich unbegrenzt gültig und werden im Portal nur einmal angezeigt. Wenn Sie einen Code verlieren, erzeugen Sie einen neuen; bereits erzeugte Codes bleiben weiterhin gültig.'}</p>
                        </div>
                      </div>
                      <div className="inline-actions onboarding-code-actions">
                        <Button className="onboarding-code-button" onClick={handleOnboardingCodeGenerationRequest} disabled={apiKeyOnboardingCode.busy} variant="primary">
                          <SymbolIcon icon="wand_stars" className="sb-btn-icon" />
                          {apiKeyOnboardingCode.busy ? 'Erzeugt…' : (apiKeyOnboardingCode.code ? 'Weiteren Onboarding-Code erzeugen' : 'Onboarding-Code erzeugen')}
                        </Button>
                      </div>
                    </div>
                    {apiKeyOnboardingCode.error && <div className="hint">{apiKeyOnboardingCode.error}</div>}
                    {apiKeyOnboardingCode.code && (
                      <>
                        <div className="table onboarding-code-table" aria-label={'Onboarding-Code'}>
                          <div className="table-header">
                            <span>{'Onboarding-Code'}</span>
                          </div>
                          <div className="table-row">
                            <span className="onboarding-code-cell">
                              <span className="onboarding-code-value">{apiKeyOnboardingCode.code}</span>
                              {apiKeyOnboardingCode.copied ? (
                                <span className="code-copy-confirmation">
                                  <SymbolIcon icon="approve" />
                                </span>
                              ) : (
                                <IconButton
                                  icon="copy"
                                  label="Kopieren"
                                  ariaLabel="Onboarding-Code kopieren"
                                  onClick={handleCopyOnboardingCode}
                                  variant="quiet"
                                  className="code-copy-button"
                                />
                              )}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="archive-search">
                  <ClearableInput
                    type="search"
                    placeholder={'Suche (POS, Unternehmen, Betriebsstätte, Status)'}
                    value={apiKeysSearchTerm}
                    onValueChange={setApiKeysSearchTerm}
                  />
                </div>
                {canManageApiKeys && pendingApiKeys.length > 0 && (
                  <div className="api-key-bulk-actions">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        className="api-key-selection-checkbox"
                        checked={pendingApiKeys.every((item) => selectedPendingApiKeyIds.has(String(item.api_key_id || '')))}
                        onChange={toggleAllPendingApiKeys}
                        disabled={bulkApiKeyApprovalBusy}
                      />
                      <span>{'Alle offenen Anfragen auswählen'}</span>
                    </label>
                    <Button
                      onClick={handleApproveSelectedApiKeys}
                      disabled={bulkApiKeyApprovalBusy || !pendingApiKeys.some((item) => selectedPendingApiKeyIds.has(String(item.api_key_id || '')))} variant="primary">
                      <SymbolIcon icon="approve" className="sb-btn-icon" />
                      {bulkApiKeyApprovalBusy
                        ? 'Freigabe läuft ...'
                        : 'Ausgewählte freigeben'}
                    </Button>
                  </div>
                )}
                {bulkApiKeyApprovalStatus && <div className="hint">{bulkApiKeyApprovalStatus}</div>}
                <div className="table api-keys-table">
                  <div className="table-header">
                    <span aria-label={'Auswahl'}>{'Auswahl'}</span>
                    <span>{'POS-Seriennummer'}</span>
                    <span>{'Unternehmen'}</span>
                    <span>{'Betriebsstätte'}</span>
                    <span>{'Status'}</span>
                    <span>{'Aktionen'}</span>
                  </div>
                  {filteredApiKeys.map((item) => (
                    <div
                      className={`table-row${apiKeyActionDialog.open && apiKeyActionDialog.apiKey === (item.api_key_id || '') ? ' is-editing' : ''}${String(item.status || '').toLowerCase() === 'requested' ? ' is-selectable' : ''}`}
                      key={item.api_key_id || item.pos_id}
                      onClick={(event) => {
                        if (String(item.status || '').toLowerCase() !== 'requested' || event.target.closest('button, input')) return
                        toggleApiKeySelection(item.api_key_id)
                      }}
                    >
                      <span>
                        {String(item.status || '').toLowerCase() === 'requested' && (
                          <input
                            type="checkbox"
                            className="api-key-selection-checkbox"
                            checked={selectedPendingApiKeyIds.has(String(item.api_key_id || ''))}
                            onChange={() => toggleApiKeySelection(item.api_key_id)}
                            disabled={bulkApiKeyApprovalBusy}
                            aria-label={`POS ${item.pos_serial || item.reported_pos_serial || item.api_key_id} auswählen`}
                          />
                        )}
                      </span>
                      <span>{highlightSearchSubstring(item.pos_serial || item.reported_pos_serial || '-', apiKeysSearchTerm, true)}</span>
                      <span>{highlightSearchSubstring(item.company_name || item.reported_company_name || '-', apiKeysSearchTerm, true)}</span>
                      <span>{highlightSearchSubstring(item.site_name || item.reported_site_name || '-', apiKeysSearchTerm, true)}</span>
                      <span className="api-key-status-cell"><StatusBadge className="api-key-status-badge" variant="compact" tone={apiKeyStatusTone(item)}>{highlightSearchSubstring(mapApiKeyStateLabel(item), apiKeysSearchTerm)}</StatusBadge></span>
                      <span>
                        {renderApiKeyActions(item)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="api-keys-mobile-list" role="list">
                  {filteredApiKeys.map((item) => {
                    const rowId = item.api_key_id || item.pos_id || ''
                    const isExpanded = expandedApiKeyMobileId === rowId
                    const isRequested = String(item.status || '').toLowerCase() === 'requested'
                    const isActionDialogOpen = apiKeyActionDialog.open && apiKeyActionDialog.apiKey === (item.api_key_id || '')
                    const detailsId = `api-key-mobile-details-${rowId}`
                    return (
                      <div className="api-keys-mobile-item" key={`api-mobile-${rowId}`} role="listitem">
                        <button
                          className={`api-keys-mobile-summary${isRequested ? ' has-selection' : ''}`}
                          type="button"
                          onClick={() => setExpandedApiKeyMobileId((prev) => (prev === rowId ? '' : rowId))}
                          aria-expanded={isExpanded}
                          aria-controls={detailsId}
                        >
                          <span className={`api-keys-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                          {isRequested && (
                            <input
                              type="checkbox"
                              className="api-key-selection-checkbox"
                              checked={selectedPendingApiKeyIds.has(String(item.api_key_id || ''))}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => {
                                event.stopPropagation()
                                toggleApiKeySelection(item.api_key_id)
                              }}
                              disabled={bulkApiKeyApprovalBusy}
                              aria-label={`POS ${item.pos_serial || item.reported_pos_serial || item.api_key_id} auswählen`}
                            />
                          )}
                          <span className="api-keys-mobile-pos">{highlightSearchSubstring(item.pos_serial || item.reported_pos_serial || '-', apiKeysSearchTerm, true)}</span>
                          <span className="api-keys-mobile-company">{highlightSearchSubstring(item.company_name || item.reported_company_name || '-', apiKeysSearchTerm, true)}</span>
                          <StatusBadge className="api-keys-mobile-status" variant="compact" tone={apiKeyStatusTone(item)}>{highlightSearchSubstring(mapApiKeyStateLabel(item), apiKeysSearchTerm)}</StatusBadge>
                        </button>
                        {isExpanded && (
                          <div className="api-keys-mobile-details" id={detailsId}>
                            <div className="api-keys-mobile-meta">
                              <span className="api-keys-mobile-label">Betriebsstätte</span>
                              <span>{highlightSearchSubstring(item.site_name || item.reported_site_name || '-', apiKeysSearchTerm, true)}</span>
                            </div>
                            {canManageApiKeys && !isActionDialogOpen && (
                              <div className="api-keys-mobile-meta">
                                <span className="api-keys-mobile-label">Aktionen</span>
                                <div className="api-keys-mobile-actions">
                                  {renderApiKeyActions(item)}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                {apiKeyActionDialog.open && (
                  <div className="edit-pos-panel api-key-approval-panel">
                    <div className="card-title">
                      {apiKeyActionDialog.mode === 'approve' ? 'API-Key Freigabe' : 'API-Key Aktualisierung'}
                    </div>
                    <div className={`api-key-approval-grid${apiKeyActionDialog.mode === 'refresh' ? ' is-refresh' : ''}`}>
                      <div className="api-key-approval-card">
                        <div className="api-key-approval-card-title">Gemeldete Eigenschaften</div>
                        <div className="api-key-reported-list">
                          {apiKeyActionDialog.mode === 'approve' ? <>
                            <div><span>Unternehmen</span><strong>{apiKeyActionDialog.reportedCompanyName || '-'}</strong></div>
                            <div><span>Land</span><strong>{apiKeyActionDialog.reportedCompanyCountry || '-'}</strong></div>
                            <div><span>Betriebsstätte</span><strong>{apiKeyActionDialog.reportedSiteName || '-'}</strong></div>
                            {apiKeyActionDialog.reportedPosLabel && <div><span>POS Bezeichnung</span><strong>{apiKeyActionDialog.reportedPosLabel}</strong></div>}
                            <div><span>POS Seriennummer</span><strong>{apiKeyActionDialog.reportedPosSerial || apiKeyActionDialog.posSerial}</strong></div>
                          </> : <>
                            {apiKeyActionDialog.labelChanged && <div><span>POS Bezeichnung</span><strong>{apiKeyActionDialog.reportedPosLabel || '-'}</strong></div>}
                            {apiKeyActionDialog.tseTypeChanged && <div><span>TSE Typ</span><strong>{getPosTseTypeLabel(apiKeyActionDialog.reportedTseType)}</strong></div>}
                            {apiKeyActionDialog.tseSerialNumberChanged && <div><span>TSE Seriennummer</span><strong>{apiKeyActionDialog.reportedTseSerialNumber || '-'}</strong></div>}
                          </>}
                        </div>
                      </div>
                      {apiKeyActionDialog.mode === 'approve' && (
                      <div className="api-key-approval-card">
                        <div className="api-key-approval-card-title">Zuweisung</div>
                        <div className="form-grid">
                          <div>
                            <label htmlFor="ak-company-select">Unternehmen</label>
                            {!apiKeyActionDialog.createCompany ? (
                              <select
                                id="ak-company-select"
                                value={apiKeyActionDialog.companyId}
                                onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, companyId: e.target.value, siteId: '' }))}
                                disabled={apiKeyActionDialog.busy}
                              >
                                <option value="">Bitte wählen</option>
                                {companies.filter((c) => !c.is_deleted).map((company) => (
                                  <option key={company.company_id} value={company.company_id} data-i18n-skip="1">{company.name}</option>
                                ))}
                              </select>
                            ) : (
                              <div className="two-col">
                                <input
                                  type="text"
                                  placeholder="Unternehmensname"
                                  value={apiKeyActionDialog.newCompanyName}
                                  onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, newCompanyName: e.target.value }))}
                                  disabled={apiKeyActionDialog.busy}
                                />
                                <input
                                  type="text"
                                  placeholder="Land (ISO-2)"
                                  value={apiKeyActionDialog.newCompanyCountry}
                                  onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, newCompanyCountry: e.target.value }))}
                                  disabled={apiKeyActionDialog.busy}
                                />
                              </div>
                            )}
                            <label className="checkbox-inline api-key-create-toggle">
                              <input
                                type="checkbox"
                                checked={apiKeyActionDialog.createCompany}
                                onChange={(e) =>
                                  setApiKeyActionDialog((prev) => {
                                    const checked = e.target.checked
                                    const hasCompanyName = String(prev.newCompanyName || '').trim().length > 0
                                    const hasCompanyCountry = String(prev.newCompanyCountry || '').trim().length > 0
                                    const reportedName = String(prev.reportedCompanyName || '').trim()
                                    const reportedCountry = String(prev.reportedCompanyCountry || '').trim().toUpperCase()
                                    return {
                                      ...prev,
                                      createCompany: checked,
                                      companyId: '',
                                      siteId: '',
                                      newCompanyName: checked && !hasCompanyName ? reportedName : prev.newCompanyName,
                                      newCompanyCountry: checked && !hasCompanyCountry
                                        ? (reportedCountry || 'DE')
                                        : prev.newCompanyCountry
                                    }
                                  })}
                                disabled={apiKeyActionDialog.busy || !canEditCompanies}
                              />
                              <span>Unternehmen neu anlegen</span>
                            </label>
                            {!canEditCompanies && (
                              <div className="hint api-key-create-toggle-hint">
                                Neuanlage erfordert das Recht Unternehmen verwalten.
                              </div>
                            )}
                          </div>
                          <div>
                            <label htmlFor="ak-site-select">Betriebsstätte</label>
                            {!apiKeyActionDialog.createSite ? (
                              <select
                                id="ak-site-select"
                                value={apiKeyActionDialog.siteId}
                                onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, siteId: e.target.value }))}
                                disabled={apiKeyActionDialog.busy || (!apiKeyActionDialog.companyId && !apiKeyActionDialog.createCompany)}
                              >
                                <option value="">Bitte wählen</option>
                                {sites
                                  .filter((s) => !s.is_deleted && s.company_id === apiKeyActionDialog.companyId)
                                  .map((site) => (
                                    <option key={site.site_id} value={site.site_id} data-i18n-skip="1">{site.name}</option>
                                  ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                placeholder="Betriebsstätte"
                                value={apiKeyActionDialog.newSiteName}
                                onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, newSiteName: e.target.value }))}
                                disabled={apiKeyActionDialog.busy}
                              />
                            )}
                            <label className="checkbox-inline api-key-create-toggle">
                              <input
                                type="checkbox"
                                checked={apiKeyActionDialog.createSite}
                                onChange={(e) =>
                                  setApiKeyActionDialog((prev) => {
                                    const checked = e.target.checked
                                    const hasCurrentValue = String(prev.newSiteName || '').trim().length > 0
                                    const reportedValue = String(prev.reportedSiteName || '').trim()
                                    return {
                                      ...prev,
                                      createSite: checked,
                                      siteId: '',
                                      newSiteName: checked && !hasCurrentValue ? reportedValue : prev.newSiteName
                                    }
                                  })}
                                disabled={apiKeyActionDialog.busy || !canEditSites}
                              />
                              <span>Betriebsstätte neu anlegen</span>
                            </label>
                            {!canEditSites && (
                              <div className="hint api-key-create-toggle-hint">
                                Neuanlage erfordert das Recht Betriebsstätten verwalten.
                              </div>
                            )}
                          </div>
                          <div className="muted">
                            Die POS-Seriennummer wird aus den gemeldeten Eigenschaften übernommen und nicht manuell bearbeitet.
                          </div>
                        </div>
                      </div>
                      )}
                      <div className="inline-actions api-key-dialog-actions">
                        <Button onClick={handleApproveOrRefreshApiKey} disabled={apiKeyActionDialog.busy} variant="primary">
                          {apiKeyActionDialog.mode === 'approve' ? 'Zuweisen' : 'Aktualisieren'}
                        </Button>
                        <Button onClick={closeApiKeyActionDialog} disabled={apiKeyActionDialog.busy} variant="secondary">
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                    {apiKeyActionDialog.status && <div className="hint">{apiKeyActionDialog.status}</div>}
                  </div>
                )}
                {!apiKeys.length && <div className="hint">Keine API-Keys definiert</div>}
                {apiKeys.length > 0 && !filteredApiKeys.length && <div className="hint">Keine passenden API-Keys gefunden</div>}
                {!canManageApiKeys && <div className="hint">Nur Leserechte: API-Keys können nicht bearbeitet werden.</div>}
              </section>
            </>
          )}

          {active === 'API-Keys' && authUser?.role === 'platform_admin' && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">API-Keys</div>
                  <h1 className="sb-h1 page-title"><SymbolIcon icon="key" />API-Key Verwaltung</h1>
                </div>
              </section>
              <section className="card">
                <div className="hint">API-Key Verwaltung ist im Hauptnutzer-Kontext verfügbar.</div>
              </section>
            </>
          )}

          {(active === 'Administration' || active === 'Unternehmensstruktur') && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">{isStructureNavigation ? 'Unternehmensstruktur' : 'Administration'}</div>
                  <h1 className="sb-h1 page-title"><SymbolIcon icon={isStructureNavigation ? 'hierarchy' : 'settings'} />{isStructureNavigation ? 'Unternehmensstruktur' : 'Administration'}</h1>
                </div>
              </section>

              {!isStructureNavigation && (
              <section className="grid admin-security-grid">
                <section className="card admin-compact-card">
                  <div className="card-title">Passwort ändern</div>
                  <ExpandableButton
                    className="admin-compact-toggle"
                    onClick={togglePasswordChange}
                    expanded={showPasswordChange}
                  >
                    {showPasswordChange ? 'Passwortänderung ausblenden' : 'Passwort ändern'}
                  </ExpandableButton>
                  {showPasswordChange && (
                    <div className="tenant-create-wrap password-change-form">
                    <div className="form-grid">
                      <div>
                        <label>Neues Passwort</label>
                          <PasswordInput
                            value={passwordNew}
                            onChange={(e) => {
                              setPasswordNew(e.target.value)
                              setPasswordFieldErrors((prev) => ({ ...prev, newPassword: false }))
                              setPasswordError('')
                            }}
                            invalid={passwordFieldErrors.newPassword}
                            autoComplete="new-password"
                          />
                        </div>
                        <div>
                          <label>Passwort wiederholen</label>
                          <PasswordInput
                            value={passwordRepeat}
                            onChange={(e) => {
                              setPasswordRepeat(e.target.value)
                              setPasswordFieldErrors((prev) => ({ ...prev, repeatPassword: false }))
                              setPasswordError('')
                            }}
                            invalid={passwordFieldErrors.repeatPassword}
                            autoComplete="new-password"
                          />
                        </div>
                      </div>
                      <Button className="tenant-create-submit" onClick={handleChangePassword} disabled={passwordBusy} variant="primary">
                        {passwordBusy ? 'Speichert…' : 'Passwort speichern'}
                      </Button>
                    </div>
                  )}
                  {passwordError && <div className="hint form-error-hint" role="alert">{passwordError}</div>}
                  {passwordSuccess && <div className="hint">{passwordSuccess}</div>}
                </section>

                <section className="card admin-compact-card">
                  <div className="card-title">Einstellungen</div>
                  <ExpandableButton
                    className="admin-compact-toggle"
                    onClick={toggleUserSettings}
                    expanded={showUserSettings}
                  >
                    {showUserSettings ? 'Einstellungen ausblenden' : 'Einstellungen'}
                  </ExpandableButton>
                  {showUserSettings && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div>
                          <label>Sprache</label>
                          <select value={preferredLanguageDraft} onChange={(e) => setPreferredLanguageDraft(String(e.target.value || 'en').toLowerCase() === 'de' ? 'de' : 'en')}>
                            <option value="de">Deutsch</option>
                            <option value="en">English</option>
                          </select>
                        </div>
                      </div>
                      <Button className="tenant-create-submit" onClick={handleSaveUserSettings} disabled={settingsBusy} variant="primary">
                        {settingsBusy ? 'Speichert…' : 'Einstellungen speichern'}
                      </Button>
                    </div>
                  )}
                  {settingsError && <div className="hint">{settingsError}</div>}
                  {settingsSuccess && <div className="hint">{settingsSuccess}</div>}
                </section>

                <section className="card passkey-manage-card">
                  <div className="card-title">Passkeys verwalten</div>
                  {!passkeySupported && <div className="hint">Passkeys werden von diesem Browser nicht unterstützt.</div>}
                  <ExpandableButton
                    className="passkey-manage-toggle"
                    onClick={togglePasskeyPanel}
                    disabled={!passkeySupported || passkeyBusy}
                    expanded={showPasskeyPanel}
                  >
                    {showPasskeyPanel ? 'Passkeys ausblenden' : 'Passkeys verwalten'}
                  </ExpandableButton>
                  {showPasskeyPanel && (
                    <>
                      <div className="table passkey-table">
                        <div className="table-header">
                          <span>Passkey</span><span>Aktionen</span>
                        </div>
                        {passkeys.map((item) => {
                          const isEditing = editPasskey.passkeyId === item.passkey_id
                          return (
                            <div className="table-row" key={item.passkey_id}>
                              {isEditing ? (
                                <>
                                  <span className="passkey-identity is-editing">
                                    <span className="passkey-identity-name">
                                    <input
                                      className="tenant-user-edit-input"
                                      type="text"
                                      value={editPasskey.name}
                                      onChange={(e) => setEditPasskey((prev) => ({ ...prev, name: e.target.value }))}
                                    />
                                    </span>
                                    <span className="passkey-identity-last-used"><span className="passkey-last-used-prefix" data-i18n-live="1">Zuletzt verwendet</span>: {formatDateTimeDisplay(item.last_used_at || item.updated_at || item.created_at)}</span>
                                  </span>
                                  <span className="tenant-user-row-actions is-edit">
                                    <Button onClick={handleSavePasskeyName} disabled={passkeyBusy} variant="primary">Speichern</Button>
                                    <Button onClick={() => setEditPasskey({ passkeyId: '', name: '' })} disabled={passkeyBusy} variant="secondary">Abbrechen</Button>
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="passkey-identity">
                                    <span className="passkey-identity-name">
                                    <span data-i18n-skip="1">{item.name || '-'}</span>
                                    </span>
                                    <span className="passkey-identity-last-used"><span className="passkey-last-used-prefix" data-i18n-live="1">Zuletzt verwendet</span>: {formatDateTimeDisplay(item.last_used_at || item.updated_at || item.created_at)}</span>
                                  </span>
                                  <span className="tenant-user-row-actions">
                                    <span className="passkey-desktop-actions">
                                      <IconButton
                                        icon="edit"
                                        label="Editieren"
                                        ariaLabel="Passkey editieren"
                                        onClick={() => setEditPasskey({ passkeyId: item.passkey_id, name: item.name || '' })}
                                      />
                                      <IconButton
                                        icon="delete"
                                        label="Löschen"
                                        ariaLabel="Passkey löschen"
                                        tone="danger"
                                        onClick={() => handleDeletePasskey(item.passkey_id, item.name || 'Passkey')}
                                      />
                                    </span>
                                    <span className="passkey-mobile-actions">
                                      <Button onClick={() => setEditPasskey({ passkeyId: item.passkey_id, name: item.name || '' })} variant="primary">Editieren</Button>
                                      <Button onClick={() => handleDeletePasskey(item.passkey_id, item.name || 'Passkey')} variant="primary" tone="destructive">Löschen</Button>
                                    </span>
                                  </span>
                                </>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      {!passkeys.length && <div className="hint empty-list-hint">Keine Passkeys vorhanden</div>}
                      <ExpandableButton
                        onClick={() => {
                          if (showPasskeyCreate) {
                            setPasskeyError('')
                            setPasskeyNameInvalid(false)
                          }
                          setShowPasskeyCreate((prev) => !prev)
                          if (!showPasskeyCreate && !passkeyName.trim()) setPasskeyName(PASSKEY_DEFAULT_NAME)
                        }}
                        disabled={!passkeySupported || passkeyBusy}
                        expanded={showPasskeyCreate}
                      >
                        {showPasskeyCreate ? 'Passkey-Anlage ausblenden' : 'Passkey hinzufügen'}
                      </ExpandableButton>
                      {showPasskeyCreate && (
                        <div className="tenant-create-wrap">
                          <div className="form-grid">
                            <div>
                              <label>Name</label>
                              <input
                                type="text"
                                placeholder={PASSKEY_DEFAULT_NAME}
                                value={passkeyName}
                                className={passkeyNameInvalid ? 'field-input-error' : ''}
                                onChange={(e) => {
                                  setPasskeyName(e.target.value)
                                  setPasskeyNameInvalid(false)
                                  setPasskeyError('')
                                }}
                              />
                            </div>
                          </div>
                          <Button className="tenant-create-submit" onClick={handleCreatePasskey} disabled={passkeyBusy || !passkeySupported} variant="primary">
                            {passkeyBusy ? 'Warte auf Passkey…' : 'Passkey registrieren'}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                  {passkeyError && <div className="hint form-error-hint">{passkeyError}</div>}
                  {passkeySuccess && <div className="hint">{passkeySuccess}</div>}
                </section>
              </section>
              )}

              {!isStructureNavigation && authUser?.role === 'platform_admin' && (
                <section className="card platform-org-panel" style={{ order: 1 }}>
                  <div className="card-title">Organisationsverwaltung</div>
                  <div className="archive-search">
                    <ClearableInput
                      type="search"
                      placeholder="Suche (Organisation, Ansprechpartner, E-Mail, Reseller)"
                      value={platformOrgSearchTerm}
                      onValueChange={setPlatformOrgSearchTerm}
                    />
                  </div>
                  <div className="table tenant-admin-table platform-org-table organization-admin-scroll-window">
                    <div className="table-header">
                      <span>Name</span><span>Ansprechpartner</span><span>E-Mail</span><span>Typ</span><span>Status</span><span>Aktionen</span>
                    </div>
                    {visiblePlatformOrganizationRows.map((row) => {
                      if (row.type === 'reseller') {
                        const item = row.item
                        const canManageItem = row.canManage
                        const isEditing = canManageItem && editResellerAdmin.userId === item.user_id
                        const isExpanded = Boolean(platformOrgExpandedResellerIds[item.user_id])
                        return (
                          <div
                            className={`table-row org-group-row level-${row.level}${row.hasChildren ? ' is-collapsible' : ''}${isEditing ? ' is-editing' : ''}`}
                            key={`platform-reseller-${item.user_id}`}
                            onClick={(!isEditing && row.hasChildren && !platformOrgSearchTerm.trim()) ? () => togglePlatformOrgResellerExpand(item.user_id) : undefined}
                          >
                            {isEditing ? (
                              <>
                                <span>
                                  <input type="text" value={editResellerAdmin.name} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                </span>
                                <span>
                                  <input type="text" value={editResellerAdmin.contactName} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, contactName: e.target.value }))} />
                                </span>
                                <span>
                                  <span data-i18n-skip="1">{editResellerAdmin.email}</span>
                                </span>
                                <span>
                                  <span className="org-type-icon" title="Reseller" aria-label="Reseller">
                                    <SymbolIcon icon="partner_exchange" />
                                  </span>
                                </span>
                                <span>-</span>
                                <span className="tenant-admin-actions">
                                  <Button onClick={handleSaveResellerAdminEdit} variant="primary">Speichern</Button>
                                <Button onClick={() => setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })} variant="secondary">Abbrechen</Button>
                                </span>
                              </>
                            ) : (
                              <>
                                <span style={{ paddingLeft: `${row.level * 18}px` }}>
                                  <span className="org-row-main">
                                    {row.hasChildren ? (
                                      <button
                                        className="tree-toggle"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (platformOrgSearchTerm.trim()) return
                                          togglePlatformOrgResellerExpand(item.user_id)
                                        }}
                                        aria-label={isExpanded ? 'Zuklappen' : 'Aufklappen'}
                                        aria-expanded={isExpanded}
                                        disabled={Boolean(platformOrgSearchTerm.trim())}
                                      >
                                        <span className="tree-toggle-icon" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                                      </button>
                                    ) : (
                                      <span className="tree-toggle tree-toggle-spacer" aria-hidden="true" />
                                    )}
                                    <span>{highlightSearchSubstring(item.name || '-', platformOrgSearchTerm, true)}</span>
                                  </span>
                                </span>
                                <span>{highlightSearchSubstring(item.contact_name || '-', platformOrgSearchTerm, true)}</span>
                                <span>{highlightSearchSubstring(item.email || '-', platformOrgSearchTerm, true)}</span>
                                <span>
                                  <span className="org-type-icon" title="Reseller" aria-label="Reseller">
                                    <SymbolIcon icon="partner_exchange" />
                                  </span>
                                </span>
                                <StatusBadge tone={getAdminStatusMeta(item.status).tone}>{getAdminStatusMeta(item.status).label}</StatusBadge>
                                <span className="tenant-user-row-actions">
                                  <ActionMenu actions={[
                                    canManageItem && { label: 'Editieren', onClick: () => setEditResellerAdmin({ userId: item.user_id, name: item.name || '', contactName: item.contact_name || '', email: item.email || '' }) },
                                    String(item.status || '').toLowerCase() === 'initial' && { label: 'Einladung erneut senden', onClick: () => handleResendInvitation(item.user_id), disabled: resendInvitationBusyUserId === item.user_id },
                                    !authUser?.impersonation?.active && { label: 'Impersonieren', onClick: () => handleImpersonateUser(item.user_id), disabled: uploadBusy || String(item.status || '').toLowerCase() !== 'active' },
                                    canManageItem && { label: String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren', onClick: () => handleDeleteResellerAdmin(item.user_id, item.name || item.email || 'Reseller'), tone: String(item.status || '').toLowerCase() === 'active' ? 'warning' : 'positive' }
                                  ]} />
                                </span>
                              </>
                            )}
                          </div>
                        )
                      }
                      const item = row.item
                      const canManageItem = row.canManage
                      const isEditing = canManageItem && editTenantAdmin.userId === item.user_id
                      return (
                        <div className={`table-row level-${row.level}${isEditing ? ' is-editing' : ''}`} key={`platform-tenant-${item.user_id}`}>
                          {isEditing ? (
                            <>
                              <span><input type="text" value={editTenantAdmin.organizationName} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, organizationName: e.target.value }))} /></span>
                              <span><input type="text" value={editTenantAdmin.name} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, name: e.target.value }))} /></span>
                              <span data-i18n-skip="1">{editTenantAdmin.email}</span>
                              <span>
                                <span className="org-type-icon" title="Organisation" aria-label="Organisation">
                                  <SymbolIcon icon="corporate_fare" />
                                </span>
                              </span>
                              <span>-</span>
                              <span className="tenant-admin-actions">
                                <Button onClick={handleSaveTenantAdminEdit} variant="primary">Speichern</Button>
                              <Button onClick={() => setEditTenantAdmin({ userId: '', organizationName: '', name: '', email: '' })} variant="secondary">Abbrechen</Button>
                              </span>
                            </>
                          ) : (
                            <>
                              <span style={{ paddingLeft: `${row.level * 18}px` }}>
                                <span className="org-row-main">
                                  <span className="tree-toggle tree-toggle-spacer" aria-hidden="true" />
                                  <span>{highlightSearchSubstring(item.organization_name || '-', platformOrgSearchTerm, true)}</span>
                                </span>
                              </span>
                              <span>{highlightSearchSubstring(item.name || '-', platformOrgSearchTerm, true)}</span>
                              <span>{highlightSearchSubstring(item.email || '-', platformOrgSearchTerm, true)}</span>
                              <span>
                                <span className="org-type-icon" title="Organisation" aria-label="Organisation">
                                  <SymbolIcon icon="corporate_fare" />
                                </span>
                              </span>
                              <StatusBadge tone={getAdminStatusMeta(item.status).tone}>{getAdminStatusMeta(item.status).label}</StatusBadge>
                              <span className="tenant-user-row-actions">
                                <ActionMenu actions={[
                                  canManageItem && { label: 'Editieren', onClick: () => setEditTenantAdmin({ userId: item.user_id, organizationName: item.organization_name || '', name: item.name || '', email: item.email || '' }) },
                                  String(item.status || '').toLowerCase() === 'initial' && { label: 'Einladung erneut senden', onClick: () => handleResendTenantAdminInvitation(item.user_id), disabled: resendInvitationBusyUserId === item.user_id },
                                  !authUser?.impersonation?.active && { label: 'Impersonieren', onClick: () => handleImpersonateUser(item.user_id), disabled: uploadBusy || String(item.status || '').toLowerCase() !== 'active' },
                                  canManageItem && { label: String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren', onClick: () => handleTogglePlatformTenantAdminStatus(item), tone: String(item.status || '').toLowerCase() === 'active' ? 'warning' : 'positive' }
                                ]} />
                              </span>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="platform-mobile-list" role="list">
                    {visiblePlatformOrganizationRows.map((row) => {
                      const rowKey = `${row.type}:${row.item.user_id}`
                      const isExpanded = expandedPlatformMobileRowId === rowKey
                      const isReseller = row.type === 'reseller'
                      const item = row.item
                      const canManageItem = row.canManage
                      const isEditing = canManageItem && (isReseller ? editResellerAdmin.userId === item.user_id : editTenantAdmin.userId === item.user_id)
                      const canToggleChildren = isReseller && row.hasChildren && !platformOrgSearchTerm.trim()
                      return (
                        <div className="platform-mobile-item" key={`platform-mobile-${rowKey}`} role="listitem">
                          <button
                            className="platform-mobile-summary"
                            type="button"
                            onClick={() => {
                              if (canToggleChildren) togglePlatformOrgResellerExpand(item.user_id)
                              setExpandedPlatformMobileRowId((prev) => (prev === rowKey ? '' : rowKey))
                            }}
                            aria-expanded={isExpanded}
                          >
                            <span className={`platform-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                            <span className="platform-mobile-name" style={{ paddingLeft: `${row.level * 14}px` }}>
                              {isReseller ? highlightSearchSubstring(item.name || '-', platformOrgSearchTerm, true) : highlightSearchSubstring(item.organization_name || '-', platformOrgSearchTerm, true)}
                            </span>
                            <span className="platform-mobile-email">{highlightSearchSubstring(item.email || '-', platformOrgSearchTerm, true)}</span>
                            <StatusBadge className="platform-mobile-status" tone={isReseller ? getAdminStatusMeta(item.status).tone : 'success'}>
                              {isReseller ? highlightSearchSubstring(getAdminStatusMeta(item.status).label, platformOrgSearchTerm) : highlightSearchSubstring('Organisation', platformOrgSearchTerm)}
                            </StatusBadge>
                          </button>
                          {isExpanded && (
                            <div className="platform-mobile-details">
                              {isEditing ? (
                                <div className="platform-mobile-edit">
                                  {isReseller ? (
                                    <>
                                      <input type="text" value={editResellerAdmin.name} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                      <input type="text" value={editResellerAdmin.contactName} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, contactName: e.target.value }))} />
                                      <span data-i18n-skip="1">{editResellerAdmin.email}</span>
                                      <div className="platform-mobile-actions">
                                        <Button onClick={handleSaveResellerAdminEdit} variant="primary">Speichern</Button>
                                        <Button onClick={() => setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })} variant="secondary">Abbrechen</Button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <input type="text" value={editTenantAdmin.organizationName} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, organizationName: e.target.value }))} />
                                      <input type="text" value={editTenantAdmin.name} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                      <span data-i18n-skip="1">{editTenantAdmin.email}</span>
                                      <div className="platform-mobile-actions">
                                        <Button onClick={handleSaveTenantAdminEdit} variant="primary">Speichern</Button>
                                        <Button onClick={() => setEditTenantAdmin({ userId: '', organizationName: '', name: '', email: '' })} variant="secondary">Abbrechen</Button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="platform-mobile-actions">
                                  {canManageItem && (
                                    <Button onClick={() => (isReseller ? setEditResellerAdmin({ userId: item.user_id, name: item.name || '', contactName: item.contact_name || '', email: item.email || '' }) : setEditTenantAdmin({ userId: item.user_id, organizationName: item.organization_name || '', name: item.name || '', email: item.email || '' }))} variant="primary">Editieren</Button>
                                  )}
                                  {isReseller && String(item.status || '').toLowerCase() === 'initial' && (
                                    <Button onClick={() => handleResendInvitation(item.user_id)} disabled={resendInvitationBusyUserId === item.user_id} variant="primary">
                                      {'Einladung erneut senden'}
                                    </Button>
                                  )}
                                  {!isReseller && String(item.status || '').toLowerCase() === 'initial' && (
                                    <Button onClick={() => handleResendTenantAdminInvitation(item.user_id)} disabled={resendInvitationBusyUserId === item.user_id} variant="primary">
                                      {'Einladung erneut senden'}
                                    </Button>
                                  )}
                                  {!authUser?.impersonation?.active && (
                                    <Button
                                      onClick={() => (isReseller ? handleImpersonateUser(item.user_id) : handleImpersonateUser(item.user_id))}
                                      disabled={uploadBusy || String(item.status || '').toLowerCase() !== 'active'} variant="primary">
                                      Impersonieren
                                    </Button>
                                  )}
                                  {canManageItem && (
                                    <Button
                                      data-i18n-live="1"
                                      onClick={() => (isReseller ? handleDeleteResellerAdmin(item.user_id, item.name || item.email || 'Reseller') : handleTogglePlatformTenantAdminStatus(item))}
                                      variant="primary"
                                      tone={String(item.status || '').toLowerCase() === 'active' ? 'warning' : 'default'}
                                    >
                                      {String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {!visiblePlatformOrganizationRows.length && (
                    <div className="hint">
                      {platformOrgSearchTerm.trim() ? 'Keine passende Organisation gefunden' : 'Keine Organisationen oder Reseller vorhanden'}
                    </div>
                  )}
                  <ExpandableButton onClick={() => toggleAdminCreateForm('tenantAdmin', showTenantCreate, setShowTenantCreate)} expanded={showTenantCreate}>
                    {showTenantCreate ? 'Organisationsanlage ausblenden' : 'Neue Organisation anlegen'}
                  </ExpandableButton>
                  {showTenantCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div><label>Organisationsname</label><input className={adminFieldErrors.tenantAdmin.organizationName ? 'field-input-error' : ''} type="text" placeholder="Organisation" value={tenantAdminOrgName} onChange={(e) => { setTenantAdminOrgName(e.target.value); clearAdminFormFieldError('tenantAdmin', 'organizationName') }} /></div>
                        <div><label>{'Name Tenant-Admin'}</label><input className={adminFieldErrors.tenantAdmin.name ? 'field-input-error' : ''} type="text" placeholder="Klartext Name" value={tenantAdminName} onChange={(e) => { setTenantAdminName(e.target.value); clearAdminFormFieldError('tenantAdmin', 'name') }} /></div>
                        <div><label>{'E-Mail Tenant-Admin'}</label><input className={adminFieldErrors.tenantAdmin.email ? 'field-input-error' : ''} type="email" placeholder="admin@tenant.tld" value={tenantAdminEmail} onChange={(e) => { setTenantAdminEmail(e.target.value); clearAdminFormFieldError('tenantAdmin', 'email') }} /></div>
                      </div>
                      <Button className="tenant-create-submit" onClick={handleCreateTenantAdmin} variant="primary">Anlegen</Button>
                    </div>
                  )}
                  <ExpandableButton onClick={() => toggleAdminCreateForm('resellerAdmin', showResellerCreate, setShowResellerCreate)} expanded={showResellerCreate}>
                    {showResellerCreate ? 'Reseller-Anlage ausblenden' : 'Reseller anlegen'}
                  </ExpandableButton>
                  {showResellerCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div><label>Reseller Name (optional)</label><input type="text" placeholder="Reseller Name" value={resellerAdminName} onChange={(e) => setResellerAdminName(e.target.value)} /></div>
                        <div><label>Name Reseller-Admin (optional)</label><input type="text" placeholder="Klartext Name" value={resellerAdminContactName} onChange={(e) => setResellerAdminContactName(e.target.value)} /></div>
                        <div><label>E-Mail</label><input className={adminFieldErrors.resellerAdmin.email ? 'field-input-error' : ''} type="email" placeholder="reseller@partner.tld" value={resellerAdminEmail} onChange={(e) => { setResellerAdminEmail(e.target.value); clearAdminFormFieldError('resellerAdmin', 'email') }} /></div>
                      </div>
                      <Button className="tenant-create-submit" onClick={handleCreateResellerAdmin} variant="primary">Anlegen</Button>
                    </div>
                  )}
                  {platformError && <div className="hint form-error-hint">{platformError}</div>}
                  {platformSuccess && <div className="hint">{platformSuccess}</div>}
                </section>
              )}

              {!isStructureNavigation && authUser?.role === 'reseller_admin' && (
                <>
                <section className="card reseller-org-panel">
                  <div className="card-title">Organisationsverwaltung</div>
                  <div className="archive-search">
                    <ClearableInput
                      type="search"
                      placeholder="Suche (Reseller, Organisation, Ansprechpartner, E-Mail, Status)"
                      value={resellerSearchTerm}
                      onValueChange={setResellerSearchTerm}
                    />
                  </div>
                  <div className="table tenant-admin-table reseller-org-table organization-admin-scroll-window">
                    <div className="table-header">
                      <span>Name</span><span>Ansprechpartner</span><span>E-Mail</span><span>Typ</span><span>Impersonation</span><span>Status</span><span>Aktionen</span>
                    </div>
                    {visibleResellerOrganizationRows.map((row) => {
                      if (row.type === 'reseller') {
                        const item = row.item
                        const isEditing = editResellerAdmin.userId === item.user_id
                        const isExpanded = Boolean(resellerOrgExpandedResellerIds[item.user_id])
                        const canManageResellerNode = Boolean(authUser?.user_id) && item.created_by_reseller_admin_id === authUser.user_id
                        const resellerImpersonationStatus = getResellerImpersonationStatus(item)
                        const subResellerImpersonationActions = getSubResellerImpersonationActions({
                          status: resellerImpersonationStatus,
                          isDirectChild: canManageResellerNode,
                          impersonationActive: Boolean(authUser?.impersonation?.active),
                          isSelf: item.user_id === authUser?.user_id,
                          targetStatus: item.status
                        })
                        return (
                          <div
                            className={`table-row org-group-row level-${row.level}${row.hasChildren ? ' is-collapsible' : ''}${isEditing ? ' is-editing' : ''}`}
                            key={`reseller-admin-${item.user_id}`}
                            onClick={(!isEditing && row.hasChildren && !resellerSearchTerm.trim()) ? () => toggleResellerOrgResellerExpand(item.user_id) : undefined}
                          >
                            {isEditing ? (
                              <>
                                <span><input type="text" value={editResellerAdmin.name} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, name: e.target.value }))} /></span>
                                <span><input type="text" value={editResellerAdmin.contactName} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, contactName: e.target.value }))} /></span>
                                <span data-i18n-skip="1">{editResellerAdmin.email}</span>
                                <span>-</span>
                                <span>-</span>
                                <span>-</span>
                                <span className="tenant-admin-actions">
                                  <Button onClick={handleSaveResellerAdminEdit} variant="primary">Speichern</Button>
                                <Button onClick={() => setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })} variant="secondary">Abbrechen</Button>
                                </span>
                              </>
                            ) : (
                              <>
                                <span style={{ paddingLeft: `${row.level * 18}px` }}>
                                  <span className="org-row-main">
                                    {row.hasChildren ? (
                                      <button
                                        className="tree-toggle"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (resellerSearchTerm.trim()) return
                                          toggleResellerOrgResellerExpand(item.user_id)
                                        }}
                                        aria-label={isExpanded ? 'Zuklappen' : 'Aufklappen'}
                                        aria-expanded={isExpanded}
                                        disabled={Boolean(resellerSearchTerm.trim())}
                                      >
                                        <span className="tree-toggle-icon" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                                      </button>
                                    ) : (
                                      <span className="tree-toggle tree-toggle-spacer" aria-hidden="true" />
                                    )}
                                    <span>{highlightSearchSubstring(item.name || '-', resellerSearchTerm, true)}</span>
                                  </span>
                                </span>
                                <span>{highlightSearchSubstring(item.contact_name || '-', resellerSearchTerm, true)}</span>
                                <span>{highlightSearchSubstring(item.email || '-', resellerSearchTerm, true)}</span>
                                <span><span className="org-type-icon" title="Reseller" aria-label="Reseller"><SymbolIcon icon="partner_exchange" /></span></span>
                                <span><span className={`impersonation-status-icon is-${getImpersonationStatusMeta(getResellerImpersonationStatus(item)).key}`} title={getImpersonationStatusMeta(getResellerImpersonationStatus(item)).title} /></span>
                                <StatusBadge tone={getAdminStatusMeta(item.status).tone}>{getAdminStatusMeta(item.status).label}</StatusBadge>
                                <span className="tenant-user-row-actions">
                                  <ActionMenu actions={[
                                    canManageResellerNode && { label: 'Editieren', onClick: () => setEditResellerAdmin({ userId: item.user_id, name: item.name || '', contactName: item.contact_name || '', email: item.email || '' }) },
                                    String(item.status || '').toLowerCase() === 'initial' && { label: 'Einladung erneut senden', onClick: () => handleResendInvitation(item.user_id), disabled: resendInvitationBusyUserId === item.user_id },
                                    subResellerImpersonationActions.showStart && { label: 'Impersonieren', onClick: () => handleImpersonateResellerUser(item.user_id), disabled: uploadBusy || subResellerImpersonationActions.startDisabled },
                                    subResellerImpersonationActions.showRequest && { label: 'Impersonation anfragen', onClick: () => handleRequestSubResellerImpersonation(item.user_id) },
                                    subResellerImpersonationActions.showWithdraw && { label: subResellerImpersonationActions.withdrawsApproval ? 'Impersonation-Freigabe zurückziehen' : 'Impersonation-Anfrage zurückziehen', onClick: () => handleWithdrawSubResellerImpersonation(item.user_id, resellerImpersonationStatus, item.name || item.email) },
                                    canManageResellerNode && { label: String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren', onClick: () => handleDeleteResellerAdmin(item.user_id, item.name || item.email || 'Reseller'), tone: String(item.status || '').toLowerCase() === 'active' ? 'warning' : 'positive' }
                                  ]} />
                                </span>
                              </>
                            )}
                          </div>
                        )
                      }
                      const item = row.item
                      const isEditing = editResellerTenantAdmin.userId === item.user_id
                      const tenantCreatorId = item.created_by_user_id || item.reseller_admin_id || row.parentResellerId
                      const canManageTenantNode = Boolean(authUser?.user_id) && tenantCreatorId === authUser.user_id
                      const resellerTenantImpersonationStatus = getResellerImpersonationStatus(item)
                      const canStartTenantImpersonation = resellerTenantImpersonationStatus === IMPERSONATION_STATUS_APPROVED && !authUser?.impersonation?.active
                      const canChangeTenantImpersonation = Boolean(authUser?.user_id) && item.reseller_admin_id === authUser.user_id && !authUser?.impersonation?.active
                      const canRequestTenantImpersonation = isImpersonationRequestable(resellerTenantImpersonationStatus)
                      const canWithdrawTenantImpersonation = isImpersonationWithdrawable(resellerTenantImpersonationStatus)
                      return (
                        <div className={`table-row level-${row.level}${isEditing ? ' is-editing' : ''}`} key={`reseller-tenant-${item.user_id}`}>
                          {isEditing ? (
                            <>
                              <span><input type="text" value={editResellerTenantAdmin.organizationName} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, organizationName: e.target.value }))} /></span>
                              <span><input type="text" value={editResellerTenantAdmin.name} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, name: e.target.value }))} /></span>
                              <span data-i18n-skip="1">{editResellerTenantAdmin.email}</span>
                              <span>-</span>
                              <span>-</span>
                              <span>-</span>
                              <span className="tenant-admin-actions tenant-admin-actions-inline">
                                <Button onClick={handleSaveResellerTenantAdminEdit} variant="primary">Speichern</Button>
                                <Button onClick={() => { setEditResellerTenantAdmin({ userId: '', tenantId: '', organizationName: '', name: '', email: '' }) }} variant="secondary">Abbrechen</Button>
                              </span>
                            </>
                          ) : (
                            <>
                              <span style={{ paddingLeft: `${row.level * 18}px` }}>
                                <span className="org-row-main">
                                  <span className="tree-toggle tree-toggle-spacer" aria-hidden="true" />
                                  <span>{highlightSearchSubstring(item.organization_name || '-', resellerSearchTerm, true)}</span>
                                </span>
                              </span>
                              <span>{highlightSearchSubstring(item.name || '-', resellerSearchTerm, true)}</span>
                              <span>{highlightSearchSubstring(item.email || '-', resellerSearchTerm, true)}</span>
                              <span><span className="org-type-icon" title="Organisation" aria-label="Organisation"><SymbolIcon icon="corporate_fare" /></span></span>
                              <span><span className={`impersonation-status-icon is-${getImpersonationStatusMeta(getResellerImpersonationStatus(item)).key}`} title={getImpersonationStatusMeta(getResellerImpersonationStatus(item)).title} /></span>
                              <StatusBadge tone={getAdminStatusMeta(item.status).tone}>{getAdminStatusMeta(item.status).label}</StatusBadge>
                              <span className="tenant-user-row-actions">
                                <ActionMenu actions={[
                                  canStartTenantImpersonation && { label: 'Impersonieren', onClick: () => handleImpersonateTenant(item.tenant_id), disabled: uploadBusy || String(item.status || '').toLowerCase() !== 'active' },
                                  canManageTenantNode && { label: 'Editieren', onClick: () => setEditResellerTenantAdmin({ userId: item.user_id, tenantId: item.tenant_id, organizationName: item.organization_name || '', name: item.name || '', email: item.email || '' }) },
                                  String(item.status || '').toLowerCase() === 'initial' && { label: 'Einladung erneut senden', onClick: () => handleResendTenantAdminInvitation(item.user_id, true), disabled: resendInvitationBusyUserId === item.user_id },
                                  canChangeTenantImpersonation && canRequestTenantImpersonation && { label: 'Impersonation anfragen', onClick: () => handleRequestResellerTenantImpersonation(item.tenant_id) },
                                  canChangeTenantImpersonation && canWithdrawTenantImpersonation && { label: resellerTenantImpersonationStatus === IMPERSONATION_STATUS_APPROVED ? 'Impersonation-Freigabe zurückziehen' : 'Impersonation-Anfrage zurückziehen', onClick: () => handleWithdrawResellerTenantImpersonation(item.tenant_id, resellerTenantImpersonationStatus, item.organization_name || item.name || item.email) },
                                  canManageTenantNode && { label: String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren', onClick: () => handleToggleResellerTenantStatus(item), tone: String(item.status || '').toLowerCase() === 'active' ? 'warning' : 'positive' }
                                ]} />
                              </span>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="platform-mobile-list" role="list">
                    {visibleResellerOrganizationRows.map((row) => {
                      const rowKey = `${row.type}:${row.item.user_id}`
                      const item = row.item
                      const isReseller = row.type === 'reseller'
                      const isExpanded = expandedResellerMobileRowId === rowKey
                      const isEditing = isReseller ? editResellerAdmin.userId === item.user_id : editResellerTenantAdmin.userId === item.user_id
                      const canManageRow = isReseller
                        ? (Boolean(authUser?.user_id) && item.created_by_reseller_admin_id === authUser.user_id)
                        : (Boolean(authUser?.user_id) && (item.created_by_user_id || item.reseller_admin_id || row.parentResellerId) === authUser.user_id)
                      const mobileImpersonationStatus = getResellerImpersonationStatus(item)
                      const subResellerMobileActions = getSubResellerImpersonationActions({
                        status: mobileImpersonationStatus,
                        isDirectChild: isReseller && canManageRow,
                        impersonationActive: Boolean(authUser?.impersonation?.active),
                        isSelf: item.user_id === authUser?.user_id,
                        targetStatus: item.status
                      })
                      const canStartMobileTenantImpersonation = !isReseller && mobileImpersonationStatus === IMPERSONATION_STATUS_APPROVED && !authUser?.impersonation?.active
                      const canChangeMobileTenantImpersonation = Boolean(authUser?.user_id) && !isReseller && item.reseller_admin_id === authUser.user_id && !authUser?.impersonation?.active
                      const canRequestMobileTenantImpersonation = isImpersonationRequestable(mobileImpersonationStatus)
                      const canWithdrawMobileTenantImpersonation = isImpersonationWithdrawable(mobileImpersonationStatus)
                      const canToggleChildren = isReseller && row.hasChildren && !resellerSearchTerm.trim()
                      return (
                        <div className="platform-mobile-item" key={`reseller-mobile-${rowKey}`} role="listitem">
                          <button
                            className="platform-mobile-summary"
                            type="button"
                            onClick={() => {
                              if (canToggleChildren) toggleResellerOrgResellerExpand(item.user_id)
                              setExpandedResellerMobileRowId((prev) => (prev === rowKey ? '' : rowKey))
                            }}
                            aria-expanded={isExpanded}
                          >
                            <span className={`platform-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                            <span className="platform-mobile-name" style={{ paddingLeft: `${row.level * 14}px` }}>
                              {isReseller ? highlightSearchSubstring(item.name || '-', resellerSearchTerm) : highlightSearchSubstring(item.organization_name || '-', resellerSearchTerm)}
                            </span>
                            <span className="platform-mobile-email">{highlightSearchSubstring(item.email || '-', resellerSearchTerm)}</span>
                            <StatusBadge className="platform-mobile-status" tone={isReseller ? getAdminStatusMeta(item.status).tone : getImpersonationStatusMeta(getResellerImpersonationStatus(item)).tone}>
                              {isReseller ? highlightSearchSubstring(getAdminStatusMeta(item.status).label, resellerSearchTerm) : highlightSearchSubstring(getImpersonationStatusMeta(getResellerImpersonationStatus(item)).label, resellerSearchTerm)}
                            </StatusBadge>
                          </button>
                          {isExpanded && (
                            <div className="platform-mobile-details">
                              {isEditing ? (
                                <div className="platform-mobile-edit">
                                  {isReseller ? (
                                    <>
                                      <input type="text" value={editResellerAdmin.name} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                      <input type="text" value={editResellerAdmin.contactName} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, contactName: e.target.value }))} />
                                      <span data-i18n-skip="1">{editResellerAdmin.email}</span>
                                      <div className="platform-mobile-actions">
                                        <Button onClick={handleSaveResellerAdminEdit} variant="primary">Speichern</Button>
                                        <Button onClick={() => setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })} variant="secondary">Abbrechen</Button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <input type="text" value={editResellerTenantAdmin.organizationName} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, organizationName: e.target.value }))} />
                                      <input type="text" value={editResellerTenantAdmin.name} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                      <span data-i18n-skip="1">{editResellerTenantAdmin.email}</span>
                                      <div className="platform-mobile-actions">
                                        <Button onClick={handleSaveResellerTenantAdminEdit} variant="primary">Speichern</Button>
                                        <Button onClick={() => { setEditResellerTenantAdmin({ userId: '', tenantId: '', organizationName: '', name: '', email: '' }) }} variant="secondary">Abbrechen</Button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="platform-mobile-actions">
                                  {canManageRow && (
                                    <Button onClick={() => (isReseller ? setEditResellerAdmin({ userId: item.user_id, name: item.name || '', contactName: item.contact_name || '', email: item.email || '' }) : setEditResellerTenantAdmin({ userId: item.user_id, tenantId: item.tenant_id, organizationName: item.organization_name || '', name: item.name || '', email: item.email || '' }))} variant="primary">Editieren</Button>
                                  )}
                                  {isReseller && String(item.status || '').toLowerCase() === 'initial' && (
                                    <Button onClick={() => handleResendInvitation(item.user_id)} disabled={resendInvitationBusyUserId === item.user_id} variant="primary">
                                      {'Einladung erneut senden'}
                                    </Button>
                                  )}
                                  {!isReseller && String(item.status || '').toLowerCase() === 'initial' && (
                                    <Button onClick={() => handleResendTenantAdminInvitation(item.user_id, true)} disabled={resendInvitationBusyUserId === item.user_id} variant="primary">
                                      {'Einladung erneut senden'}
                                    </Button>
                                  )}
                                  {isReseller && subResellerMobileActions.showStart && (
                                    <Button onClick={() => handleImpersonateResellerUser(item.user_id)} disabled={uploadBusy || subResellerMobileActions.startDisabled} variant="primary">Impersonieren</Button>
                                  )}
                                  {isReseller && subResellerMobileActions.showRequest && (
                                    <Button onClick={() => handleRequestSubResellerImpersonation(item.user_id)} variant="primary">
                                      Impersonation anfragen
                                    </Button>
                                  )}
                                  {isReseller && subResellerMobileActions.showWithdraw && (
                                    <Button onClick={() => handleWithdrawSubResellerImpersonation(item.user_id, mobileImpersonationStatus, item.name || item.email)} variant="primary">
                                      {subResellerMobileActions.withdrawsApproval ? 'Impersonation-Freigabe zurückziehen' : 'Impersonation-Anfrage zurückziehen'}
                                    </Button>
                                  )}
                                  {canStartMobileTenantImpersonation && (
                                    <Button onClick={() => handleImpersonateTenant(item.tenant_id)} disabled={uploadBusy || String(item.status || '').toLowerCase() !== 'active'} variant="primary">Impersonieren</Button>
                                  )}
                                  {canChangeMobileTenantImpersonation && canRequestMobileTenantImpersonation && (
                                    <Button onClick={() => handleRequestResellerTenantImpersonation(item.tenant_id)} variant="primary">
                                      Impersonation anfragen
                                    </Button>
                                  )}
                                  {canChangeMobileTenantImpersonation && canWithdrawMobileTenantImpersonation && (
                                    <Button onClick={() => handleWithdrawResellerTenantImpersonation(item.tenant_id, mobileImpersonationStatus, item.organization_name || item.name || item.email)} variant="primary">
                                      {mobileImpersonationStatus === IMPERSONATION_STATUS_APPROVED ? 'Impersonation-Freigabe zurückziehen' : 'Impersonation-Anfrage zurückziehen'}
                                    </Button>
                                  )}
                                  {canManageRow && (
                                    <Button
                                      data-i18n-live="1"
                                      onClick={() => (isReseller ? handleDeleteResellerAdmin(item.user_id, item.name || item.email || 'Reseller') : handleToggleResellerTenantStatus(item))}
                                      variant="primary"
                                      tone={String(item.status || '').toLowerCase() === 'active' ? 'warning' : 'default'}
                                    >
                                      {String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {!visibleResellerOrganizationRows.length && (
                    <div className="hint">
                      {resellerSearchTerm.trim() ? 'Keine passenden Einträge gefunden' : 'Keine Organisationen oder Reseller vorhanden'}
                    </div>
                  )}
                  <ExpandableButton
                    onClick={() => toggleAdminCreateForm('resellerTenantAdmin', showResellerTenantCreate, setShowResellerTenantCreate)}
                    expanded={showResellerTenantCreate}
                  >
                    {showResellerTenantCreate ? 'Organisationsanlage ausblenden' : 'Kundenorganisation anlegen'}
                  </ExpandableButton>
                  {showResellerTenantCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div>
                          <label>Organisationsname</label>
                          <input
                            type="text"
                            placeholder="Organisation"
                            value={resellerTenantOrgName}
                            className={adminFieldErrors.resellerTenantAdmin.organizationName ? 'field-input-error' : ''}
                            onChange={(e) => { setResellerTenantOrgName(e.target.value); clearAdminFormFieldError('resellerTenantAdmin', 'organizationName') }}
                          />
                        </div>
                        <div>
                          <label>{'Name Tenant-Admin'}</label>
                          <input
                            type="text"
                            placeholder="Klartext Name"
                            value={resellerTenantName}
                            className={adminFieldErrors.resellerTenantAdmin.name ? 'field-input-error' : ''}
                            onChange={(e) => { setResellerTenantName(e.target.value); clearAdminFormFieldError('resellerTenantAdmin', 'name') }}
                          />
                        </div>
                        <div>
                          <label>{'E-Mail Tenant-Admin'}</label>
                          <input
                            type="email"
                            placeholder="admin@tenant.tld"
                            value={resellerTenantEmail}
                            className={adminFieldErrors.resellerTenantAdmin.email ? 'field-input-error' : ''}
                            onChange={(e) => { setResellerTenantEmail(e.target.value); clearAdminFormFieldError('resellerTenantAdmin', 'email') }}
                          />
                        </div>
                        <label className="multi-select-option">
                          <input
                            type="checkbox"
                            checked={resellerTenantRequestImpersonation}
                            onChange={(e) => setResellerTenantRequestImpersonation(e.target.checked)}
                          />
                          <span>Impersonation anfragen</span>
                        </label>
                      </div>
                      <Button className="tenant-create-submit" onClick={handleCreateResellerTenantAdmin} variant="primary">
                        Anlegen
                      </Button>
                    </div>
                  )}
                  <ExpandableButton onClick={() => toggleAdminCreateForm('resellerAdmin', showResellerSubCreate, setShowResellerSubCreate)} expanded={showResellerSubCreate}>
                    {showResellerSubCreate ? 'Reseller-Anlage ausblenden' : 'Reseller anlegen'}
                  </ExpandableButton>
                  {showResellerSubCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div><label>Reseller Name (optional)</label><input type="text" placeholder="Reseller Name" value={resellerAdminName} onChange={(e) => setResellerAdminName(e.target.value)} /></div>
                        <div><label>Name Reseller-Admin (optional)</label><input type="text" placeholder="Klartext Name" value={resellerAdminContactName} onChange={(e) => setResellerAdminContactName(e.target.value)} /></div>
                        <div><label>E-Mail</label><input className={adminFieldErrors.resellerAdmin.email ? 'field-input-error' : ''} type="email" placeholder="reseller@partner.tld" value={resellerAdminEmail} onChange={(e) => { setResellerAdminEmail(e.target.value); clearAdminFormFieldError('resellerAdmin', 'email') }} /></div>
                        <label className="multi-select-option">
                          <input type="checkbox" checked={resellerSubRequestImpersonation} onChange={(e) => setResellerSubRequestImpersonation(e.target.checked)} />
                          <span>Impersonation anfragen</span>
                        </label>
                      </div>
                      <Button className="tenant-create-submit" onClick={handleCreateResellerAdmin} variant="primary">Anlegen</Button>
                    </div>
                  )}
                  {platformError && <div className="hint form-error-hint">{platformError}</div>}
                  {platformSuccess && <div className="hint">{platformSuccess}</div>}
                </section>

                <section className="card reseller-impersonation-panel">
                  <div className="card-title card-title-with-badge">
                    <span>Impersonation-Anfragen</span>
                    {pendingImpersonationPermissionCount > 0 && (
                      <span className="impersonation-request-count">{pendingImpersonationPermissionCount}</span>
                    )}
                  </div>
                  <div className="archive-search">
                    <ClearableInput
                      type="search"
                      placeholder="Suche (Name, E-Mail)"
                      value={resellerImpersonationSearchTerm}
                      onValueChange={setResellerImpersonationSearchTerm}
                    />
                  </div>
                  <div className="organization-admin-scroll-window reseller-impersonation-scroll-window">
                    <div className="table tenant-admin-table reseller-impersonation-table">
                      <div className="table-header">
                        <span>Anfrager</span><span>E-Mail</span><span>Status</span><span>Aktionen</span>
                      </div>
                      {filteredResellerImpersonationPermissions.map((item) => {
                        const meta = getImpersonationStatusMeta(item.status)
                        const decision = getImpersonationPermissionDecision(item.status)
                        const actionLabel = decision?.action === IMPERSONATION_ACTION_REVOKE ? 'Widerrufen' : 'Freigeben'
                        return (
                          <div className="table-row" key={item.permission_id}>
                            <span>{highlightSearchSubstring(item.requester_name || '-', resellerImpersonationSearchTerm, true)}</span>
                            <span>{highlightSearchSubstring(item.requester_email || '-', resellerImpersonationSearchTerm, true)}</span>
                            <span><StatusBadge className="impersonation-status-badge" variant="compact-fixed" tone={meta.tone} title={meta.title}>{meta.label}</StatusBadge></span>
                            <span className="tenant-admin-actions tenant-admin-actions-inline">
                              {decision && (
                                <IconButton
                                  icon={decision.action === IMPERSONATION_ACTION_REVOKE ? 'close' : 'approve'}
                                  label={actionLabel}
                                  tone={decision.action === IMPERSONATION_ACTION_REVOKE ? 'danger' : 'positive'}
                                  onClick={() => handleUpdateResellerImpersonationPermissionStatus(item.permission_id, decision.nextStatus, item.requester_name || item.requester_email)}
                                />
                              )}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                    <div className="reseller-impersonation-mobile-list" role="list">
                      {filteredResellerImpersonationPermissions.map((item) => {
                        const meta = getImpersonationStatusMeta(item.status)
                        const decision = getImpersonationPermissionDecision(item.status)
                        const actionLabel = decision?.action === IMPERSONATION_ACTION_REVOKE ? 'Widerrufen' : 'Freigeben'
                        return (
                          <div className="reseller-impersonation-mobile-item" key={`mobile-${item.permission_id}`} role="listitem">
                            <div className="reseller-impersonation-mobile-row">
                              <span className="reseller-impersonation-mobile-label">Anfrager</span>
                              <strong>{highlightSearchSubstring(item.requester_name || '-', resellerImpersonationSearchTerm, true)}</strong>
                            </div>
                            <div className="reseller-impersonation-mobile-row">
                              <span className="reseller-impersonation-mobile-label">E-Mail</span>
                              <span>{highlightSearchSubstring(item.requester_email || '-', resellerImpersonationSearchTerm, true)}</span>
                            </div>
                            <div className="reseller-impersonation-mobile-row">
                              <span className="reseller-impersonation-mobile-label">Status</span>
                              <StatusBadge className="impersonation-status-badge" variant="compact-fixed" tone={meta.tone} title={meta.title}>{meta.label}</StatusBadge>
                            </div>
                            {decision && (
                              <Button
                                onClick={() => handleUpdateResellerImpersonationPermissionStatus(item.permission_id, decision.nextStatus, item.requester_name || item.requester_email)} variant="primary">
                                {actionLabel}
                              </Button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    {!resellerImpersonationPermissions.length && <div className="hint empty-list-hint">Keine Impersonation-Anfragen vorhanden</div>}
                  </div>
                  {resellerImpersonationPermissions.length > 0 && !filteredResellerImpersonationPermissions.length && <div className="hint">Keine passenden Anfragen gefunden</div>}
                  {resellerImpersonationError && <div className="hint form-error-hint">{resellerImpersonationError}</div>}
                </section>
                </>
              )}

              {(isTenantAdmin || isTenantUser) && (
              <>
              <section className={`grid tenant-admin-layout-grid${isStructureNavigation ? ' is-structure-view' : ''}`}>
                {!isStructureNavigation && (
                <div className="card tenant-organization-panel">
                  <div className="card-title">Organisation</div>
                  <ExpandableButton
                    className="admin-compact-toggle"
                    onClick={() => setShowTenantOrganizationPanel((prev) => !prev)}
                    expanded={showTenantOrganizationPanel}
                  >
                    {showTenantOrganizationPanel ? 'Organisation ausblenden' : 'Organisation bearbeiten'}
                  </ExpandableButton>
                  {showTenantOrganizationPanel && (
                  <div className="organization-tenant-row">
                    {!editTenant && <span className="organization-tenant-name">{tenantDisplayName}</span>}
                    {canRenameOrganization && editTenant ? (
                      <div className="organization-tenant-edit">
                        <input
                          type="text"
                          value={tenantNameEdit}
                          onChange={(e) => setTenantNameEdit(e.target.value)}
                        />
                        <Button
                          onClick={async () => {
                            await handleRenameTenant()
                            setEditTenant(false)
                          }} variant="primary">
                          Speichern
                        </Button>
                        <Button
                          onClick={() => {
                            setTenantNameEdit(tenantDisplayName === '-' ? '' : tenantDisplayName)
                            setEditTenant(false)
                          }} variant="secondary">
                          Abbrechen
                        </Button>
                      </div>
                    ) : (
                      canRenameOrganization && (
                        <span className="organization-tenant-actions">
                          {renderEntityActionIcon('edit', 'Umbenennen', () => setEditTenant(true))}
                        </span>
                      )
                    )}
                  </div>
                  )}
                </div>
                )}
                {isStructureNavigation && (
                <div className="card tenant-structure-panel">
                  <div className="card-title">Unternehmensstruktur</div>
                  <div className="archive-search">
                    <ClearableInput
                      type="search"
                      placeholder="Suche (Unternehmen, Betriebsstätte, POS)"
                      value={organizationSearchTerm}
                      onValueChange={setOrganizationSearchTerm}
                    />
                  </div>
                  <div className="organization-structure-header" aria-hidden="true">
                    <span>Name</span>
                    <span>Aktionen</span>
                  </div>
                  {companies.length === 0 && (
                    <>
                      <div className="hint">Keine Unternehmen vorhanden</div>
                      {canEditCompanies && (
                        <button
                          className="link sb-link organization-empty-create-action"
                          type="button"
                          onClick={handleToggleCompanyCreate}
                        >
                          + Erstes Unternehmen anlegen
                        </button>
                      )}
                    </>
                  )}
                  {companies.length > 0 && filteredOrganizationCompanies.length === 0 && (
                    <div className="hint">Nicht gefunden</div>
                  )}
                  {filteredOrganizationCompanies.length > 0 && (
                    <div className={`tree organization-tree-scroll-window${editPos.id ? ' is-pos-editing' : ''}`}>
                      {filteredOrganizationCompanies.map((company) => {
                        const companySites = company.sites || []
                        const companySearchExpansion = organizationSearchExpansion.query === organizationSearchQuery
                          ? organizationSearchExpansion.companies[company.company_id]
                          : undefined
                        const companyExpanded = isOrganizationSearchActive
                          ? (companySearchExpansion ?? company.hasSearchMatchInDescendants)
                          : (expandedCompanies[company.company_id] ?? false)
                        const hasNoSites = companySites.length === 0
                        const showCompanyCreateHint = canEditSites && !isOrganizationSearchActive && !companyExpanded && hasNoSites
                        return (
                          <div className="tree-node" key={company.company_id}>
                            <div
                              className="tree-row tree-company"
                              onClick={editCompany.id === company.company_id ? undefined : () => toggleCompanyExpand(company.company_id, companyExpanded)}
                            >
                              {editCompany.id === company.company_id ? (
                                <div className="tree-edit-line">
                                  <input
                                    type="text"
                                    value={editCompany.name}
                                    onChange={(e) => setEditCompany({ id: company.company_id, name: e.target.value })}
                                  />
                                  <Button onClick={() => handleRenameCompany(company.company_id, editCompany.name)} variant="primary">Speichern</Button>
                                  <Button onClick={() => setEditCompany({ id: '', name: '' })} variant="secondary">Abbrechen</Button>
                                </div>
                              ) : (
                                <>
                                  <span className={`tree-row-main${showCompanyCreateHint ? ' has-create-hint' : ''}`}>
                                    <span className="tree-row-main-top">
                                      <button
                                        className="tree-toggle"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          toggleCompanyExpand(company.company_id, companyExpanded)
                                        }}
                                        aria-label={companyExpanded
                                          ? formatUiTemplate('aria.company.collapse', { name: company.name })
                                          : formatUiTemplate('aria.company.expand', { name: company.name })}
                                        aria-expanded={companyExpanded}
                                      >
                                        <span className="tree-toggle-icon" aria-hidden="true">{companyExpanded ? '▾' : '▸'}</span>
                                      </button>
                                      <span>{highlightSearchSubstring(company.name, organizationSearchTerm, true)}</span>
                                    </span>
                                    {showCompanyCreateHint && (
                                      <span className="tree-row-create-hint">(+ Erste Betriebsstätte anlegen)</span>
                                    )}
                                  </span>
                                  {canEditCompanies && (
                                    <span className="tree-row-actions">
                                      {renderEntityActionIcon('edit', 'Umbenennen', (e) => {
                                          e.stopPropagation()
                                          setEditCompany({ id: company.company_id, name: company.name })
                                        })}
                                      {renderEntityActionIcon('delete', 'Löschen', (e) => {
                                          e.stopPropagation()
                                          handleDeleteCompany(company.company_id, company.name)
                                        })}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            {companyExpanded && (
                              <>
                                {canEditSites && !isOrganizationSearchActive && (
                                  <button
                                    className="link tree-action tree-site sb-link"
                                    type="button"
                                    onClick={() => {
                                      setSelectedCompany((prev) => (prev === company.company_id ? '' : company.company_id))
                                      setSiteName('')
                                      setSiteNameInvalid(false)
                                      setSiteCreateError('')
                                    }}
                                  >
                                    {hasNoSites ? '+ Erste Betriebsstätte anlegen' : '+ Betriebsstätte anlegen'}
                                  </button>
                                )}
                                {canEditSites && !isOrganizationSearchActive && selectedCompany === company.company_id && (
                                  <div className="tree-inline-form tree-site">
                                    <input
                                      type="text"
                                      placeholder="Betriebsstätte"
                                      value={siteName}
                                      className={siteNameInvalid ? 'field-input-error' : ''}
                                      onChange={(e) => {
                                        setSiteName(e.target.value)
                                        setSiteNameInvalid(false)
                                        setSiteCreateError('')
                                      }}
                                    />
                                    <div className="inline-form-actions">
                                      <Button
                                        onClick={async () => {
                                          if (await handleCreateSite(company.company_id, siteName)) {
                                            setSiteName('')
                                            setSiteCreateError('')
                                            setSelectedCompany('')
                                          }
                                        }} variant="primary">
                                        Anlegen
                                      </Button>
                                      <Button
                                        onClick={() => {
                                          setSiteName('')
                                          setSiteNameInvalid(false)
                                          setSiteCreateError('')
                                          setSelectedCompany('')
                                        }} variant="secondary">
                                        Abbrechen
                                      </Button>
                                    </div>
                                    {siteCreateError && <div className="hint form-error-hint">{siteCreateError}</div>}
                                  </div>
                                )}
                                {companySites.length === 0 && (
                                  canEditSites && !isOrganizationSearchActive ? (
                                    <button
                                      className="link tree-row tree-empty tree-empty-action sb-link"
                                      type="button"
                                      onClick={() => {
                                        setSelectedCompany(company.company_id)
                                        setSiteName('')
                                        setSiteNameInvalid(false)
                                        setSiteCreateError('')
                                      }}
                                    >
                                      Keine Betriebsstätten
                                    </button>
                                  ) : (
                                    <div className="tree-row tree-empty">Keine Betriebsstätten</div>
                                  )
                                )}
                                {companySites.map((site) => {
                                  const sitePos = site.posItems || []
                                  const hasNoPos = sitePos.length === 0
                                  const siteSearchExpansion = organizationSearchExpansion.query === organizationSearchQuery
                                    ? organizationSearchExpansion.sites[site.site_id]
                                    : undefined
                                  const siteExpanded = isOrganizationSearchActive
                                    ? (siteSearchExpansion ?? site.hasSearchMatchInDescendants)
                                    : (expandedSites[site.site_id] ?? true)
                                  const showSiteCreateHint = canEditPos && !isOrganizationSearchActive && !siteExpanded && hasNoPos
                                  return (
                                    <div className="tree-node" key={site.site_id}>
                                      <div
                                        className="tree-row tree-site"
                                        onClick={editSite.id === site.site_id ? undefined : () => toggleSiteExpand(site.site_id, siteExpanded)}
                                      >
                                        {editSite.id === site.site_id ? (
                                          <div className="tree-edit-line">
                                            <input
                                              type="text"
                                              value={editSite.name}
                                              onChange={(e) => setEditSite({ id: site.site_id, name: e.target.value })}
                                            />
                                            <Button onClick={() => handleRenameSite(site.site_id, editSite.name)} variant="primary">Speichern</Button>
                                            <Button onClick={() => setEditSite({ id: '', name: '' })} variant="secondary">Abbrechen</Button>
                                          </div>
                                        ) : (
                                          <>
                                            <span className={`tree-row-main${showSiteCreateHint ? ' has-create-hint' : ''}`}>
                                              <span className="tree-row-main-top">
                                                <button
                                                  className="tree-toggle"
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation()
                                                    toggleSiteExpand(site.site_id, siteExpanded)
                                                  }}
                                                  aria-label={siteExpanded
                                                    ? formatUiTemplate('aria.site.collapse', { name: site.name })
                                                    : formatUiTemplate('aria.site.expand', { name: site.name })}
                                                  aria-expanded={siteExpanded}
                                                >
                                                  <span className="tree-toggle-icon" aria-hidden="true">{siteExpanded ? '▾' : '▸'}</span>
                                                </button>
                                                <span>{highlightSearchSubstring(site.name, organizationSearchTerm, true)}</span>
                                              </span>
                                              {showSiteCreateHint && (
                                                <span className="tree-row-create-hint">(+ Erste POS anlegen)</span>
                                              )}
                                            </span>
                                            {canEditSites && (
                                              <span className="tree-row-actions">
                                                {renderEntityActionIcon('edit', 'Umbenennen', (e) => {
                                                    e.stopPropagation()
                                                    setEditSite({ id: site.site_id, name: site.name })
                                                  })}
                                                {renderEntityActionIcon('delete', 'Löschen', (e) => {
                                                    e.stopPropagation()
                                                    handleDeleteSite(site.site_id, site.name)
                                                  })}
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </div>
                                      {siteExpanded && (
                                        <>
                                          {canEditPos && !isOrganizationSearchActive && (
                                            <button
                                              className="link tree-action tree-pos sb-link"
                                              type="button"
                                              onClick={() => openCreatePosDialog(createPosDialog.siteId === site.site_id ? '' : site.site_id)}
                                            >
                                              {hasNoPos ? '+ Erste POS anlegen' : '+ POS anlegen'}
                                            </button>
                                          )}
                                          {canEditPos && !isOrganizationSearchActive && createPosDialog.siteId === site.site_id && (
                                            <div className="tree-pos-settings">
                                              {createPosDialog.apiKey ? (
                                                <>
                                                  <div className="api-key-setup-layout">
                                                    <div className="api-key-setup-secret">
                                                      <div className="table onboarding-code-table api-key-secret-table" aria-label={formatUiTemplate('pos.api_key.aria', { pos: createdPosDisplayName })}>
                                                        <div className="table-header">
                                                          <span>{'API-Key für'} <strong>{createdPosDisplayName}</strong></span>
                                                        </div>
                                                        <div className="table-row">
                                                          <span className="onboarding-code-cell">
                                                            <span className="onboarding-code-value">{createPosDialog.apiKey}</span>
                                                            {createPosDialog.copied ? (
                                                              <span className="code-copy-confirmation">
                                                                <SymbolIcon icon="approve" />
                                                              </span>
                                                            ) : (
                                                              <IconButton
                                                                icon="copy"
                                                                label="Kopieren"
                                                                ariaLabel="API-Key kopieren"
                                                                onClick={handleCopyCreatedPosApiKey}
                                                                variant="quiet"
                                                                className="code-copy-button"
                                                              />
                                                            )}
                                                          </span>
                                                        </div>
                                                      </div>
                                                      {createPosDialog.status && <div className="hint form-error-hint">{createPosDialog.status}</div>}
                                                    </div>
                                                    <div className="api-key-setup-guidance">
                                                      <div className="api-key-setup-title">{'API-Key einrichten'}</div>
                                                      <div className="api-key-setup-description">
                                                        <p>{'Die POS wurde erfolgreich erstellt. Für den sicheren Upload und die Archivierung von Artefakten benötigt sie einen API-Key.'}</p>
                                                        <p>
                                                          {formatUiTemplate('pos.api_key.setup.belongs_to.prefix')}
                                                          <strong>{createdPosSerial}</strong>
                                                          {formatUiTemplate('pos.api_key.setup.belongs_to.suffix')}
                                                        </p>
                                                      </div>
                                                      <div className="hint api-key-secret-warning" role="alert">
                                                        <SymbolIcon icon="warning" className="api-key-secret-warning-icon" />
                                                        {'Der API-Key wird nur einmal angezeigt. Kopieren Sie ihn jetzt und bewahren Sie ihn sicher auf. Geben Sie ihn nicht an Dritte weiter und entfernen Sie ihn nach der Einrichtung aus allen temporären Ablagen.'}
                                                      </div>
                                                    </div>
                                                    <div className="inline-form-actions">
                                                      <Button onClick={resetCreatePosDialog} variant="primary">Fertig</Button>
                                                    </div>
                                                  </div>
                                                </>
                                              ) : (
                                                <>
                                              <label>POS Seriennummer</label>
                                              <input
                                                type="text"
                                                className={createPosDialog.fieldErrors?.posId ? 'field-input-error' : ''}
                                                value={createPosDialog.posId}
                                                onChange={(e) =>
                                                  setCreatePosDialog((prev) => ({
                                                    ...prev,
                                                    posId: e.target.value,
                                                    status: '',
                                                    fieldErrors: {}
                                                  }))
                                                }
                                                placeholder="POS Seriennummer"
                                              />
                                              <label>POS Bezeichnung (optional)</label>
                                              <input
                                                type="text"
                                                value={createPosDialog.posLabel}
                                                onChange={(e) => setCreatePosDialog((prev) => ({ ...prev, posLabel: e.target.value, status: '' }))}
                                                placeholder="POS Bezeichnung"
                                              />
                                              <label>TSE Typ</label>
                                              <select
                                                value={createPosDialog.tseType}
                                                onChange={(e) =>
                                                  setCreatePosDialog((prev) => ({
                                                    ...prev,
                                                    tseType: e.target.value,
                                                    status: ''
                                                  }))
                                                }
                                              >
                                                {POS_TSE_TYPE_OPTIONS.map((option) => (
                                                  <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                              </select>
                                              {isSwissbitCloudPosTseType(createPosDialog.tseType) ? (
                                                <>
                                                  <label>TSE Seriennummer</label>
                                                  <input
                                                    type="text"
                                                    className={createPosDialog.fieldErrors?.tseSerialNumber ? 'field-input-error' : ''}
                                                    value={createPosDialog.tseSerialNumber}
                                                    onChange={(e) =>
                                                      setCreatePosDialog((prev) => ({
                                                        ...prev,
                                                        tseSerialNumber: e.target.value,
                                                        status: '',
                                                        fieldErrors: {}
                                                      }))
                                                    }
                                                    placeholder="TSE Seriennummer"
                                                  />
                                                </>
                                              ) : isFiskalyCloudPosTseType(createPosDialog.tseType) ? (
                                                <div className="hint">fiskaly Cloud TSE ist aktuell nicht unterstützt.</div>
                                              ) : (
                                                <>
                                                  <label>TSE Seriennummer (optional)</label>
                                                  <input
                                                    type="text"
                                                    className={createPosDialog.fieldErrors?.tseSerialNumber ? 'field-input-error' : ''}
                                                    value={createPosDialog.tseSerialNumber}
                                                    onChange={(e) =>
                                                      setCreatePosDialog((prev) => ({
                                                        ...prev,
                                                        tseSerialNumber: e.target.value,
                                                        status: '',
                                                        fieldErrors: {}
                                                      }))
                                                    }
                                                    placeholder="TSE Seriennummer"
                                                  />
                                                </>
                                              )}
                                              {createPosDialog.status && <div className="hint form-error-hint">{createPosDialog.status}</div>}
                                              <div className="inline-form-actions">
                                                <Button onClick={handleCreatePos} variant="primary">Anlegen</Button>
                                                <Button onClick={resetCreatePosDialog} variant="secondary">Abbrechen</Button>
                                              </div>
                                                </>
                                              )}
                                            </div>
                                          )}
                                          {sitePos.length === 0 && (
                                            canEditPos && !isOrganizationSearchActive ? (
                                              <button
                                                className="link tree-row tree-empty tree-empty-action sb-link"
                                                type="button"
                                                onClick={() => openCreatePosDialog(site.site_id)}
                                              >
                                                Keine POS
                                              </button>
                                            ) : (
                                              <div className="tree-row tree-empty">Keine POS</div>
                                            )
                                          )}
                                          {sitePos.map((pos) => (
                                            <div className="tree-node" key={pos.pos_id}>
                                              <div
                                                className={`tree-row tree-pos${canEditPos && editPos.id !== pos.pos_id ? ' tree-pos-row-clickable' : ''}`}
                                                onClick={canEditPos && editPos.id !== pos.pos_id ? () => handleEditPosOpen(pos) : undefined}
                                              >
                                                <span>{highlightSearchSubstring(pos.pos_label || pos.pos_serial, organizationSearchTerm, true)}</span>
                                                {canEditPos && editPos.id !== pos.pos_id && (
                                                  <span className="tree-row-actions">
                                                    {renderEntityActionIcon('edit', 'Editieren', (e) => {
                                                        e.stopPropagation()
                                                        if (editPos.id === pos.pos_id) {
                                                          resetEditPos()
                                                          return
                                                        }
                                                        handleEditPosOpen(pos)
                                                      })}
                                                    {renderEntityActionIcon('delete', 'Löschen', (e) => {
                                                        e.stopPropagation()
                                                        handleDeletePos(pos.pos_id, pos.pos_label || pos.pos_serial)
                                                      })}
                                                  </span>
                                                )}
                                              </div>
                                              {canEditPos && editPos.id === pos.pos_id && (
                                                <div className="tree-pos-settings">
                                                  <label>POS Seriennummer</label>
                                                  <div className="tree-pos-settings-readonly">{editPos.posSerial}</div>
                                                  <label>POS Bezeichnung (optional)</label>
                                                  <input
                                                    type="text"
                                                    value={editPos.posLabel}
                                                    onChange={(e) => setEditPos((prev) => ({ ...prev, posLabel: e.target.value, status: '' }))}
                                                    placeholder="POS Bezeichnung"
                                                  />
                                                  <label>TSE Typ</label>
                                                  <select
                                                    value={editPos.tseType}
                                                    onChange={(e) =>
                                                      setEditPos((prev) => ({
                                                        ...prev,
                                                        tseType: e.target.value,
                                                        status: ''
                                                      }))
                                                    }
                                                  >
                                                    {POS_TSE_TYPE_OPTIONS.map((option) => (
                                                      <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                  </select>
                                                  {isSwissbitCloudPosTseType(editPos.tseType) ? (
                                                    <>
                                                      <label>TSE Seriennummer</label>
                                                      <input
                                                        type="text"
                                                        className={editPos.fieldErrors?.tseSerialNumber ? 'field-input-error' : ''}
                                                        value={editPos.tseSerialNumber}
                                                        onChange={(e) =>
                                                          setEditPos((prev) => ({
                                                            ...prev,
                                                            tseSerialNumber: e.target.value,
                                                            status: '',
                                                            fieldErrors: {}
                                                          }))
                                                        }
                                                        placeholder="TSE Seriennummer"
                                                      />
                                                    </>
                                                  ) : isFiskalyCloudPosTseType(editPos.tseType) ? (
                                                    <div className="hint">fiskaly Cloud TSE ist aktuell nicht unterstützt.</div>
                                                  ) : (
                                                    <>
                                                      <label>TSE Seriennummer (optional)</label>
                                                      <input
                                                        type="text"
                                                        className={editPos.fieldErrors?.tseSerialNumber ? 'field-input-error' : ''}
                                                        value={editPos.tseSerialNumber}
                                                        onChange={(e) =>
                                                          setEditPos((prev) => ({
                                                            ...prev,
                                                            tseSerialNumber: e.target.value,
                                                            status: '',
                                                            fieldErrors: {}
                                                          }))
                                                        }
                                                        placeholder="TSE Seriennummer"
                                                      />
                                                    </>
                                                  )}
                                                  {editPos.status && <div className="hint form-error-hint">{editPos.status}</div>}
                                                  <div className="inline-form-actions">
                                                    <Button onClick={handleSavePosSettings} variant="primary">Speichern</Button>
                                                    <Button onClick={resetEditPos} variant="secondary">Abbrechen</Button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </>
                                      )}
                                    </div>
                                  )
                                })}
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                  <div className="tenant-structure-actions">
                    {canEditCompanies && (
                    <ExpandableButton
                      className="tenant-structure-toggle-btn"
                      onClick={handleToggleCompanyCreate}
                      expanded={showCompanyCreate}
                    >
                      Unternehmen anlegen
                    </ExpandableButton>
                    )}
                    {canEditCompanies && showCompanyCreate && (
                      <div className="tree-inline-form">
                        <input
                          type="text"
                          placeholder="Unternehmensname"
                          value={companyName}
                          className={companyNameInvalid ? 'field-input-error' : ''}
                          onChange={(e) => {
                            setCompanyName(e.target.value)
                            setCompanyNameInvalid(false)
                            setCompanyCreateError('')
                          }}
                        />
                        <select
                          value={companyCountry}
                          onChange={(e) => setCompanyCountry(e.target.value)}
                          disabled={!selectableCountries.length}
                        >
                          {!selectableCountries.length && <option value="">Kein Land mit aktivem Profil</option>}
                          {selectableCountries.map((country) => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                        <div className="inline-form-actions">
                          <Button
                            onClick={async () => {
                              if (await handleCreateCompany(companyName, companyCountry)) {
                                setCompanyName('')
                                setCompanyCreateError('')
                                setShowCompanyCreate(false)
                              }
                            }} variant="primary">
                            Anlegen
                          </Button>
                          <Button
                            onClick={() => {
                              setCompanyName('')
                              setShowCompanyCreate(false)
                            }} variant="secondary">
                            Abbrechen
                          </Button>
                        </div>
                        {companyCreateError && <div className="hint form-error-hint">{companyCreateError}</div>}
                      </div>
                    )}
                  </div>
                </div>
                )}
                {!isStructureNavigation && canManageUsers && (
                <div className="card tenant-user-panel">
                  <div className="card-title">Nutzerverwaltung</div>
                  <ExpandableButton
                    className="admin-compact-toggle"
                    onClick={() => setShowTenantUsersPanel((prev) => !prev)}
                    expanded={showTenantUsersPanel}
                  >
                    {showTenantUsersPanel ? 'Nutzerverwaltung ausblenden' : 'Nutzerverwaltung öffnen'}
                  </ExpandableButton>
                  {showTenantUsersPanel && (
                  <>
                  <div className="archive-search">
                    <ClearableInput
                      type="search"
                      placeholder="Suche (Name, E-Mail, Status)"
                      value={tenantUsersSearchTerm}
                      onValueChange={setTenantUsersSearchTerm}
                    />
                  </div>
                  <div className="tenant-user-content">
                    <div className={`table tenant-user-table tenant-user-scroll-window${filteredTenantUsers.length ? '' : ' is-empty'}`}>
                      <div className="table-header">
                        <span>Nutzer</span><span>Status</span><span>Aktionen</span>
                      </div>
                      {filteredTenantUsers.map((item) => {
                        const actions = getTenantUserActions(item)
                        return (
                        <div className="table-row" key={item.user_id}>
                          {editTenantUser.userId === item.user_id ? (
                            <>
                              <span className="tenant-user-identity is-editing">
                                <input
                                  className="tenant-user-edit-input"
                                  type="text"
                                  value={editTenantUser.name}
                                  onChange={(e) => setEditTenantUser((prev) => ({ ...prev, name: e.target.value }))}
                                />
                                <span className="tenant-user-identity-email" data-i18n-skip="1">
                                  {editTenantUser.email}
                                </span>
                              </span>
                              <span className="tenant-user-status-hidden" aria-hidden="true" />
                              <span className="tenant-user-row-actions is-edit">
                                <Button onClick={handleSaveTenantUserEdit} variant="primary">Speichern</Button>
                              <Button onClick={() => setEditTenantUser({ userId: '', name: '', email: '', rights: TENANT_USER_RIGHTS_DEFAULT })} variant="secondary">Abbrechen</Button>
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="tenant-user-identity">
                                <span className="tenant-user-identity-name">{highlightSearchSubstring(item.name || '-', tenantUsersSearchTerm, true)}</span>
                                <span className="tenant-user-identity-email">{highlightSearchSubstring(item.email || '-', tenantUsersSearchTerm, true)}</span>
                              </span>
                              <span>
                                <StatusBadge tone={getTenantUserStatusMeta(item.status).tone}>
                                  {highlightSearchSubstring(getTenantUserStatusMeta(item.status).label, tenantUsersSearchTerm)}
                                </StatusBadge>
                              </span>
                              <span className="tenant-user-row-actions">
                                <ActionMenu actions={actions} />
                              </span>
                            </>
                          )}
                        </div>
                        )
                      })}
                    </div>
                    <div className="tenant-users-mobile-list" role="list">
                      {filteredTenantUsers.map((item) => {
                        const statusMeta = getTenantUserStatusMeta(item.status)
                        const isEditing = editTenantUser.userId === item.user_id
                        const isExpanded = isEditing || expandedTenantUserId === item.user_id
                        const detailsId = `tenant-user-mobile-details-${item.user_id}`
                        const actions = getTenantUserActions(item)
                        const resendAction = actions.find((action) => action.key === 'resend')
                        const primaryActions = actions.filter((action) => action.key !== 'resend')
                        return (
                          <div className="tenant-users-mobile-item" key={`mobile-user-${item.user_id}`} role="listitem">
                            <button
                              className="tenant-users-mobile-summary"
                              type="button"
                              onClick={() => !isEditing && setExpandedTenantUserId((prev) => (prev === item.user_id ? '' : item.user_id))}
                              aria-expanded={isExpanded}
                              aria-controls={detailsId}
                            >
                              <span className={`tenant-users-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                              <span className="tenant-users-mobile-name">{highlightSearchSubstring(item.name || '-', tenantUsersSearchTerm, true)}</span>
                              <span className="tenant-users-mobile-email">{highlightSearchSubstring(item.email || '-', tenantUsersSearchTerm, true)}</span>
                              <StatusBadge className="tenant-users-mobile-status" tone={statusMeta.tone}>{highlightSearchSubstring(statusMeta.label, tenantUsersSearchTerm)}</StatusBadge>
                            </button>
                            {isExpanded && (
                              <div className="tenant-users-mobile-details" id={detailsId}>
                                {isEditing ? (
                                  <div className="tenant-users-mobile-edit">
                                    <input
                                      className="tenant-user-edit-input"
                                      type="text"
                                      value={editTenantUser.name}
                                      onChange={(e) => setEditTenantUser((prev) => ({ ...prev, name: e.target.value }))}
                                    />
                                    <span className="tenant-user-identity-email" data-i18n-skip="1">
                                      {editTenantUser.email}
                                    </span>
                                    <div className="tenant-users-mobile-actions">
                                      <Button onClick={handleSaveTenantUserEdit} variant="primary">Speichern</Button>
                                      <Button
                                        onClick={() => setEditTenantUser({ userId: '', name: '', email: '', rights: TENANT_USER_RIGHTS_DEFAULT })} variant="secondary">
                                        Abbrechen
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {resendAction && (
                                      <Button
                                        className="tenant-users-mobile-resend"
                                        onClick={resendAction.onClick}
                                        disabled={resendAction.disabled} variant="primary">
                                        {resendAction.label}
                                      </Button>
                                    )}
                                    <div className="tenant-users-mobile-actions">
                                      {primaryActions.map((action) => (
                                        <Button
                                          className={`tenant-users-mobile-action${action.tone === 'positive' ? ' is-positive' : ''}`}
                                          key={action.key}
                                          onClick={action.onClick}
                                          disabled={action.disabled}
                                          variant="primary"
                                          tone={action.tone === 'danger' ? 'destructive' : action.tone === 'warning' ? 'warning' : 'default'}
                                        >
                                          {action.label}
                                        </Button>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    {!tenantUsers.length && <div className="hint empty-list-hint">Keine Nutzer vorhanden</div>}
                    {tenantUsers.length > 0 && !filteredTenantUsers.length && <div className="hint">Keine passenden Nutzer gefunden</div>}
                  </div>
                  <div className="tenant-user-actions">
                    <ExpandableButton
                      className="tenant-user-toggle-btn"
                      onClick={toggleTenantUserCreate}
                      expanded={showTenantUserCreate}
                    >
                      {showTenantUserCreate ? 'Nutzeranlage ausblenden' : 'Nutzer anlegen'}
                    </ExpandableButton>
                    {showTenantUserCreate && (
                      <div className="tenant-create-wrap">
                        <div className="form-grid">
                          <div>
                            <label>Name</label>
                            <input
                              type="text"
                              placeholder="Vorname Nachname"
                              value={tenantUserName}
                              className={adminFieldErrors.tenantUser.name ? 'field-input-error' : ''}
                              onChange={(e) => { setTenantUserName(e.target.value); clearAdminFormFieldError('tenantUser', 'name') }}
                            />
                          </div>
                          <div>
                            <label>E-Mail</label>
                            <input
                              type="email"
                              placeholder="user@tenant.tld"
                              value={tenantUserEmail}
                              className={adminFieldErrors.tenantUser.email ? 'field-input-error' : ''}
                              onChange={(e) => { setTenantUserEmail(e.target.value); clearAdminFormFieldError('tenantUser', 'email') }}
                            />
                          </div>
                        </div>
                        <div className="tenant-user-create-actions">
                          <Button className="tenant-user-create-submit" onClick={handleCreateTenantUser} variant="primary">Anlegen</Button>
                          <Button
                            onClick={() => openTenantUserRightsDialog('create', { rights: tenantUserRights })} variant="primary">
                            Rechte bearbeiten
                          </Button>
                        </div>
                      </div>
                    )}
                    {tenantUserError && <div className="hint form-error-hint">{tenantUserError}</div>}
                    {tenantUserSuccess && <div className="hint">{tenantUserSuccess}</div>}
                  </div>
                  <Modal open={rightsDialog.open} title="Nutzerrechte" onClose={closeTenantUserRightsDialog}>
                    {rightsDialog.mode === 'edit' && (
                      <div className="hint">{'Nutzer'}: {rightsDialog.label || '-'}</div>
                    )}
                    <div className="dialog-rights-list">
                      {TENANT_USER_RIGHTS_OPTIONS.map((opt) => (
                        <label key={opt.key} className="multi-select-option">
                          <input
                            type="checkbox"
                            checked={Boolean(rightsDraft[opt.key])}
                            onChange={() => toggleTenantUserRight(opt.key)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="dialog-actions">
                      <Button onClick={saveTenantUserRightsDialog} variant="primary">
                        Speichern
                      </Button>
                      <Button onClick={closeTenantUserRightsDialog} variant="secondary">
                        Abbrechen
                      </Button>
                    </div>
                  </Modal>
                  </>
                  )}
                </div>
                )}
              {!isStructureNavigation && canManageImpersonation && (
              <div className="card tenant-user-panel tenant-impersonation-panel">
                <div className="card-title card-title-with-badge">
                  <span>Vollzugriff</span>
                  {pendingImpersonationPermissionCount > 0 && (
                    <span className="impersonation-request-count">{pendingImpersonationPermissionCount}</span>
                  )}
                </div>
                <ExpandableButton
                  className="admin-compact-toggle"
                  onClick={() => setShowTenantFullAccessPanel((prev) => !prev)}
                  expanded={showTenantFullAccessPanel}
                >
                  {showTenantFullAccessPanel
                    ? 'Vollzugriff ausblenden'
                    : 'Vollzugriff öffnen'}
                </ExpandableButton>
                {showTenantFullAccessPanel && (
                <>
                <div className="archive-search">
                  <ClearableInput
                    type="search"
                    placeholder="Suche (Name, E-Mail)"
                    value={tenantImpersonationSearchTerm}
                    onValueChange={setTenantImpersonationSearchTerm}
                  />
                </div>
                <div className="tenant-user-content">
                  <div className={`table tenant-user-table tenant-impersonation-table${filteredTenantImpersonationPermissions.length ? '' : ' is-empty'}`}>
                    <div className="table-header">
                      <span>Nutzer</span><span>Status</span><span>Aktionen</span>
                    </div>
                    {filteredTenantImpersonationPermissions.map((item) => {
                      const meta = getImpersonationStatusMeta(item.status)
                      const decision = getImpersonationPermissionDecision(item.status)
                      const actionLabel = decision?.action === IMPERSONATION_ACTION_REVOKE ? 'Widerrufen' : 'Genehmigen'
                      return (
                        <div className="table-row" key={item.permission_id}>
                          <span className="tenant-user-identity">
                            <span className="tenant-user-identity-name">{highlightSearchSubstring(item.helper_name || '-', tenantImpersonationSearchTerm, true)}</span>
                            <span className="tenant-user-identity-email">{highlightSearchSubstring(item.helper_email || '-', tenantImpersonationSearchTerm, true)}</span>
                          </span>
                          <span>
                            <StatusBadge className="impersonation-status-badge" variant="compact-fixed" tone={meta.tone} title={meta.title}>{meta.label}</StatusBadge>
                          </span>
                          <span className="tenant-user-row-actions tenant-impersonation-row-actions">
                            {decision && (
                              <IconButton
                                icon={decision.action === IMPERSONATION_ACTION_REVOKE ? 'close' : 'approve'}
                                label={actionLabel}
                                tone={decision.action === IMPERSONATION_ACTION_REVOKE ? 'danger' : 'positive'}
                                onClick={() => handleUpdateTenantImpersonationPermissionStatus(item.permission_id, decision.nextStatus, item.helper_name || item.helper_email)}
                              />
                            )}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  <div className="tenant-impersonation-mobile-list" role="list">
                    {filteredTenantImpersonationPermissions.map((item) => {
                      const meta = getImpersonationStatusMeta(item.status)
                      const decision = getImpersonationPermissionDecision(item.status)
                      const actionLabel = decision?.action === IMPERSONATION_ACTION_REVOKE ? 'Widerrufen' : 'Genehmigen'
                      const isExpanded = expandedTenantImpersonationPermissionId === item.permission_id
                      const detailsId = `tenant-impersonation-details-${item.permission_id}`
                      return (
                        <div className="tenant-impersonation-mobile-item" key={`mobile-${item.permission_id}`} role="listitem">
                          <button
                            className="tenant-impersonation-mobile-summary"
                            type="button"
                            onClick={() => setExpandedTenantImpersonationPermissionId((prev) => (prev === item.permission_id ? '' : item.permission_id))}
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                          >
                            <span className={`tenant-impersonation-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                            <span className="tenant-impersonation-mobile-name">{highlightSearchSubstring(item.helper_name || '-', tenantImpersonationSearchTerm, true)}</span>
                            <span className="tenant-impersonation-mobile-email">{highlightSearchSubstring(item.helper_email || '-', tenantImpersonationSearchTerm, true)}</span>
                            <StatusBadge className="impersonation-status-badge tenant-impersonation-mobile-status" variant="compact-fixed" tone={meta.tone} title={meta.title}>{meta.label}</StatusBadge>
                          </button>
                          {isExpanded && (
                            <div className="tenant-impersonation-mobile-details" id={detailsId}>
                              <div className="tenant-impersonation-mobile-meta">
                                <span className="tenant-impersonation-mobile-label">Impersonation</span>
                                <StatusBadge className="impersonation-status-badge" variant="compact-fixed" tone={meta.tone} title={meta.title}>{meta.label}</StatusBadge>
                              </div>
                              <div className="tenant-impersonation-mobile-actions">
                                {decision && (
                                  <Button
                                    className="impersonation-mobile-action-btn"
                                    onClick={() => handleUpdateTenantImpersonationPermissionStatus(item.permission_id, decision.nextStatus, item.helper_name || item.helper_email)} variant="primary">
                                    {actionLabel}
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {!tenantImpersonationPermissions.length && <div className="hint empty-list-hint">Keine Helfer vorhanden</div>}
                  {tenantImpersonationPermissions.length > 0 && !filteredTenantImpersonationPermissions.length && <div className="hint">Keine passenden Helfer gefunden</div>}
                </div>
                <div className="tenant-user-actions">
                  <ExpandableButton
                    className="tenant-user-toggle-btn"
                    onClick={toggleTenantImpersonationCreate}
                    expanded={showTenantImpersonationCreate}
                  >
                    {showTenantImpersonationCreate ? 'Anfrage ausblenden' : 'Anfrage stellen'}
                  </ExpandableButton>
                  {showTenantImpersonationCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div>
                          <label>Helfer per E-Mail hinzufügen</label>
                          <input
                            type="email"
                            placeholder="user@example.tld"
                            value={tenantImpersonationEmail}
                            className={adminFieldErrors.tenantImpersonation.email ? 'field-input-error' : ''}
                            onChange={(e) => { setTenantImpersonationEmail(e.target.value); clearAdminFormFieldError('tenantImpersonation', 'email') }}
                          />
                        </div>
                      </div>
                      <Button
                        className="tenant-user-create-submit"
                        onClick={handleCreateTenantImpersonationPermission} variant="primary">
                        Anfrage erstellen
                      </Button>
                    </div>
                  )}
                  {tenantImpersonationError && <div className="hint form-error-hint">{tenantImpersonationError}</div>}
                </div>
                </>
                )}
              </div>
              )}
              </section>
              </>
              )}
            </>
          )}
        </main>
      </div>
      <LegalBottomBar showTerms appVersion={appVersion} />
      {(uploadSuccess || exportSuccess) && (
        <div className="success-snackbar" role="status" aria-live="polite">
          <span>{exportSuccess || uploadSuccess}</span>
          <button
            className="success-snackbar-close"
            type="button"
            onClick={() => {
              setUploadSuccess('')
              setExportSuccess('')
            }}
            aria-label="Benachrichtigung schließen"
          >
            ×
          </button>
        </div>
      )}
      <ConfirmationDialog
        open={confirmationDialog.open}
        title={confirmationDialog.title}
        message={pendingSessionExitKind ? (
          <div className="session-exit-wait" role="status" aria-live="polite">
            <div>Der aktuelle Upload-Vorgang wird abgeschlossen. Die gewählte Aktion wird danach automatisch fortgesetzt.</div>
            <div className="upload-progress-copy">
              {uploadTransferActive
                ? (uploadTransferPercent == null
                    ? 'Upload läuft…'
                    : formatUiTemplate('upload.transfer.progress', { percent: uploadTransferPercent }))
                : 'Upload wird abgeschlossen…'}
            </div>
            <div className={`upload-progress-bar${uploadTransferPercent == null ? ' is-indeterminate' : ''}`} aria-hidden="true">
              <span style={uploadTransferPercent == null ? undefined : { width: `${uploadTransferPercent}%` }} />
            </div>
          </div>
        ) : confirmationDialog.message}
        confirmLabel={confirmationDialog.confirmLabel}
        tone={confirmationDialog.tone}
        onConfirm={confirmDialogAction}
        onClose={closeConfirmationDialog}
        busy={Boolean(pendingSessionExitKind)}
        busyLabel="Upload wird abgeschlossen…"
      />
      <Modal
        open={Boolean(authToken && authUser && !authUser.terms_accepted_at && !authUser?.impersonation?.active)}
        title="Geschäftsbedingungen akzeptieren"
        onClose={() => {}}
        dismissible={false}
        className="terms-acceptance-dialog"
      >
        <p className="dialog-message">Bitte akzeptieren Sie die Geschäftsbedingungen, um EleFANT weiter zu verwenden.</p>
        <label className="terms-acceptance-check">
          <input
            type="checkbox"
            required
            checked={termsDialogAccepted}
            onChange={(event) => {
              setTermsDialogAccepted(event.target.checked)
              setTermsDialogError('')
            }}
            disabled={termsDialogBusy}
          />
          <span>Ich akzeptiere die <a className="sb-link" href={termsUrl} target="_blank" rel="noreferrer">Geschäftsbedingungen</a> für den EleFANT-Dienst.</span>
        </label>
        {termsDialogError && <div className="form-validation-message" role="alert">{termsDialogError}</div>}
        <div className="dialog-actions">
          <Button variant="primary" onClick={handleAcceptTerms} disabled={!termsDialogAccepted || termsDialogBusy} aria-busy={termsDialogBusy || undefined}>
            {termsDialogBusy ? 'Zustimmung wird gespeichert…' : 'Akzeptieren'}
          </Button>
          <Button variant="secondary" onClick={() => void performLogout()} disabled={termsDialogBusy}>
            Logout
          </Button>
        </div>
      </Modal>
    </div>
  )
}

function SessionController() {
  const apiBase = import.meta.env.VITE_API_BASE || '/api/v1'
  const [session, setSession] = useState(() => {
    const token = localStorage.getItem('fiscalarchive_token') || ''
    return {
      id: 0,
      token,
      user: null,
      restoring: Boolean(token)
    }
  })
  const sessionRef = useRef(session)
  sessionRef.current = session

  useEffect(() => {
    if (session.token) {
      localStorage.setItem('fiscalarchive_token', session.token)
    } else {
      localStorage.removeItem('fiscalarchive_token')
    }
  }, [session.token])

  useEffect(() => {
    if (!session.restoring || !session.token) return undefined
    let cancelled = false
    const sessionId = session.id
    const token = session.token

    const restoreSession = async () => {
      try {
        const res = await fetch(`${apiBase}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!res.ok) throw new Error('Session restoration failed')
        const data = await res.json()
        if (cancelled) return
        setSession((current) => (
          current.id === sessionId
            ? { id: current.id + 1, token, user: normalizeAuthUser(data), restoring: false }
            : current
        ))
      } catch {
        if (cancelled) return
        setSession((current) => (
          current.id === sessionId
            ? { id: current.id + 1, token: '', user: null, restoring: false }
            : current
        ))
      }
    }

    void restoreSession()
    return () => {
      cancelled = true
    }
  }, [apiBase, session.id, session.restoring, session.token])

  const replaceAuthSession = (payload, sourceSessionId) => {
    if (sessionRef.current.id !== sourceSessionId) return
    const token = String(payload?.token || '')
    const user = normalizeAuthUser(payload?.user)
    const nextSession = { id: sourceSessionId + 1, token, user, restoring: false }
    sessionStorage.removeItem(IMPERSONATION_NOTIFICATION_DISMISSED_KEY)
    sessionRef.current = nextSession
    setSession(nextSession)
  }

  const clearAuthSession = (sourceSessionId) => {
    if (sessionRef.current.id !== sourceSessionId) return
    const nextSession = { id: sourceSessionId + 1, token: '', user: null, restoring: false }
    sessionStorage.removeItem(IMPERSONATION_NOTIFICATION_DISMISSED_KEY)
    sessionRef.current = nextSession
    setSession(nextSession)
  }

  const updateAuthUser = (updater, sourceSessionId) => {
    setSession((current) => {
      if (current.id !== sourceSessionId) return current
      const user = typeof updater === 'function' ? updater(current.user) : updater
      return user === current.user ? current : { ...current, user }
    })
  }

  return (
    <Application
      key={session.id}
      authToken={session.token}
      authUser={session.user}
      authRestoring={session.restoring}
      onReplaceAuthSession={(payload) => replaceAuthSession(payload, session.id)}
      onClearAuthSession={() => clearAuthSession(session.id)}
      onUpdateAuthUser={(updater) => updateAuthUser(updater, session.id)}
    />
  )
}

function App() {
  const pathname = typeof window === 'undefined' ? '/' : window.location.pathname.replace(/\/+$/, '') || '/'
  const appVersion = import.meta.env.VITE_APP_VERSION || 'dev'
  if (pathname === '/changelog') return <ChangelogPage appVersion={appVersion} />
  return <SessionController />
}

export default App
